import { pickBestLocales, Translations } from "./localizationLib"

export const DEFAULT_LOCALE = "en-US" satisfies keyof typeof defaultTranslations

export function findLocale(savedUserLocale?: string) {
    const supportedLocales = Object.keys(defaultTranslations)
    const bestLocales = pickBestLocales(supportedLocales, DEFAULT_LOCALE, savedUserLocale)
    const locale = bestLocales.length > 0 ? bestLocales[0] : DEFAULT_LOCALE
    return locale
}

export const defaultTranslations = {
    "da-DK": {
        // Branding
        "-gopay-app-name": "GoPay",
        "-gopay-wallet": "GoPay Wallet",


        // Date and time formats as Luxon format strings
        // See: https://moment.github.io/luxon/docs/manual/formatting.html#table-of-tokens
        "date-time-format-date": "dd/MM/yyyy",
        "date-time-format-day-and-date": "EEEE dd/MM",
        "date-time-format-time": "HH:mm",
        "date-time-format-date-and-time": "dd/MM/yyyy\u00A0HH:mm",
        "date-time-format-duration-hours-minutes": "h't.' m'min.'",
        "date-time-format-duration-minutes": "m'min.'",
        "date-time-format-duration-seconds": "s's'",
        "date-time-format-month-and-year": "MMMM, yyyy",
        "date-time-short-relative-units": "s|m|t|d|u|md|å",


        // success / error handling
        "success-modal-status": "\n .buttonLabel = Luk",
        "error-route-status": "\n .topbarTitle = Fejl",
        "error-dialog-close-button": "\n .buttonLabel = Luk",
        "action-failed-modal": "\n .buttonLabel = Luk",
        "errorHandler-labels": "\n    .title = Noget gik galt\n    .buttonLabel = Gå til forside",
        "errorHandlerModal-labels": "\n    .title = Noget gik galt\n    .buttonLabel = Luk",
        "error-clientNetworkError-label" : "Ingen Internet forbindelse",
        "unknown-error-title": "Ukendt fejl",
        "unknown-error-message": "Ingen fejloplysninger tilgængelige",
        "general-error-title": "Noget gik galt",
        "internal-error-title": "Intern fejl",
        "api-call-error-title-network-error": "Netværksfejl",
        "api-call-error-title-bad-request": "Forespørgsel afvist",
        "api-call-error-title-unauthorized": "Ugyldigt login",
        "api-call-error-title-forbidden": "Adgang nægtet",
        "api-call-error-title-not-found": "Ikke fundet",
        "api-call-error-title-conflict": "Konflikt",
        "api-call-error-title-too-many-requests": "For mange forespørgsler",
        "api-call-error-title-internal-server-error": "Intern server fejl",
        "api-call-error-title-service-unavailable": "Service ikke tilgængelig",
        "api-call-error-title-request-failed": "Forespørgsel fejlede",
        "api-call-error-no-internet-connection": "Ingen Internet forbindelse",
        "api-call-error-could-not-reach-server": "Kunne ikke opnå forbindelse til server",
        "api-call-error-protocol-error": "Server fejl: { $responseCode } { $responseStatus }",
        "api-call-error-api-returned-error": "Uventet server fejl",
        "api-call-error-response-not-json": "Uventet svar fra server",
        "api-call-error-retry-button": "Forsøg igen",

        // accept terms and conditions
        "accept-terms-and-conditions": "Jeg accepterer <conditionsLink>handelsbetingelserne</conditionsLink>",


        // authenticate common
        "-authenticate-submit-button": "Næste",


        // order types shared translations
        "-order-type-lunch": "Dagskøb",
        "-order-type-take-away": "Takeaway",
        "-order-type-catering": "Mødeforplejning",
        "-order-type-meeting": "{ $count -> \n *[one] Booking \n [other] Bookinger \n }",
        "-order-type-meeting-service": "{ $count -> \n *[one] Mødeforplejning \n [other] Mødeforplejning \n }",
        "-order-type-refill-account": "{ $count -> \n *[one] Optankning \n [other] Optankninger \n }",
        "-order-type-auto-refill-account": "Automatisk optankning",
        "-order-type-lunch-subscription": "{ $count -> \n *[one] Abonnement \n [other] Abonnementer \n }",
        "-order-type-subscribe-payment-card": "Tilmeldt betalingskort",
        "-order-type-refund": "{ $count -> \n *[one] Refundering \n [other] Refunderinger \n }",


        // user prompts
        "user-prompt-button-label-saving": "\n .loadingLabel = Gemmer...",

        // onboarding flow (right after login)
        "onboarding-skip-step": "Spring over",
        "onboarding-step-of": "Trin { $step } af { $total }",
        "onboarding-location-title": "Vælg lokation",
        "onboarding-location-title-private-user": "Vælg salgssted",
        "onboarding-location-description": "Din lokation er stedet du normalt arbejder fra. Du kan altid skifte lokation i toppen af appen.",
        "onboarding-push-title": "Må vi sende dig pushbeskeder?",
        "onboarding-push-description": "Så får du besked når din ordre er bekræftet og klar til afhentning, hvis noget ændrer sig i din ordre eller booking, og når et møde nærmer sig. Beskeder fra kantinen kommer kun fra den kantine du har valgt.",
        "onboarding-push-footnote": "Ændres under Mig › Indstillinger › Pushbeskeder",
        "onboarding-push-continue-button": "Fortsæt\n .loadingLabel = Gemmer...",
        "onboarding-done-title": "{ $location } er valgt",
        "onboarding-done-title-no-location": "Du er klar",
        "onboarding-done-push-both": "Du får beskeder om drift og ordrer samt nyheder og tilbud. Ændres under Mig › Indstillinger › Pushbeskeder.",
        "onboarding-done-push-tx": "Du får beskeder om drift og ordrer. Ændres under Mig › Indstillinger › Pushbeskeder.",
        "onboarding-done-push-news": "Du får beskeder om nyheder og tilbud. Ændres under Mig › Indstillinger › Pushbeskeder.",
        "onboarding-done-push-none": "Du får ingen pushbeskeder. Du kan slå dem til under Mig › Indstillinger › Pushbeskeder.",
        "onboarding-done-button": "Til forsiden",

        // push notification types & settings
        "push-type-tx-title": "Drift og ordrer",
        "push-type-tx-description": "Vigtige beskeder, ordrer og møder.",
        "push-type-news-title": "Nyheder og tilbud",
        "push-type-news-description": "Dagens menu, nyheder og tilbud.",
        "push-settings-title": "\n .primaryTopBarTitle = Pushbeskeder",
        "push-settings-status-unavailable": "Pushbeskeder er kun tilgængelige i appen. Dine valg herunder gemmes og bruges i appen.",
        "push-settings-status-allowed": "Pushbeskeder er tilladt på denne telefon",
        "push-settings-status-denied-title": "Slået fra på denne telefon",
        "push-settings-status-denied-text": "Uanset valgene herunder sender vi ingen beskeder, før du tillader dem på denne telefon.",
        "push-settings-allow-button": "Tillad pushbeskeder\n .loadingLabel = Gemmer...",
        "push-settings-open-settings-link": "Åbn indstillinger",
        "push-settings-turn-off-link": "Slå helt fra i telefonens indstillinger",


        // welcome
        "authenticate-welcome-slide-header-buy-lunch": "Køb frokost og firmaydelser",
        "authenticate-welcome-slide-text-buy-lunch": "Køb nemt din mad hos frokostleverandøren samt andre firmaydelser, f.eks. forplejning ved events og tøjrens.",
        "authenticate-welcome-slide-header-reduce-food-waste": "Bekæmp madspild",
        "authenticate-welcome-slide-text-reduce-food-waste": "Forudbestil og afbestil din frokost, så køkkenet kan planlægge madproduktionen. Vælg fremhævede menuer, der er bedre for miljøet.",
        "authenticate-welcome-slide-header-receive-offers": "Ugens menuer og tilbud",
        "authenticate-welcome-slide-text-receive-offers": "Du kan se ugens menuer med næringsindhold, modtage tilbud og læse nyheder.",
        "authenticate-welcome-slide-header-purchase-overview": "Se dine køb",
        "authenticate-welcome-slide-text-purchase-overview": "Du har et godt overblik over dine køb og kvitteringer. Betaler du via løntræk, kan du nemt afstemme.",
        "authenticate-welcome-continue-button": "Log ind / Opret konto",


        // login choices
        "authenticate-login-choices-header": "Bestil mad og andre services",
        "authenticate-login-choices-login-button": "Log ind",
        "authenticate-login-choices-create-account-button": "Opret konto",
        "authenticate-login-choices-guest-link": "Køb som gæst",


        // login / signup email
        "authenticate-email-header": "Indtast din e-mail adresse",
        "authenticate-email-signup-header": "Opret konto",
        "authenticate-email-email-input": "\n    .label = E-mail adresse",
        "authenticate-email-submit-button": "{ -authenticate-submit-button }\n .loadingLabel = Checker e-mail adresse...",
        "authenticate-email-email-is-required": "Indtast din e-mail adresse",
        "authenticate-email-email-must-be-an-email": "Ugyldig e-mail adresse",
        "authenticate-email-expect-company-flow": "Du forsøger at oprette en konto med e-mail\u00ADdomænet <emailDomain>{ $emailDomain }</emailDomain>, der er tilknyttet en virksomhed. Hvis du ønsker at oprette en privat konto, skal du bruge din private e-mail\u00ADadresse.",
        "authenticate-email-expect-private-flow": "Dit e-mail\u00ADdomæne <emailDomain>{ $emailDomain }</emailDomain> er i øjeblikket ikke tilknyttet nogen virksomhed i systemet. Du skal bruge din arbejds\u00ADmail og ikke din private e-mail\u00ADadresse. Kontakt din administrator, hvis du mener, at dit domæne burde være tilknyttet en virksomhed.",
        "authenticate-email-switch-to-login-flow": "Den indtastede e-mail\u00ADadresse findes allerede i systemet. Ønsker du at logge ind i stedet?",
        "authenticate-email-switch-to-signup-flow": "Den indtastede e-mail\u00ADadresse fandtes ikke i systemet. Ønsker du at oprette en konto i stedet?",
        "authenticate-email-switch-flow-no-button": "Nej tak",
        "authenticate-email-switch-flow-yes-button": "Ja tak",
        "authenticate-email-okay-button": "Forstået",


        // activate by phone
        "authenticate-phone-number-header": "Aktiver konto",
        "authenticate-phone-number-text": "Vi har brug for at kende dit mobilnummer, så vi kan sende en aktiveringskode:",
        "authenticate-phone-number-phone-number-input": "\n .label = Telefonnummer",
        "authenticate-phone-number-submit-button": "{ -authenticate-submit-button }\n .loadingLabel = Sender kode...",
        "authenticate-phone-number-username-missing": "Brugernavn / e-mail mangler. Gå venligst tilbage og udfyld.",
        "authenticate-phone-number-phone-number-is-required": "Telefonnummer er påkrævet",
        "authenticate-phone-number-phone-number-is-invalid": "Ugyldigt telefonnummer",


        // activation code
        "authenticate-activation-code-header": "Aktiver konto",
        "authenticate-activation-code-automatic-activation-text": "Vent venligst mens vi aktiverer din konto...",
        "authenticate-activation-code-manual-activation-text": "Indtast venligst den kode, som du har modtaget på e-mail eller SMS:",
        "authenticate-activation-code-activation-code-input": "\n .label = Aktiveringskode",
        "authenticate-activation-code-submit-button": "{ -authenticate-submit-button }\n .loadingLabel = Aktiverer...",
        "authenticate-activation-code-code-field-required": "Indtast en aktiveringskode",
        "authenticate-activation-code-code-field-min-len": "Aktiveringskode er på min. { $codeLength } tegn",


        // login/password
        "authenticate-password-header": "Indtast dit kodeord",
        "authenticate-password-header-with-name": "Velkommen { $displayName }! Log venligst ind.",
        "authenticate-password-password-input": "\n .label = Kodeord",
        "authenticate-password-submit-button": "{ -authenticate-submit-button }\n .loadingLabel = Logger ind...",
        "authenticate-password-username-missing": "Brugernavn / e-mail mangler. Gå venligst tilbage og udfyld.",
        "authenticate-password-forgot-password-link": "Glemt kodeord?",
        "authenticate-password-password-is-required": "Kodeord skal indtastes",


        // login/single sign on
        "authenticate-sso-header": "Single Sign-on",
        "authenticate-sso-explanation": "Din organisation { $organization } benytter Single Sign-on. Du vil nu blive sendt til jeres fælles login side.",
        "authenticate-sso-canceled-message": "Du gennemførte ikke login processen.",
        "authenticate-sso-retry-button": "Prøv igen\n .loadingLabel = Viderestiller...",


        // login/account-type
        "authenticate-account-type-choose-account-type": "Vælg kontotype",
        "authenticate-account-type-private-account": "Privat konto",
        "authenticate-account-type-company-account": "Firmakonto",
        "authenticate-account-type-private-account-description": "Med en privat konto kan du tilmelde dig med din private e-mailadresse og få adgang til offentlige shops. Du har ikke adgang til firma shops med denne kontotype.",
        "authenticate-account-type-company-account-description": "En firmakonto giver dig adgang til de shops, der er tilknyttet din virksomhed. Du skal bruge din arbejdsmail for at oprette en konto.",
        "authenticate-account-type-help-text": "Skal jeg bruge en privat konto eller en virksomhedskonto?",
        "authenticate-account-type-help-drawer": "### Firmakonto\n\n Firmaer og andre organisationer kan udbyde forskellige services med betaling via GoPay, f.eks. køb af kantinemad. For at tilgå disse shops, skal du have en firmakonto. Vi finder frem til dit firma ud fra dit e-mail\u00ADdomæne.\n\n ### Privat konto\n\n Udover firma shops findes der også offentlige shops i GoPay, som privat\u00ADpersoner kan købe i. Med en privat konto har du adgang til alle offentlige shops.\n   .title = Kontotype forklaring",


        // select company
        "authenticate-select-company-header-reason-multiple-companies-for-domain": "Vælg firma",
        "authenticate-select-company-header-reason-multiple-accounts-for-username": "Vælg brugerkonto",
        "authenticate-select-company-reason-message-multiple-companies-for-domain": "Vi var ikke i stand til at bestemme dit firma automatisk, da dit e-mail domæne <duplicateValue>{ $duplicateValue }</duplicateValue> optræder flere steder i vores system.",
        "authenticate-select-company-reason-message-multiple-accounts-for-username": "Vi fandt flere konti i vores system med e-mail adressen <duplicateValue>{ $duplicateValue }</duplicateValue>.",
        "authenticate-select-company-action-message-multiple-companies-for-domain": "Vælg lokation (firma), som du ønsker at oprette din konto under:",
        "authenticate-select-company-action-message-multiple-accounts-for-username": "Vælg venligst den brugerkonto, som du ønsker at logge ind med.",
        "authenticate-select-company-show-list-button": "Vis valgmuligheder",
        "authenticate-select-company-submit-button": "{ -authenticate-submit-button }\n .loadingLabel = Gemmer...",


        // sign up
        "authenticate-signup-header" : "Opret brugerkonto",
        "authenticate-signup-text" : "Vi har brug for at vide lidt mere om dig, så vi kan oprette din konto:",
        "authenticate-signup-name-input": "\n .label = Navn",
        "authenticate-signup-mobile-input": "\n .label = Mobil (valgfrit)",
        "authenticate-signup-accept-terms-link": "Jeg accepterer brugsbetingelserne",
        "authenticate-signup-submit-button": "{ -authenticate-submit-button }",
        "authenticate-signup-username-missing": "Brugernavn / e-mail mangler. Gå venligst tilbage og udfyld.",
        "authenticate-signup-name-is-required": "Navn er påkrævet",
        "authenticate-signup-name-must-be-full-name": "Vi skal bruge dit fulde navn (fornavn og efternavn)",
        "authenticate-signup-phone-number-is-invalid": "Ugyldigt telefonnummer",
        "authenticate-signup-terms-must-be-accepted": "Du skal acceptere betingelserne for at bruge appen",


        // confirm account details
        "authenticate-confirm-account-details-header": "Bekræft oplysninger",
        "authenticate-confirm-account-details-email-field": "\n .primary = E-mail",
        "authenticate-confirm-account-details-name-field": "\n .primary = Navn",
        "authenticate-confirm-account-details-mobile-field": "\n .primary = Mobil",
        "authenticate-confirm-account-submit-button": "{ -authenticate-submit-button }\n .loadingLabel = Opretter din konto...",
        "authenticate-confirm-account-details-account-missing": "Kontodetaljer mangler. Gå venligst tilbage og udfyld.",


        // create password
        "authenticate-create-password-header": "Opret kodeord",
        "authenticate-create-password-text": "Du skal oprette et kodeord for at kunne tage din konto i brug:",
        "authenticate-create-password-password-input": "\n .label = Kodeord",
        "authenticate-create-password-repeat-password-input": "\n .label = Gentag kodeord",
        "authenticate-create-password-submit-button": "{ -authenticate-submit-button }\n .loadingLabel = Opretter dit kodeord...",
        "authenticate-create-password-one-time-token-missing": "Kan ikke finde engangs-token. Gå venligst et skridt tilbage og prøv igen.",
        "authenticate-create-password-password-is-required": "Du skal indtaste et kodeord",
        "authenticate-create-password-password-too-short": "Dit kodeord skal være på mindst { $minPasswordLength } tegn",
        "authenticate-create-password-repeat-is-required": "Du skal gentage dit kodeord",
        "authenticate-create-password-password-mismatch": "Dine kodeord stemmer ikke overens",


        // reset password
        "authenticate-reset-password-header": "Nulstil kodeord",
        "authenticate-reset-password-button": "Nulstil\n .loadingLabel = Nulstiller...",
        "authenticate-reset-password-username-missing": "Brugernavn / e-mail mangler. Gå venligst tilbage og udfyld.",


        // login route components
        "login-external-guest-dialog": "\n .title = Gæstekøb\n .message = Du er ved at foretage gæstekøb. Shoppen åbnes i et nyt vindue.\n .buttonLabel = Fortsæt",


        // --- User cards ---
        "user-cards-loading-element": "\n .title = Indlæser kort...",


        // home
        "-show-all": "Se alle",
        "-home-offers": "Gode tilbud",
        "-home-shops": "Shops",
        "-home-my-favorites": "Mine favoritter",

        "home-label": "Hjem",

        "home-news": "\n    .title = Nyheder\n    .rightOptionText = { -show-all }",
        "home-news-kitchen": "\n    .title = Nyt fra kantinen\n    .rightOptionText = { -show-all }",
        "home-news-company-and-service-center": "\n    .title = Nyt fra {$company}\n    .rightOptionText = { -show-all }",
        "home-news-facilitynet": "\n    .title = Nyt fra { -gopay-app-name }\n    .rightOptionText = { -show-all }",

        "home-my-meetings": "\n    .title = Kommende møder\n    .rightOptionText = { -show-all }",
        "home-upcoming-meetings": "Kommende møder",
        "home-contact-the-canteen": "Kontakt kantinen",
        "home-no-planned-meetings": "Ingen planlagte møder",
        "home-plan-meeting": "Planlæg et møde",

        "home-you-are-here": "Du er her:",
        "home-change-location": "Skift lokation",

        "home-takeaway-offers": "\n     .title = { -home-offers } \n     .rightOptionText = { -show-all }",
        "home-all-takeaway-offers-title": "\n   .primaryTopBarTitle = { -home-offers }",

        "home-takeaway-shops": "\n      .title = { -home-shops } \n      .rightOptionText = { -show-all }",
        "home-all-takeaway-shops-title": "\n   .primaryTopBarTitle = { -home-shops }",

        "home-product-favorites": "\n      .title = { -home-my-favorites } \n      .rightOptionText = { -show-all }",
        "home-all-product-favorites-title": "\n   .primaryTopBarTitle = { -home-my-favorites }",
        "home-all-product-favorites-empty": "Du har ingen favoritter.",
        "home-product-favorites-options": "\n .loadingDescription = Fjerner favorit...",
        "home-remove-favorite": "Fjern favorit",

        "home-user-card-survey-rate-now": "Bedøm nu",

        "home-user-card-check-in-reminder-check-in": "Tak fordi du checkede ind i dag",
        "home-user-card-check-in-reminder-no-check-in": "Husk at checke ind i kantinen i dag",
        "home-user-card-check-in-reminder-learn-more": "Læs mere",

        "home-user-card-lunch-subscription-used-count": "{ $used } af { $total }",
        "home-user-card-lunch-subscription-used-this-week": "brugt denne uge",
        "home-user-card-lunch-subscription-used-this-month": "brugt denne måned",

        "home-user-card-recent-purchase-view-receipt": "Vis kvittering",
        "home-user-card-recent-purchase-status": "Status: { $status }",
        "home-user-card-recent-purchase-status-item-received": "MODTAGET",
        "home-user-card-recent-purchase-status-item-paid": "BETALT",
        "home-user-card-recent-purchase-status-item-failed": "FEJLET",
        "home-user-card-recent-purchase-status-item-canceled": "ANNULLERET",

        // user survey modal
        "user-survey-step2-question-good": "Hvad gjorde vi særlig godt?",
        "user-survey-step2-question-bad": "Hvad kunne vi have gjort bedre?",
        "user-survey-add-comment-button": "\n {$comment ->\n      *[0] Skriv en\n      [other] Rediger\n} kommentar",
        "user-survey-thank-you-message": "Tak fordi du ville deltage",
        "user-survey-next-button": "Næste",
        "user-survey-skip-button": "Spring over",
        "user-survey-submit-button": "Indsend\n .loadingLabel = Sender...",
        "user-survey-close-button": "Luk",
        "user-survey-comment-drawer": "\n   .title = Kommentar",
        "user-survey-comment-field": "\n   .label = Skriv din kommentar her",
        "user-survey-comment-submit-button": "Gem",
        "user-survey-comment-cancel-button": "Annuller",

        // navigation
        "bottom-navigation-home": "\n   .label = Forside",
        "bottom-navigation-todays-purchases": "\n   .label = I dag",
        "bottom-navigation-order": "\n   .label = Bestil",
        "bottom-navigation-messages": "\n   .label = Beskeder",
        "bottom-navigation-me": "\n   .label = Mig",

        "app-navigation-to-shop-select": "Gå til shops",
        "app-navigation-to-login": "Gå til log ind",
        "app-navigation-to-home": "Gå til forside",


        // Consent dialog
        "consent-dialog-personal-information-header": "Personoplysninger",
        "consent-dialog-pii-category-contact-info": "Kontaktinformation",
        "consent-dialog-pii-category-identifiers": "Identifikation",
        "consent-dialog-pii-category-purchases": "Køb",
        "consent-dialog-pii-category-user-content": "Brugerindhold",
        "consent-dialog-pii-item-name": "Navn",
        "consent-dialog-pii-item-email-address": "E-mail",
        "consent-dialog-pii-item-phone-number": "Telefonnummer",
        "consent-dialog-pii-item-user-id": "Bruger id",
        "consent-dialog-pii-item-device-id": "Device id",
        "consent-dialog-pii-item-customer-support": "Kundeservice",
        "consent-dialog-pii-item-purchase-history": "Købshistorik",
        "consent-dialog-privacy-policy-header": "Privatlivspolitik",
        "consent-dialog-privacy-policy-link": "Se firmaets privatlivspolitik",


        // My meetings
        "my-meetings-sittings": "{$amount} kuv.",
        "my-meetings-header-label": "\n   .primaryTopBarTitle = Mine møder",
        "my-meetings-tab-meeting-booking": "\n .label = Ressourcebookinger",
        "my-meetings-tab-meeting-service": "\n .label = Mødeforplejning",
        "my-meetings-now-badge": "\n      .label = Nu",
        "my-meetings-search-label": "\n   .label = Søg",
        "my-meetings-no-filtered-label": "Ingen matchende møder",
        "my-meetings-no-meetings-label": "Ingen planlagte møder",


        // Meeting details
        "meeting-details-delete-dialog": "\n  .description = Afbestil levering d. {$date}?\n .loadingDescription = Sletter levering...\n  .confirmLabel = Slet",


        // Todays purchases
        "todays-orders-receipt-top-bar-title": "Dagens køb",
        "todays-orders-receipt-top-bar-title-length": "Dagens køb {$selectedOrder} af {$maxOrders}",
        "todays-orders-none-found": "\n .title = Ingen køb\n .description = Du har ingen køb i dag.",
        "todays-orders-no-such-order": "Ordren findes ikke",


        // My orders
        "my-orders-page": "\n  .primaryTopBarTitle = Mine ordrer",
        "my-orders-page-header": "\n .title = Mine ordrer",

        // My Orders tabs
        "my-orders-tab-purchases": "\n .label = Daglige køb",
        "my-orders-tab-wallet-refills": "\n .label = Optankning",

        // My Orders expenses chart tabs
        "my-orders-expenses-chart-tab-last-week": "\n .label = Sidste 7 dage",
        "my-orders-expenses-chart-tab-last-month": "\n .label = Sidste 30 dage",
        "my-orders-expenses-chart-tab-last-year": "\n .label = Sidste 365 dage",
        "my-orders-expenses-chart-title": "Privatkøb",

        // My Orders filter button
        "my-orders-filter-button-none": "Ingen",
        "my-orders-filter-button-all": "Alle",
        "my-orders-filter-button-some": "{$selected} / {$available} valgte",
        "my-orders-filter-button-lunch": "{ -order-type-lunch(count: 100) }",
        "my-orders-filter-button-take-away": "{ -order-type-take-away(count: 100) }",
        "my-orders-filter-button-catering": "{ -order-type-catering(count: 100) }",
        "my-orders-filter-button-meeting": "{ -order-type-meeting(count: 100) }",
        "my-orders-filter-button-refill-account": "{ -order-type-refill-account(count: 100) }",
        "my-orders-filter-button-auto-refill-account": "{ -order-type-auto-refill-account(count: 100) }",
        "my-orders-filter-button-lunch-subscription": "{ -order-type-lunch-subscription(count: 100) }",
        "my-orders-filter-button-subscribe-payment-card": "{ -order-type-subscribe-payment-card(count: 100) }",
        "my-orders-filter-button-refund": "{ -order-type-refund(count: 100) }",

        // My Orders filter order types
        "my-orders-filter-order-type-lunch": "\n .label = { -order-type-lunch(count: 100) }",
        "my-orders-filter-order-type-take-away": "\n .label = { -order-type-take-away(count: 100) }",
        "my-orders-filter-order-type-catering": "\n .label = { -order-type-catering(count: 100) }",
        "my-orders-filter-order-type-meeting": "\n .label = { -order-type-meeting(count: 100) }",
        "my-orders-filter-order-type-refill-account": "\n .label = { -order-type-refill-account(count: 100) }",
        "my-orders-filter-order-type-auto-refill-account": "\n .label = { -order-type-auto-refill-account(count: 100) }",
        "my-orders-filter-order-type-lunch-subscription": "\n .label = { -order-type-lunch-subscription(count: 100) }",
        "my-orders-filter-order-type-subscribe-payment-card": "\n .label = { -order-type-subscribe-payment-card(count: 100) }",
        "my-orders-filter-order-type-refund": "\n .label = { -order-type-refund(count: 100) }",

        "my-orders-filter-modal": "\n .title = Ordretyper\n .cancelButtonLabel = Luk",
        "my-orders-filter-order-type-label": "Ordretyper",
        "my-orders-results": "{$amount} resultater",
        "my-orders-load-more-button": "Indlæs flere",
        "my-orders-price-total": "I alt",
        "my-orders-print": "Print",
        "my-orders-edit": "Rediger",
        "my-orders-share": "Del",
        "my-orders-delete": "Slet",
        "my-orders-receipt": "Kvittering",


        // Transactions
        "transaction-generic-processing": "Vent venligst...",
        "transaction-generic-success": "Transaktionen er gennemført",
        "transaction-missing-payment-window-url": "API'et sendte ikke et link til betalingsvinduet. Kontakt venligst support",


        // Shop - payment methods
        "-payment-accept-terms": "Jeg accepterer handelsbetingelser",

        "payment-method-initial-message": "Vælg betalingsmetode",
        "payment-method-mobile-pay-alert-message": "Brug MobilePay app'en på din telefon til at færdiggøre betalingen. Du skal godkende betalingen i MobilePay indenfor 5 minutter og vende tilbage hertil.",
        "payment-method-free": "Gratis",
        "payment-method-credit-card": "Kreditkort",
        "payment-method-one-click": "Betal med { $card }",
        "payment-method-mobile-pay": "MobilePay",
        "payment-method-salary-reduction": "Løntræk",
        "payment-method-lunch-registration": "Frokostordning",
        "payment-method-guest-lunch-registration": "Frokostordning",
        "payment-method-facilitynet-account": "{ -gopay-wallet } (<balance>{ $balance }</balance>)",
        "payment-method-invoice": "Faktura",
        "payment-method-card-expired": "udløbet",


        // --- Shopping basket ---

        // Basket section
        "shopping-basket-product-to-pay-by-free": "(gratis)",
        "shopping-basket-product-to-pay-by-credit-card": "(via\u00A0kort)",
        "shopping-basket-product-to-pay-by-one-click-payment": "(via\u00A0kort)",
        "shopping-basket-product-to-pay-by-mobile-pay": "(via\u00A0MobilePay)",
        "shopping-basket-product-to-pay-by-payroll-deduction": "(via\u00A0løntræk)",
        "shopping-basket-product-to-pay-by-lunch-registration": "(via\u00A0frokostordning)",
        "shopping-basket-product-to-pay-by-guest-lunch-registration": "(via\u00A0frokostordning)",
        "shopping-basket-product-to-pay-by-gopay-account": "(via\u00A0{ -gopay-wallet })",
        "shopping-basket-product-to-pay-by-invoice": "(via\u00A0faktura)",

        // Section total
        "shopping-basket-total-section-header": "Til betaling:",
        "shopping-basket-total-line": "\n .label = Til betaling",
        "shopping-basket-total-private-price": "Din betaling: { $privateTotal }",
        "shopping-basket-total-company-price": "Firmabetaling: { $companyTotal }",

        "shopping-basket-total-to-pay-by-free": "\n .label = Gratis",
        "shopping-basket-total-to-pay-by-credit-card": "\n .label = Med kort",
        "shopping-basket-total-to-pay-by-one-click-payment": "\n .label = Med kort",
        "shopping-basket-total-to-pay-by-mobile-pay": "\n .label = Med MobilePay",
        "shopping-basket-total-to-pay-by-payroll-deduction": "\n .label = Via løntræk",
        "shopping-basket-total-to-pay-by-lunch-registration": "\n .label = Via frokostordning",
        "shopping-basket-total-to-pay-by-guest-lunch-registration": "\n .label = Via frokostordning",
        "shopping-basket-total-to-pay-by-gopay-account": "\n .label = Med { -gopay-wallet }",
        "shopping-basket-total-to-pay-by-invoice": "\n .label = Med faktura",

        // Accounting section
        "shopping-basket-accounting-header": "\n .description = Kontering",

        // Conditions section
        "shopping-basket-conditions-terms-label": "Jeg accepterer <conditionsLink>handelsbetingelserne</conditionsLink>",
        "shopping-basket-conditions-terms-not-accepted": "Du skal acceptere handelsbetingelserne",

        // Refill account
        "-refill-account-balance": "{ $pos -> \n [initial] Saldo \n *[other] saldo \n }",
        "refill-account-balance-limit": "Din { -refill-account-balance } må aldrig overstige { $maxBalance }",

        "refill-title": "\n .primaryTopBarTitle = { -gopay-wallet }",
        "refill-tab": "\n .label = Tank op",
        "refill-current-balance": "Nuværende { -refill-account-balance }: { $currentBalanceFormatted }",
        "refill-step-description-1": "\n    .description = Indtast beløb",
        "refill-step-description-1-custom": "Andet beløb:",
        "refill-no-preset-amounts": "Ingen foruddefinerede beløb tilgængelige",
        "refill-step-amount-custom-amount": "\n .label = Beløb ({ $currencySymbol })\n .helperText = Ny { -refill-account-balance } må ikke overstige { $maxBalance }",
        "refill-menu-item-custom-amount": "Andet",
        "refill-terms-not-provided": "Salgsstedet har ikke defineret handelsbetingelser. Kontakt dem venligst for at løse problemet.",
        "refill-account-perform-refill-button": "Gennemfør optankning ({ $toPay })\n .loadingLabel = Tanker penge op...",

        // Automatic account refill
        "automatic-refill-page-title": "\n .primaryTopBarTitle = Automatisk optankning",
        "automatic-refill-tab": "\n .label = Automatisk optankning",
        "automatic-refill-description": "Aktiver automatisk optankning, når din { -refill-account-balance } runder et bestemt beløb. Din { -refill-account-balance } må aldrig overstige { $maxBalance }",
        "automatic-refill-step-description-1": "\n    .description = Tank op med",
        "automatic-refill-step-when-balance-below": "\n .description = Når saldo er under",
        "automatic-refill-step-when-balance-below-input-amount": "\n    .label = Beløb ({ $currencySymbol })",
        "automatic-refill-confirm-button": "Aktiver automatisk tank-op\n .loadingLabel = Aktiverer...",
        "automatic-refill-confirm-button-activated": "Opdater\n .loadlingLabel = Opdaterer...",
        "automatic-refill-account": "Opdater automatisk optankning",
        "automatic-refill-cancel": "Afmeld automatisk optankning",
        "automatic-refill-cancel-modal": "\n    .description = Afmeld automatisk optankning?\n .loadingDescription = Afmelder...\n  .confirmLabel = Afmeld",
        "automatic-refill-activated-chip": "\n  .label = Aktiv",
        "automatic-refill-limit-amount-too-low": "Saldo genoptankningsgrænse må ikke være mindre end { $minLimitAmount }",
        "automatic-refill-would-exceed-max-balance": "Ny { -refill-account-balance } må ikke overstige { $maxBalance }",

        // Top-up
        "automatic-refill-no-subscription-banner-text": "Aktiver automatisk optankning",
        "automatic-refill-no-subscription-help-link": "Hvordan fungerer det?",
        "automatic-refill-no-subscription-help-drawer": "Ved at aktivere automatisk optankning har du altid penge i din digitale wallet.\n\n Du vælger optankningsbeløb og en beløbsgrænse, så tanker systemet automatisk penge på din wallet, når saldoen falder under grænseværdien.\n\n Når du aktiverer automatisk optankning, gemmes dit betalingskort sikkert hos vores certificerede betalingsgateway.\n .title = Sådan fungerer automatisk optankning",
        "automatic-refill-active-subscription-banner-text": "Auto tank-op er aktiv",
        "automatic-refill-active-subscription-banner-below": "Når saldo er under {$sum}",
        "automatic-refill-active-subscription-banner-amount": "Tank op med {$amount}",

        // Lunch subscription
        "lunch-subscription-page-title": "\n .primaryTopBarTitle = Mine abonnementer",
        "my-lunch-subscriptions-no-active-subscriptions": "Du har ingen aktive abonnementer.",
        "lunch-subscription-subscribe-button": "Tilmeld nyt abonnement",
        "lunch-subscription-subscribe-page-title": "\n .primaryTopBarTitle = Vælg abonnement",
        "lunch-subscription-subscribe-no-offers-message": "Der er ingen tilgængelige abonnementer.",
        "lunch-subscription-choose-offer": "Vælg",
        "lunch-subscription-unsubscribe-screen": "\n .primaryTopBarTitle = Afmeld abonnement",
        "lunch-subscription-unsubscribe-title": "Er du sikker på, at du vil afmelde dit abonnement?",
        "lunch-subscription-unsubscribe-terms": "Læs handelsbetingelser",

        "lunch-subscription-menu-cancel-subscription-option": "Afmeld abonnement",
        "lunch-subscription-subscription-conditions-modal": "\n .title = Abonnements\u00ADbetingelser",

        "lunch-subscription-basket": "\n .primaryTopBarTitle = Tilføj abonnement",
        "lunch-subscription-basket-step-subperiod-label": "Opstart",
        "lunch-subscription-basket-step-payment-label": "Betaling",
        "lunch-subscription-basket-period": "\n .description = Vælg abonnementsstart",
        "lunch-subscription-basket-period-too-late-notice": "(for sent)",
        "lunch-subscription-basket-period-next-button": "Næste",
        "lunch-subscription-basket-step-two": "\n .description = Diæt",
        "lunch-subscription-basket-step-three": "\n .description = Betaling",
        "lunch-subscription-basket-diet-vegetarian": "Vegetar",
        "lunch-subscription-terms": "{ -payment-accept-terms }",
        "lunch-subscription-payment-method-subscribe-payment-card": "Tilmeld nyt betalingskort",
        "lunch-subscription-payment-method-one-click-payment": "Betal med { $card }",
        "lunch-subscription-payment-method-payroll-deduction": "Løntræk",

        "lunch-subscription-price-per-month": "{$amount} pr. måned",

        "lunch-subscription-order": "Tilmeld\n .loadingLabel = Tilmelder...",
        "lunch-subscription-cancel": "Afmeld abonnement",
        "lunch-subscription-cancel-button": "Afmeld\n .loadingLabel = Afmelder...",
        "lunch-subscription-closed": "Lukket",
        "lunch-subscription-tab-month": "\n .label = Måned",
        "lunch-subscription-tab-week": "\n .label = Uge",
        "lunch-subscription-tab-day": "\n .label = Dag",
        "lunch-subscription-monthly-overview-description-closed": "Køkkenet er lukket",
        "lunch-subscription-monthly-overview-description-subscribed": "Tilmeldte dage",
        "lunch-subscription-on-locations": "\n .primaryTopBarTitle = Abonnement lokationer",
        "lunch-subscription-locations": "{$amount} lokationer",
        "lunch-subscription-conditions": "Abonnementsbetingelser",
        "lunch-subscription-start-date": "Starter {$date}",

        "lunch-subscription-weekly-overview": "Ugeoversigt",
        "lunch-subscription-weekly-overview-dish-of-the-day": "Dagens ret",
        "lunch-subscription-weekly-overview-description": "Fravælg de dage, du ikke ønsker at bestille frokost.",


        // New Order
        "order-cart-cleared": "Indkøbskurven blev tømt.",


        // Paymentcards
        "paymentcards-title": "\n .primaryTopBarTitle = Betalingskort",
        "paymentcards-card": "\n    .primary = {$number} ({$type})",
        "paymentcards-empty": "Du har ingen tilmeldte betalingskort. Hvis du gemmer et kort her, bliver det nemmere at betale, fordi du ikke skal taste dine kreditkortoplysninger hver gang.",
        "paymentcards-too-many-cards": "\n  .text = Du kan have { $maxPaymentCards} tilmeldte betalingskort. Fjern et kort for at kunne tilføje et nyt.",
        "paymentcards-button-add-payment-card": "Tilføj betalingskort\n .loadingLabel = Tilføjer kort...",
        "paymentcards-remove-card": "Fjern betalingskort",
        "paymentcards-options-dialog": "\n .loadingDescription = Fjerner kort...",

        // Conditions section
        "paymentcards-conditions-terms-error-dialog-label": "\n .text = Dit firma har ikke defineret handelsbetingelser. Kontakt dem venligst for at løse problemet.",
        "paymentcards-conditions-terms-error-label": "Kan ikke tilføje kort uden handelsbetingelser",
        "paymentcards-conditions-terms-not-accepted": "Du skal acceptere handelsbetingelserne",
        "paymentcards-conditions-iframe-done-button": 'Luk',


        // QuickPay
        "quickpay-generic-success": "Din betaling blev gennemført",
        "quickpay-finalization-error-timeout": "Vi har endnu ikke modtaget en bekræftelse fra din betalingsudbyder. Venligst tjek din netbank for at sikre, at betalingen er gennemført.",
        "quickpay-finalization-error-other": "Betalingen kunne ikke gennemføres på grund af en uventet fejl. Prøv venligst at gennemføre en ny betaling senere.",
        "quickpay-source-mismatch-title-should-be-app": "Handling fra app",
        "quickpay-source-mismatch-title-should-be-web": "Handling fra browser",
        "quickpay-source-mismatch-message-should-be-app": "Det lykkedes ikke automatisk at skifte tilbage til din GoPay app. Du er nødt til selv at gå tilbage til appen.",
        "quickpay-source-mismatch-message-should-be-web": "Dette er din GoPay app. Du brugte GoPay i din browser, men har appen installeret. Dette giver konflikt ved kreditkort og MobilePay transaktioner. Brug fortrinsvis appen, når den er installeret.",
        "quickpay-canceled": "\n .title = Køb annulleret\n .message = Du annullerede betalingen af dit køb",


        // Me page
        "me-user-greeting": "Hej {$firstName}",
        "me-my-wallets-section": "\n .title = Mine wallets",
        "me-my-wallets-status-auto-refill-active": "Auto tank-op",
        "me-my-wallets-wallet-name": "Min wallet",
        "me-my-wallets-refill-select-card-balance": "Balance:",
        "me-my-wallets-wallet-brand": "GoPay Wallet",
        "me-my-facility-net": "Mit { -gopay-app-name }",
        "me-my-meetings": "Mine møder",
        "me-my-orders": "Mine ordrer",
        "me-customer-service-category": "Kundeservice",
        "me-customer-service-intercom-consent-dialog": "\n .title = Tillad chat\n .message = Vi benytter Intercom til vores chat support. Hvis du godkender, deler vi nedenstående oplysninger med Intercom.\n .skipLabel = Ikke nu\n .consentLabel = Tillad",
        "me-customer-service-intercom-error-dialog": "\n .title = Chat fejlede\n .message = Der opstod en fejl under indlæsning af chatten. Du kan i stedet sende en e-mail til support.\n .buttonLabel = Forstået",
        "me-customer-service-intercom-loading-dialog": "\n .description = Chatten indlæser...",
        "me-help-center": "Hjælpecenter",
        "me-chat-support": "Chat",
        "me-email-support": "E-mail support",
        "me-shortcuts": "Genveje",
        "me-payment-cards": "Betalingskort",
        "me-lunch-subscription-menu-item": "Abonnement",
        "me-settings": "Indstillinger",
        "me-switch-account": "Skift brugerkonto",
        "me-switch-account-top-bar-title": "\n .primaryTopBarTitle = Skift brugerkonto",
        "me-switch-account-description": "Du har følgende konti med e-mailadressen <email>{ $email }</email>.",
        "me-switch-account-email-confirm": "Før du kan skifte mellem dine brugerkonti, skal du bekræfte, at du ejer e-mailkontoen  <email>{ $email }</email>.",
        "me-switch-account-no-accounts": " Ingen andre brugerkonti fundet med denne e-mailadresse.",
        "me-switch-account-invalid-authentication": "Ugyldigt serversvar",
        "me-switch-account-send-confirmation-link": "Send bekræftelseslink",
        "me-switch-account-confirmation-send": "En e-mail er blevet sendt. Tjek venligst din indboks.",
        "me-switch-account-failed-to-load": "Kunne ikke indlæse dine brugerkonti.",
        "me-switch-account-contact-support": "Kontakt support",
        "me-switch-account-send-confirmation-submit-button": "{ -authenticate-submit-button }\n .loadingLabel = Afventer bekræftelse...",
        "me-logout": "Log ud",

        // User account page
        "user-account-page": "\n .primaryTopBarTitle = Brugerkonto",
        "user-account-type-company-account": "\n .label = Kontotype\n .value = Firmakonto",
        "user-account-type-private-account": "\n .label = Kontotype\n .value = Privat konto",
        "user-account-company": "\n  .label = Firma",
        "user-account-username": "\n  .label = Brugernavn",
        "user-account-name": "\n  .label = Navn",
        "user-account-email": "\n  .label = Email",
        "user-account-mobile": "\n  .label = Mobilnr",
        "user-account-department": "\n  .label = Afdeling",
        "user-account-about-link": "Om denne konto",
        "user-account-about-drawer-company-account": "### Firmakonto\n\n Firmaer og andre organisationer kan udbyde forskellige services med betaling via GoPay, f.eks. køb af kantinemad. Med din firmakonto kan du tilgå disse shops.\n .title = Om din brugerkonto",
        "user-account-about-drawer-private-account": "### Privat konto\n\n Udover firma shops findes der også offentlige shops i GoPay, som privat\u00ADpersoner kan købe i. Med din private konto har du adgang til alle offentlige shops.\n .title = Om din brugerkonto",


        // Me settings module
        "settings-title": "\n .primaryTopBarTitle = Indstillinger",
        "settings-change-language": "Skift sprog",
        "settings-english": "Engelsk",
        "settings-danish": "Dansk",
        "settings-norwegian": "Norsk",
        "settings-swedish": "Svensk",
        "settings-notifications": "Notifikationer",
        "settings-push-notifications": "Push notifikationer",
        "settings-receipts-via-email": "Kvittering på e-mail",
        "settings-receipts-meeting-service": "Kvitteringer for mødeservice",
        "settings-receipts-canteen": "Private kantinekøb",
        "settings-sms-notifications": "SMS notifikationer",
        "settings-customer-service": "Kundeservice",
        "settings-social-media": "Sociale medier",
        "settings-policies": "Politikker",
        "settings-HELP_CENTER": "Hjælpecenter",
        "settings-HELP_CENTER_USER_GUIDE_MOBILE_APP": "Brugerguide",
        "settings-SOCIAL_FACILITYNET_FACEBOOK": "Følg os på Facebook",
        "settings-SOCIAL_FACILITYNET_LINKEDIN": "Følg os på LinkedIn",
        "settings-PRIVACY_TERMS": "Privatlivspolitik",
        "settings-COOKIES_TERMS": "Cookie-betingelser",
        "settings-EULA": "EULA",


        // Fast track menu
        "fast-track-menu-show-shop": "\n .title = Fast Track\n .contentActionText = Vis shop",
        "fast-track-menu-no-menus": "Ingen menuer tilgængelige. Kontakt kantinen.",


        // Anonymous enter kitchen
        "anonymous-enter-kitchen-header": "Indtast shop nummer",
        "anonymous-enter-kitchen-shop-number-input": "\n .label = Shop nummer",
        "anonymous-enter-kitchen-submit-button": "{ -authenticate-submit-button }",
        "anonymous-enter-kitchen-loading-dialog": "\n .description = Indlæser Fast Track menu...",
        "anonymous-enter-kitchen-shop-number-requirements": "Indtast venligst et shop nummer på { $minDigits }-{ $maxDigits} cifre",


        // To fast track menu via route
        "to-fast-track-menu-via-route-invalid-kitchen-id": "Shop nummeret {$kitchenCode} findes ikke.",
        "to-fast-track-menu-via-route-error": "Noget gik galt",
        "to-fast-track-menu-via-route-contact": "Kontakt køkkenpersonalet hvis fejlen gentager sig.",


        // Choose location
        "choose-location-picker-no-location": "Vælg lokation",
        "choose-location-header-company-user": "\n .title = Vælg din lokation",
        "choose-location-header-private-user": "\n .title = Vælg salgssted",
        "choose-location-shop-number-field": "\n .label = Indtast shop nummer",
        "choose-location-shop-number-submit-button": "Vælg\n .loadingLabel = Indlæser...",
        "choose-location-about-shop-number-link": "Hvad er et shop nummer?",
        "choose-location-alternative-method-header": "⸺ eller ⸺",
        "choose-location-search-for-location-button": "Vis alle salgssteder",
        "choose-location-search-field": "\n .label = Søg på navn, adresse, shop nummer...",
        "choose-location-help-text": "Findes din lokation ikke skal du kontakte jeres administrator.",
        "choose-location-dialog": "\n .title = Skift lokation",
        "choose-location-dialog-private-user": "\n .title = Skift salgssted",
        "choose-location-shop-numbers": "Shops: { $numbers }",
        "choose-location-select-location-button": "Vælg\n .disabledLabel = Aktiv\n .loadingLabel = Gemmer valg...",
        "choose-location-active-badge": "Aktiv",
        "choose-location-confirm-button": "Bekræft lokation\n .loadingLabel = Gemmer valg...",
        "choose-location-no-results": "Ingen lokationer matcher din søgning.",


        // Order takeaway
        "order-takeaway-page-title": "\n .primaryTopBarTitle = Takeaway",

        // Order catering
        "order-catering-page-title": "\n .primaryTopBarTitle = Frokost",

        // --- Products page --- Offer tag ---
        "offer-tag-good-price": "Skarp pris",
        "offer-tag-great-offer": "Godt tilbud",
        "offer-tag-buy-now": "Slå til nu",
        "offer-tag-discount": "Tilbud",
        "offer-tag-week-offer": "Ugens tilbud",
        "offer-tag-day-offer": "Dagens tilbud",
        "offer-tag-save-x-percentage": "Spar { $amount }%",
        "offer-tag-save-x-price": "Spar { $amount } kr",
        "offer-tag-limited-on-sale": "Kun { $amount } tilbage",

        // --- Products page --- Allergens ---
        "order-food-allergens-list": "Allergens: { $allergens }",

        // Order takeaway/catering
        // "webshop-canteen-not-found-dialog": "\n .title = Ikke fundet\n .message = Køkkenet blev ikke fundet. Kontakt support.\n .buttonLabel = Luk",
        "order-basket-title": "\n .primaryTopBarTitle = Kurv",
        "webshop-shop-type-lunch": "Frokostordning",
        "webshop-shop-type-take-away": "Takeaway",
        "webshop-shop-type-catering": "Mødeforplejning",
        "order-food-add": "Tilføj",
        "order-food-amount-private": "Til mig",
        "order-food-amount-company": "Til gæst",
        "order-food-pickup": "Afhentes: {$time} {$place}",
        "order-food-items-left": "{$amount} tilbage",
        "order-food-basket-button": "Gå til kasse ({ $items }) { $price }",
        "order-food-item-out-of-stock": "Varen er udsolgt",
        "order-food-item-past-deadline": "Deadline er overskredet",
        "order-food-item-quick-add": "Tilføj",
        "order-food-basket-overview": "\n .description = Varekøb",
        "order-food-basket-private": "Privat",
        "order-food-basket-company": "Firma",
        "order-food-basket-guest": "Gæst:",
        "order-food-basket-guest-information": "\n .description = Gæsteinformation",
        "order-food-basket-payment": "\n .description = Betalingsmetode",
        "order-food-basket-payment-methods": "\n .title = Betalingsmetoder",
        "order-food-basket-delivery": "\n .description = Levering",
        "order-food-basket-pickup": "Skal afhentes",
        "order-food-basket-paid": "Betalt - {$selectedOrder} af {$maxOrders}",
        "order-food-basket-name": "\n .label = Dit navn\n .emptyErrorText = Dette felt skal udfyldes",
        "order-food-basket-email": "\n .label = Din e-mail\n .emptyErrorText = Dette felt skal udfyldes\n .invalidErrorText = E-mailen er ugyldig",
        "order-food-basket-phone": "\n .label = Telefonnr. (valgfrit)",
        "order-food-basket-comment": "\n .label = Skriv en kommentar",
        "order-food-basket-purchase-button": "Gennemfør betaling\n .loadingLabel = Gennemfører køb...",
        "order-food-basket-seller":"Sælger: {$seller}",
        "order-food-canteen-details-contact": "Kontakt",
        "order-food-canteen-details-address": "Adresse",
        "order-food-canteen-details-closed": "Lukket",
        "order-food-receipt-scan-to-checkin": "Scan for at tjekke ind",
        "order-food-receipt-scan-to-checkin-offline": "QR-koden kunne ikke indlæses.",
        "order-food-featured-deliverydate": "Vælg leveringstid",
        "order-food-upcoming-menus": "Kommende menuer",
        "order-food-empty-state-text": "Køkkenet tilbyder ingen mad idag. Prøv en af de andre dage.",
        "order-food-sold-out-snackbar": "Varen er udsolgt",

        "product-unavailable-product-not-found-modal": "\n .title = Vare ikke fundet\n .message = Den ønskede vare findes ikke i sortimentet for valgt dato. Kontakt kantinen.\n .buttonLabel = Forstået",
        "product-unavailable-product-unavailable-modal": "\n .buttonLabel = Forstået",


        // Product modal
        "canteenProductModal-preparation-label": "Tilberedning",
        "canteenProductModal-declaration-label": "Varedeklaration",
        "canteenProductModal-allergens-label": "Allergener",


        // Proof of Purchase
        "proof-of-purchase-order-id": "Ordre-id: { NUMBER($orderId, useGrouping: 0) }",
        "proof-of-purchase-open-receipt-button": "Se kvittering",
        "proof-of-purchase-subscription-type": "Abonnementstype: { $limit } dage pr. { $period }",
        "proof-of-purchase-usage-remaining": "Forbrug: { $remaining } dage tilbage",
        "usage-period-week": "uge",
        "usage-period-month": "måned",
        "proof-of-purchase-order-type-lunch": "{ -order-type-lunch }",
        "proof-of-purchase-order-type-takeaway": "{ -order-type-take-away }",
        "proof-of-purchase-order-type-take-away": "{ -order-type-take-away }",
        "proof-of-purchase-order-type-catering": "{ -order-type-catering }",
        "proof-of-purchase-order-type-meeting": "{ -order-type-meeting }",
        "proof-of-purchase-order-type-refill-account": "{ -order-type-refill-account }",
        "proof-of-purchase-order-type-auto-refill-account": "{ -order-type-auto-refill-account }",
        "proof-of-purchase-order-type-lunch-subscription": "{ -order-type-lunch-subscription }",
        "proof-of-purchase-order-type-subscribe-payment-card": "{ -order-type-subscribe-payment-card }",
        "proof-of-purchase-order-type-refund": "{ -order-type-refund }",
        "receipt-qr-dialog-back": "Tilbage",
        "receipt-no-deliveries": "Ordren indeholder ingen leveringer",
        "receipt-company-orderlines": "Firmakøb",

        "receipt-product-paid-by-free": "(gratis)",
        "receipt-product-paid-by-credit-card": "(via\u00A0kort)",
        "receipt-product-paid-by-one-click-payment": "(via\u00A0kort)",
        "receipt-product-paid-by-mobile-pay": "(via\u00A0MobilePay)",
        "receipt-product-paid-by-payroll-deduction": "(via\u00A0løntræk)",
        "receipt-product-paid-by-lunch-registration": "(via\u00A0frokostordning)",
        "receipt-product-paid-by-guest-lunch-registration": "(via\u00A0frokostordning)",
        "receipt-product-paid-by-gopay-account": "(via\u00A0{ -gopay-wallet })",
        "receipt-product-paid-by-invoice": "(via\u00A0faktura)",


        // Receipt
        "receipt-title": "Kvittering",
        "receipt-forward-receipt-dialog": "\n .title = { $receipts -> \n *[one] Send kvittering \n [other] Send kvitteringer \n } \n .cancelButtonLabel = Annuller\n .submitButtonLabel = Send\n .submitButtonLoadingLabel = Sender...",
        "receipt-forward-receipt-email-field": "\n .label = E-mail adresse",
        "receipt-forward-receipt-email-field-is-required": "E-mail adresse skal udfyldes",
        "receipt-forward-receipt-email-field-must-be-valid": "Ugyldig e-mail adresse",

        // Purchase section
        "receipt-purchase-details-section": "\n .heading = Købsdetaljer",
        "receipt-purchase-payment-status-paid": "BETALT",
        "receipt-purchase-payment-status-unpaid": "EJ BETALT",
        "receipt-purchase-payment-status-invoiced": "FAKTURERET",
        "receipt-purchase-payment-status-refunded": "REFUNDERET",
        "receipt-purchase-payment-status-cancelled": "REFUNDERET",
        "receipt-purchase-payment-status-failed": "FEJLET",
        "receipt-purchase-address-item": "\n .label = Købssted:",
        "receipt-purchase-seller-vat-number": "CVR:\u00A0{ $vatNumber }",
        "receipt-purchase-customer-item": "\n .label = Kunde:",
        "receipt-purchase-time-item": "\n .label = Købstidspunkt:",
        "receipt-purchase-payment-method-item": "\n .label = Betalingsmetode:",
        "receipt-purchase-order-id-item": "\n .label = Ordre-id:",
        "receipt-purchase-shop-channel-item": "\n .label = Via:",
        "receipt-purchase-cost-center-item": "\n .label = Kontostreng:",

        // Actions section
        "receipt-actions-forward-receipt-button": "Send kvittering",
        "receipt-actions-cancel-order-button": "Annuller bestilling",
        "receipt-actions-delete-order-button": "Slet ordre",
        "receipt-actions-edit-order-button": "Rediger",
        "receipt-actions-update-order-button": "Rediger ordre",
        "receipt-actions-confirm-cancel-dialog": "\n .description = Du er ved at annullere din bestilling. Er du sikker?\n .confirmLabel = Ja\n .cancelLabel = Nej\n .loadingDescription = Annullerer bestilling...",
        "receipt-actions-cancel-unavailable-dialog": "\n .buttonLabel = Forstået",

        // Delivery section
        "receipt-delivery-details-section": "\n .heading = Levering",
        "receipt-delivery-order-type-item": "\n .label = Ordretype:",
        "receipt-order-type-lunch": "{ -order-type-lunch }",
        "receipt-order-type-takeaway": "{ -order-type-take-away }",
        "receipt-order-type-take-away": "{ -order-type-take-away }",
        "receipt-order-type-catering": "{ -order-type-catering }",
        "receipt-order-type-meeting": "{ -order-type-meeting }",
        "receipt-order-type-meeting-service": "{ -order-type-meeting-service }",
        "receipt-order-type-refill-account": "{ -order-type-refill-account }",
        "receipt-order-type-auto-refill-account": "{ -order-type-auto-refill-account }",
        "receipt-order-type-lunch-subscription": "{ -order-type-lunch-subscription }",
        "receipt-order-type-subscribe-payment-card": "{ -order-type-subscribe-payment-card }",
        "receipt-order-type-refund": "{ -order-type-refund }",
        "receipt-delivery-type-item": "\n .label = Leveringstype:",
        "receipt-delivery-type-pick-up": "Afhentning",
        "receipt-delivery-time-item": "\n .label = Leveringstid:",
        "receipt-delivery-comment-item": "\n .label = Kommentar:",

        // Order details section
        "receipt-payment-method-free": "Gratis",
        "receipt-payment-method-credit-card": "Kreditkort",
        "receipt-payment-method-one-click-payment": "Kreditkort",
        "receipt-payment-method-mobile-pay": "MobilePay",
        "receipt-payment-method-salary-reduction": "Løntræk",
        "receipt-payment-method-payroll-deduction": "Løntræk",
        "receipt-payment-method-lunch-registration": "Frokostordning",
        "receipt-payment-method-guest-lunch-registration": "Frokostordning",
        "receipt-payment-method-gopay-account": "{ -gopay-wallet }",
        "receipt-payment-method-invoice": "Faktura",
        "receipt-payment-method-subscribe-payment-card": "Tilmeldt betalingskort",

        "receipt-order-details-buyerparty-private": "Privatkøb",
        "receipt-order-details-buyerparty-company": "Firmakøb",
        "receipt-order-details-vat-total": "\n .label = Heraf moms:",

        // Accounting details section
        "receipt-accounting-details-section": "\n .heading = Kontering",


        // Order details
        "orderDetails-deliveryType-delivery": "\n .title = Levering",
        "orderDetails-deliveryType-pick-up": "\n .title = Afhentning",
        "orderDetails-deliveryType-eating-in-canteen": "\n .title = Spiser i kantinen",
        "orderDetails-attendees-subject-label" : "\n    .title = Mødedeltagere ({$count})",
        "orderDetails-note-label" : "\n    .title = Note",
        "orderDetails-catering-totalAmountIncludeVat-label" : "Beløb inkl. moms",
        "orderDetails-header-label" : "\n    .title = Mit møde",
        "orderDetails-meeting-privacyStatus-label" : "\n    .label = Privat",
        "orderDetails-resources-subject-label" : "Ressourcer",


        // Confirmation-dialog
        "confirmation-dialog-cancel": "Annuller",


        // Notifications
        "notification-snackbar-action-view": "Vis",
        "notification-snackbar-default-message": "Ny besked",
        "notifications-page-title": "\n .primaryTopBarTitle = Beskeder",
        "notifications-no-notifications" : "\n .title = Ingen beskeder lige nu\n .description = Her vil du kunne læse beskeder fra kantinen og servicecentret.",
        "notifications-no-notifications-notification-time-type-news": "Nyhed",
        "notifications-no-notifications-notification-time-type-info": "Information",
        "notifications-no-notifications-notification-time-type-important": "Vigtigt",
        "notifications-no-notifications-notification-time-type-offer": "Tilbud",
        "notifications-no-notifications-notification-time-type-event": "Arrangement",
        "notifications-no-notifications-notification-time-type-reminder": "Påmindelse",
        "notifications-no-notifications-notification-time-type-menu-change": "Menu",
        "notifications-no-notifications-notification-time-type-feedback": "Feedback",
        "notifications-no-notifications-notification-time-type-health": "Sundhed",
        "notifications-sender-type-merchant": "Salgssted",
        "notifications-sender-type-service-center": "Service",


        // Room Finder
        "roomfinder-tabs-searcher": "Søger",
        "roomfinder-tabs-overview": "Oversigt",
        "roomfinder-overview-find-room": "\n    .label = Find lokale",
        // "roomFinder-availableResourceGroup-1": "Ledige rum",
        // "roomFinder-availableResourceGroup-2": "Ledige AV udstyr",
        // "roomFinder-availableResourceGroup-4": "Ledige biler",


        // Week menu
        "home-week-menu-title": "\n .title = Ugens menu",
        "weekMenu-badge": "Ugens menu",
        "weekMenu-header-label": "\n .title = Ugens menu",
        "weekMenu-no-menu-label": "Der er desværre ikke oprettet menuer i denne uge.",
        "weekMenu-no-menu-subtitle": "Her vil du kunne se menuen for de kommende dage, hvis den er tilgængelig. Du kan ikke foretage køb her.",


        // Home Booking Meeting
        "home-book-meeting-title": "\n .title = Mødeplanlægning",


        // Meeting Planning Dialog
        "order-meeting-service": "Bestil mødeforplejning",
        "book-resources": "Book ressourcer",


        // Booking Meeting Screen
        "meeting-service-screen-add-title": "Tilføj mødeforplejning",
        "meeting-service-screen-edit-title": "Rediger mødeforplejning",
        "meeting-planning-screen-title": "Tilføj levering af mødeforplejning",
        "delivery-form-delivery-type-field-title": "Leveringstype",
        "cancel-delivery": "\n .description = Ønsker du at annullere nuværende ordre?\n .confirmLabel = Ja\n .cancelLabel = Nej",


        // Add Initial Delivery Form
        "delivery-form-meeting-field-label": "\n .label = Mødenavn\n .title = Mødenavn\n .placeholder = Indtast mødenavn",
        "delivery-form-date-from-field-label": "\n .label = Fra",
        "delivery-form-date-to-field-label": "\n .label = Til",
        "delivery-form-meeting-field-date-label": "\n .label = Leveringsdato\n .placeholder = Vælg leveringsdato",
        "delivery-form-delivery-type-field-label": "\n .label = Leveringstype\n .placeholder = Vælg leveringstype",
        "delivery-form-delivery-location-field-label": "\n .label = Leveringssted\n .placeholder = Vælg leveringssted",
        "delivery-form-delivery-other-location-field-label": "\n .label = Andet leveringssted",
        "delivery-form-delivery-other-location-field-button": "+ Andet leveringssted",
        "delivery-form-delivery-time-field-label": "\n .label = Leveringstid\n .placeholder = Vælg leveringstid",
        "delivery-form-delivery-time-field-select-label": "Vælg tid",
        "delivery-form-delivery-seats-field-label": "\n .label = Kuverter",
        "delivery-form-delivery-seats-field-placeholder": "Indtast eller vælg kuverter",
        "delivery-form-delivery-other-message-field-label": "\n .label = Ordrebesked\n .title = Ordrebesked\n .placeholder = Skriv en besked til din ordre",
        "delivery-payment-form-delivery-bill-receiver-field-label": "Regningsmodtager",
        "delivery-payment-form-delivery-bill-receiver-field-placeholder": "Vælg regningsmodtager",
        "delivery-payment-form-delivery-cost-center-field-label": "Costcenter",
        "delivery-payment-form-delivery-cost-center-field-placeholder": "Indtast costcenter",

        "delivery-form-meeting-field-name-required": "Mødenavn er påkrævet",
        "delivery-form-meeting-field-name-max-length": "Maksimal længde er { $maxLength} tegn",
        "delivery-form-meeting-field-other-location-max-length": "Den maksimale længde for Andet leveringssted er { $maxLength } tegn",
        "delivery-form-meeting-field-other-message-max-length": "Maksimal længde for anden besked er { $maxLength } tegn",
        "delivery-form-meeting-field-from-date-required": "Startdato er påkrævet",
        "delivery-form-meeting-field-date-required": "Leveringsdato er påkrævet",
        "delivery-form-meeting-field-from-date-should-be-before-to-date": "Startdato skal være før slutdato",
        "delivery-form-meeting-field-to-date-required": "Slutdato er påkrævet",
        "delivery-form-meeting-field-delivery-type-required": "Leveringstype er påkrævet",
        "delivery-form-meeting-field-delivery-time-required": "Leveringstidspunkt er påkrævet",
        "delivery-form-meeting-field-delivery-time-available": "Leveringstid på den valgte dag er tilgængelig fra { $from } til { $to }",
        "delivery-form-meeting-field-delivery-time-need-date": "Vælg venligst en leveringsdato først.",
        "delivery-form-delivery-location-field-required": "Leveringssted er påkrævet",
        "delivery-form-delivery-other-location-field-required": "Andet leveringssted er påkrævet",
        "delivery-form-meeting-field-seats-min-length": "Minimum antal pladser er { $minLength}",
        "delivery-form-meeting-field-seats-max-length": "Maksimalt antal pladser er { $maxLength}",
        "delivery-payment-form-delivery-dynamic-field-required": "{ $field } er påkrævet",

        "delivery-form-meeting-submit-button": "Fortsæt ({ $finalPrice})\n .loadingLabel = Indlæser...",

        "delivery-form-delivery-extra-services-title": "Ekstra Services",
        "delivery-form-delivery-extra-services-add": "Tilføj",
        "delivery-form-delivery-extra-services-remove": "Fjern",
        "delivery-form-delivery-extra-services-per-person": "{ $pricePerSeat} pr. kuvert",
        "delivery-form-delivery-extra-services-min-max": "Min { $minimumPrice} | Maks { $maximumPrice}",


        // Delivery Type Options
        "delivery-type-option-canteen": "Spiser i kantinen",
        "delivery-type-option-pick-up": "Henter selv",
        "delivery-type-option-delivery": "Levering",


        // Other Location
        "other-location": "Andet leveringssted",


        // Basket
        "basket-removeAll-label" : "Fjern alle",
        "basket-meeting-catering-title" : "Meeting catering",


        // Meeting options
        "meetingOptions-extendMeeting-label": "Forlæng møde",
        "meetingOptions-endMeeting-label": "Afslut møde",
        "meetingOptions-viewDetails-label": "Vis detaljer",
        "meetingOptions-delete-label": "Aflys møde",
        "meetingOptions-edit-label": "Rediger møde",
        "meetingOptions-assistance-label": "Assistance",
        "-meetingOptions-startMeeting-base-label": "Start møde",
        "meetingOptions-startMeeting-expired-label": "{-meetingOptions-startMeeting-base-label} (udløbet)",
        "meetingOptions-startMeeting-left-label": "{-meetingOptions-startMeeting-base-label} ({$time}} tilbage)",
        "meetingOptions-startMeeting-in-label": "{-meetingOptions-startMeeting-base-label} (om {$time}})",

        "meeting-planned-complete": "Ordren er gennemført",
        "meeting-planned-added": "Mødeforplejning tilføjet",
        "meeting-planned-updated": "Mødeservice opdateret",
        "meetingBooked-next-choice-go-to-resource-booking": "Tilføj ressourcebooking til ordre",

        // Confirm Meeting Planning Modal
        "confirm-meeting-planning-modal-title": "Fristen er overskredet",
        "confirm-meeting-planning-modal-description": "Du kan stadig afgive bestillingen, men den skal godkendes af personalet, hvilket betyder, at du ikke kan forvente, at den bliver leveret.",
        "confirm-meeting-planning-modal-button-denied": "Gennemfør ikke bestillingen",
        "confirm-meeting-planning-modal-button-accept": "Gennemfør bestilling",

        // meetings
        "meetingBooked-complete": "Booking gennemført",
        "meetingBooked-when": "Hvornår:",
        "meetingBooked-next-choice": "Hvad vil du foretage dig nu?",
        "meetingBooked-next-choice-go-to-meeting-planning": "Tilføj mødeforplejning",
        "meetingBooked-next-choice-finish": "Jeg er færdig",
        "meetingStarted-labels": "\n    .topbarTitle = Møde påbegyndt\n    .title = Mødet er blevet påbegyndt\n    .buttonLabel = Fortsæt",
        "meetingEnded-labels": "\n    .topbarTitle = Møde afsluttet\n    .title = Mødet er blevet afsluttet\n    .buttonLabel = Fortsæt",
        "meetingDeleted-labels": "\n    .topbarTitle = Møde slettet\n    .title = Mødet er blevet slettet\n    .buttonLabel = Fortsæt",
        "meetingBooked-labels": "\n    .topbarTitle = Møde booket\n    .buttonLabel = Næste",
        "meetingUpdated-labels": "\n    .topbarTitle = Møde opdateret\n    .buttonLabel = Næste",
        "meeting-open-in-calendar": "Føj møde til kalenderen",


        // Buttons
        "edit-button": "Rediger",
        "delete-button": "Slet",


        // News
        "allNews-header-label": "\n    .title = Nyheder",
        "allNews-noNews-label": "Ingen nyheder",
        "newsDetails-header-label": "\n    .title = Mine nyheder",
        "newsDetails-by-label": "Af {$creator} | {$company}",

        "completeBooking-advancedOptions-button-label": "Næste",
        "completeBooking-advancedOptions-button-complete": "Udfør",
        "completeBooking-advancedOptions-booking-time": "Booking tid:",
        "completeBooking-advancedOptions-header-label": "Flere indstillinger",
        "completeBooking-advancedOptions-privateMeeting-label": "Privat møde",
        "completeBooking-advancedOptions-sittingsArrangement-label": "Siddearrangement",
        "completeBooking-header-label": "\n .title = Færdiggør booking",
        "completeBooking-meeting-endTime-label": "Til",
        "completeBooking-meeting-header-label": "Detaljer",
        "completeBooking-meeting-meetingName-label": "Mødenavn",
        "completeBooking-meeting-startTime-label": "Fra",
        "completeBooking-meeting-card-meeting-details-title": "Mødedetaljer",
        "completeBooking-meeting-card-resources-title": "Ressourcer",
        // Misc
        "action-sheet-cancel-button": "Annuller",
        "alert-dialog-close-button": "Luk",
        "options-novalue" : "-- Vælg --",

        // Tooltip hints
        "unavailable-date-tooltip": "Denne dato kan ikke vælges",

        // Errors
        "error-required": "Dette felt skal udfyldes",
        "error-max-length": "Maksimal længde er {$maxLength}",

        // --- API components ---
        "api-query-table-fetch-error-message": "Fejl ved indlæsning af data",
        "api-query-autocomplete": "Indlæser data...",
        "api-query-autocomplete-fetch-error-message": "Fejl ved indlæsning af data. <retryLink>Prøv igen</retryLink>",
    },
    "en-US": {
        // Branding
        "-gopay-app-name": "GoPay",
        "-gopay-wallet": "GoPay Wallet",
        "-gopay-wallet-refill": "{ $pos -> \n [initial] { $count -> \n *[one] Top up \n [other] Top ups \n } \n *[other] { $count -> \n *[one] top up \n [other] top ups \n } \n }",


        // Preferred hour cycle for locale
        // NOTE: Only necessary to specify if default for country/region from CLDR supplementary data used by browser Intl API is incorrect
        // See: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/Locale/hourCycle
        // See: https://github.com/unicode-org/cldr/blob/master/common/supplemental/supplementalData.xml#L4768
        "date-time-hour-cycle": "h12",

        // Date and time formats as Luxon format strings
        // See: https://moment.github.io/luxon/docs/manual/formatting.html#table-of-tokens
        "date-time-format-date": "MM/dd/yyyy",
        "date-time-format-day-and-date": "EEEE MM/dd",
        "date-time-format-time": "hh:mm\u00A0a",
        "date-time-format-date-and-time": "MM/dd/yyyy\u00A0hh:mm\u00A0a",
        "date-time-format-duration-hours-minutes": "h'h.' m'min.'",
        "date-time-format-duration-minutes": "m'min.'",
        "date-time-format-duration-seconds": "s's'",
        "date-time-format-month-and-year": "MMMM, yyyy",
        "date-time-short-relative-units": "s|m|h|d|w|mo|y",


        // success / error handling
        "success-modal-status": "\n .buttonLabel = Close",
        "error-route-status": "\n .topbarTitle = Error",
        "error-dialog-close-button": "\n .buttonLabel = Close",
        "action-failed-modal": "\n .buttonLabel = Close",
        "errorHandler-labels": "\n    .title = Something went wrong\n    .buttonLabel = Click here to go to the front page",
        "errorHandlerModal-labels": "\n    .title = Something went wrong\n    .buttonLabel = Click here to close modal",
        "error-clientNetworkError-label" : "No Internet connection",
        "unknown-error-title": "Unknown Error",
        "unknown-error-message": "No additional information available",
        "general-error-title": "Something went wrong",
        "internal-error-title": "Internal Error",
        "api-call-error-title-network-error": "Network Error",
        "api-call-error-title-bad-request": "Bad Request",
        "api-call-error-title-unauthorized": "Invalid login",
        "api-call-error-title-forbidden": "Access Denied",
        "api-call-error-title-not-found": "Not Found",
        "api-call-error-title-conflict": "Conflict",
        "api-call-error-title-too-many-requests": "Too Many Requests",
        "api-call-error-title-internal-server-error": "Internal Server Error",
        "api-call-error-title-service-unavailable": "Service Unavailable",
        "api-call-error-title-request-failed": "Request Failed",
        "api-call-error-no-internet-connection": "No Internet connection",
        "api-call-error-could-not-reach-server": "Could not reach server",
        "api-call-error-protocol-error": "Server error: { $responseCode } { $responseStatus }",
        "api-call-error-api-returned-error": "Unexpected server error",
        "api-call-error-response-not-json": "Unexpected response from server",
        "api-call-error-retry-button": "Retry",

        // accept terms and conditions
        "accept-terms-and-conditions": "I accept the <conditionsLink>terms and conditions</conditionsLink>",


        // authenticate common texts
        "-authenticate-submit-button": "Next",


        // Order types shared translations
        "-order-type-lunch": "{ $count -> \n *[one] Daily purchase \n [other] Daily purchases \n }",
        "-order-type-take-away": "Takeaway",
        "-order-type-catering": "Meeting services",
        "-order-type-meeting": "{ $count -> \n *[one] Booking \n [other] Bookings \n }",
        "-order-type-meeting-service": "{ $count -> \n *[one] Meeting Service \n [other] Meeting Services \n }",
        "-order-type-lunch-subscription": "{ $count -> \n *[one] Subscription \n [other] Subscriptions \n }",
        "-order-type-subscribe-payment-card": "Subscribe payment card",
        "-order-type-refund": "{ $count -> \n *[one] Refund \n [other] Refunds \n }",


        // user prompts
        "user-prompt-button-label-saving": "\n .loadingLabel = Saving...",

        // onboarding flow (right after login)
        "onboarding-skip-step": "Skip",
        "onboarding-step-of": "Step { $step } of { $total }",
        "onboarding-location-title": "Choose location",
        "onboarding-location-title-private-user": "Choose shop",
        "onboarding-location-description": "Your location is the place you usually work from. You can always change it at the top of the app.",
        "onboarding-push-title": "May we send you push notifications?",
        "onboarding-push-description": "You will be notified when your order is confirmed and ready for pickup, if something changes in your order or booking, and when a meeting is coming up. Messages from the canteen only come from the canteen you have chosen.",
        "onboarding-push-footnote": "Can be changed under Me › Settings › Push notifications",
        "onboarding-push-continue-button": "Continue\n .loadingLabel = Saving...",
        "onboarding-done-title": "{ $location } is selected",
        "onboarding-done-title-no-location": "You are all set",
        "onboarding-done-push-both": "You will receive notifications about operations and orders as well as news and offers. Can be changed under Me › Settings › Push notifications.",
        "onboarding-done-push-tx": "You will receive notifications about operations and orders. Can be changed under Me › Settings › Push notifications.",
        "onboarding-done-push-news": "You will receive notifications about news and offers. Can be changed under Me › Settings › Push notifications.",
        "onboarding-done-push-none": "You will not receive push notifications. You can turn them on under Me › Settings › Push notifications.",
        "onboarding-done-button": "Go to front page",

        // push notification types & settings
        "push-type-tx-title": "Operations and orders",
        "push-type-tx-description": "Important messages, orders and meetings.",
        "push-type-news-title": "News and offers",
        "push-type-news-description": "Today's menu, news and offers.",
        "push-settings-title": "\n .primaryTopBarTitle = Push notifications",
        "push-settings-status-unavailable": "Push notifications are only available in the app. Your choices below are saved and used in the app.",
        "push-settings-status-allowed": "Push notifications are allowed on this phone",
        "push-settings-status-denied-title": "Turned off on this phone",
        "push-settings-status-denied-text": "Regardless of the choices below we will not send any messages until you allow them on this phone.",
        "push-settings-allow-button": "Allow push notifications\n .loadingLabel = Saving...",
        "push-settings-open-settings-link": "Open settings",
        "push-settings-turn-off-link": "Turn off completely in the phone's settings",


        // welcome
        "authenticate-welcome-slide-header-buy-lunch": "Buy lunch and company services",
        "authenticate-welcome-slide-text-buy-lunch": "Easily purchase food from the lunch company and other company services such as catering at events and dry cleaning.",
        "authenticate-welcome-slide-header-reduce-food-waste": "Fight food waste",
        "authenticate-welcome-slide-text-reduce-food-waste": "Pre-order and cancel your lunch so the kitchen can plan food production. Select featured menus that are better for the environment.",
        "authenticate-welcome-slide-header-receive-offers": "This week's menus and deals",
        "authenticate-welcome-slide-text-receive-offers": "View the week's menus with nutrition information, discover deals and read news.",
        "authenticate-welcome-slide-header-purchase-overview": "View your purchases",
        "authenticate-welcome-slide-text-purchase-overview": "Get a good overview of your order history and receipts. If you pay by payroll deduction you can easily reconcile.",
        "authenticate-welcome-continue-button": "Login / Create Account",


        // login choices
        "authenticate-login-choices-header": "Order food and other services",
        "authenticate-login-choices-login-button": "Login",
        "authenticate-login-choices-create-account-button": "Create Account",
        "authenticate-login-choices-guest-link": "Buy as guest",


        // login / signup email
        "authenticate-email-header": "Enter your email address",
        "authenticate-email-signup-header": "Sign Up",
        "authenticate-email-email-input": "\n    .label = Email address",
        "authenticate-email-submit-button": "{ -authenticate-submit-button }\n .loadingLabel = Checking email address...",
        "authenticate-email-email-is-required": "Input your email address",
        "authenticate-email-email-must-be-an-email": "Invalid email",
        "authenticate-email-expect-company-flow": "You are attempting to sign up with the email domain <emailDomain>{ $emailDomain }</emailDomain> which is associated with a company. If you want to create a private account, you should use your private email address.",
        "authenticate-email-expect-private-flow": "Your email domain <emailDomain>{ $emailDomain }</emailDomain> is not currently associated with any company in the system. You must use your work email and not your private email address. Contact your administrator, if you believe your domain should be associated with a company.",
        "authenticate-email-switch-to-login-flow": "An account already exists with this email address. Do you wish to login instead?",
        "authenticate-email-switch-to-signup-flow": "No account found in the system with this email address. Do you wish to create one instead?",
        "authenticate-email-switch-flow-no-button": "No thanks",
        "authenticate-email-switch-flow-yes-button": "Yes please",
        "authenticate-email-okay-button": "Understood",


        // activate by phone
        "authenticate-phone-number-header": "Activate Account",
        "authenticate-phone-number-text": "We need your mobile number so we can send you an activation code:",
        "authenticate-phone-number-phone-number-input": "\n .label = Phone number",
        "authenticate-phone-number-submit-button": "{ -authenticate-submit-button }\n .loadingLabel = Sending code...",
        "authenticate-phone-number-username-missing": "Username / email is missing. Please go back and fill it in.",
        "authenticate-phone-number-phone-number-is-required": "Phone number is required",
        "authenticate-phone-number-phone-number-is-invalid": "Invalid phone number",


        // activation code
        "authenticate-activation-code-header": "Activate Account",
        "authenticate-activation-code-automatic-activation-text": "Please wait while we activate your account...",
        "authenticate-activation-code-manual-activation-text": "Please input the code you have received by email or text message:",
        "authenticate-activation-code-activation-code-input": "\n .label = Activation code",
        "authenticate-activation-code-submit-button": "{ -authenticate-submit-button }\n .loadingLabel = Activating...",
        "authenticate-activation-code-code-field-required": "Enter activation code",
        "authenticate-activation-code-code-field-min-len": "Activation code is min. { $codeLength } characters",


        // login/password
        "authenticate-password-header": "Enter your password",
        "authenticate-password-header-with-name": "Welcome { $displayName }! Please  sign in.",
        "authenticate-password-password-input": "\n .label = Password",
        "authenticate-password-submit-button": "{ -authenticate-submit-button }\n .loadingLabel = Signing in...",
        "authenticate-password-username-missing": "Username / email missing. Please go back and fill it in.",
        "authenticate-password-forgot-password-link": "Forgot password?",
        "authenticate-password-password-is-required": "Enter your password",


        // login/single sign on
        "authenticate-sso-header": "Single Sign-on",
        "authenticate-sso-explanation": "Your organization { $organization } requires single sign-on. You will now be sent to your organization login portal.",
        "authenticate-sso-canceled-message": "You did not complete the login process.",
        "authenticate-sso-retry-button": "Retry\n .loadingLabel = Redirecting...",


        // login/account-type
        "authenticate-account-type-choose-account-type": "Choose account type",
        "authenticate-account-type-private-account": "Private account",
        "authenticate-account-type-company-account": "Company account",
        "authenticate-account-type-private-account-description": "A private account allows you to sign up and access public shops using your personal email. Please be aware that you won't be able to access company shops with this type of account.",
        "authenticate-account-type-company-account-description": "A company account gives you access to the shops that are connected with your company. You must use your work email to sign up.",
        "authenticate-account-type-help-text": "Do I need a private or a company account?",
        "authenticate-account-type-help-drawer": "### Company Account\n\n Companies and organizations can offer services for payment via GoPay, like purchasing canteen meals. To access these shops, you need a company account. We determine your company based on your email domain.\n\n ### Private Account\n\n Besides company shops, there are public shops in GoPay where individuals can make purchases. With a private account, you can access all public shops.\n   .title = Account type explanation",


        // select company
        "authenticate-select-company-header-reason-multiple-companies-for-domain": "Choose company",
        "authenticate-select-company-header-reason-multiple-accounts-for-username": "Select user account",
        "authenticate-select-company-reason-message-multiple-companies-for-domain": "We were unable to determine your company automatically since your email domain <duplicateValue>{ $duplicateValue }</duplicateValue> occurs multiple times in our system.",
        "authenticate-select-company-reason-message-multiple-accounts-for-username": "We found multiple accounts in our system for the email address <duplicateValue>{ $duplicateValue }</duplicateValue>.",
        "authenticate-select-company-action-message-multiple-companies-for-domain": "Please choose the location (company) that you wish to create your account under:",
        "authenticate-select-company-action-message-multiple-accounts-for-username": "Please select the user account you want to log in with.",
        "authenticate-select-company-show-list-button": "Show companies",
        "authenticate-select-company-submit-button": "{ -authenticate-submit-button }\n .loadingLabel = Saving...",


        // sign up
        "authenticate-signup-header" : "Create Account",
        "authenticate-signup-text" : "We need to know a bit more about you to create your account:",
        "authenticate-signup-name-input": "\n .label = Name",
        "authenticate-signup-mobile-input": "\n .label = Mobile (optional)",
        "authenticate-signup-accept-terms-link": "I accept the terms and conditions",
        "authenticate-signup-submit-button": "{ -authenticate-submit-button }",
        "authenticate-signup-username-missing": "Username / email missing. Please go back and fill it in.",
        "authenticate-signup-name-is-required": "Name is required",
        "authenticate-signup-name-must-be-full-name": "We need your full name (given name and family name)",
        "authenticate-signup-phone-number-is-invalid": "Invalid phone number",
        "authenticate-signup-terms-must-be-accepted": "You must accept the terms to use the app",


        // confirm account details
        "authenticate-confirm-account-details-header": "Confirm Details",
        "authenticate-confirm-account-details-email-field": "\n .primary = Email",
        "authenticate-confirm-account-details-name-field": "\n .primary = Name",
        "authenticate-confirm-account-details-mobile-field": "\n .primary = Mobile",
        "authenticate-confirm-account-submit-button": "{ -authenticate-submit-button }\n .loadingLabel = Creating your account...",
        "authenticate-confirm-account-details-account-missing": "Account details missing. Please go back and fill them in.",


        // create password
        "authenticate-create-password-header": "Create Password",
        "authenticate-create-password-text": "You must create a password to use your new account:",
        "authenticate-create-password-password-input": "\n .label = Password",
        "authenticate-create-password-repeat-password-input": "\n .label = Repeat password",
        "authenticate-create-password-submit-button": "{ -authenticate-submit-button }\n .loadingLabel = Creating your password...",
        "authenticate-create-password-one-time-token-missing": "Could not find one time token. Please go a step back and retry.",
        "authenticate-create-password-password-is-required": "You must type a password",
        "authenticate-create-password-password-too-short": "Your password must be at least { $minPasswordLength } characters",
        "authenticate-create-password-repeat-is-required": "You must repeat your password",
        "authenticate-create-password-password-mismatch": "Your passwords do not match",


        // reset password
        "authenticate-reset-password-header": "Reset Password",
        "authenticate-reset-password-button": "Reset\n .loadingLabel = Resetting...",
        "authenticate-reset-password-username-missing": "Username / email missing. Please go back and fill it in.",


        // login route components
        "login-external-guest-dialog": "\n .title = Guest Purchase\n .message = You are about to purchase as guest. Shop will open in a new window.\n .buttonLabel = Continue",


        // --- User cards ---
        "user-cards-loading-element": "\n .title = Loading cards...",


        // home
        "-show-all": "Show all",
        "-home-upcoming-meetings": "Upcoming Meetings",
        "-home-offers": "Great Deals",
        "-home-shops": "Shops",
        "-home-my-favorites": "My Favorites",

        "home-label": "Home",

        "home-news": "\n    .title = News\n    .rightOptionText = {-show-all}",
        "home-news-kitchen": "\n    .title = News from the kitchen\n    .rightOptionText = { -show-all }",
        "home-news-company-and-service-center": "\n    .title = News from {$company}\n    .rightOptionText = { -show-all }",
        "home-news-facilitynet": "\n    .title = News from { -gopay-app-name }\n    .rightOptionText = { -show-all }",

        "home-my-meetings": "\n    .title = { -home-upcoming-meetings }\n    .rightOptionText = {-show-all}",
        "home-upcoming-meetings": "{ -home-upcoming-meetings }",
        "home-contact-the-canteen": "Contact the canteen",
        "home-no-planned-meetings": "You don't have any planned meetings",
        "home-plan-meeting": "Plan a meeting",

        "home-you-are-here": "You are here:",
        "home-change-location": "Change location",

        "home-takeaway-offers": "\n     .title = { -home-offers } \n     .rightOptionText = {-show-all}",
        "home-all-takeaway-offers-title": "\n   .primaryTopBarTitle = { -home-offers }",

        "home-takeaway-shops": "\n      .title = { -home-shops } \n      .rightOptionText = {-show-all}",
        "home-all-takeaway-shops-title": "\n   .primaryTopBarTitle = { -home-shops }",

        "home-product-favorites": "\n      .title = { -home-my-favorites } \n      .rightOptionText = { -show-all }",
        "home-all-product-favorites-title": "\n   .primaryTopBarTitle = { -home-my-favorites }",
        "home-all-product-favorites-empty": "You have no favorites.",
        "home-product-favorites-options": "\n .loadingDescription = Removing favorite...",
        "home-remove-favorite": "Remove favorite",

        "home-user-card-survey-rate-now": "Rate now",

        "home-user-card-check-in-reminder-check-in": "Thanks for checking in today",
        "home-user-card-check-in-reminder-no-check-in": "Please remember to check into the canteen today",
        "home-user-card-check-in-reminder-learn-more": "Learn more",

        "home-user-card-lunch-subscription-used-count": "{ $used } of { $total }",
        "home-user-card-lunch-subscription-used-this-week": "used this week",
        "home-user-card-lunch-subscription-used-this-month": "used this month",

        "home-user-card-recent-purchase-view-receipt": "View receipt",
        "home-user-card-recent-purchase-status": "Status: { $status }",
        "home-user-card-recent-purchase-status-item-received": "RECEIVED",
        "home-user-card-recent-purchase-status-item-paid": "PAID",
        "home-user-card-recent-purchase-status-item-failed": "FAILED",
        "home-user-card-recent-purchase-status-item-canceled": "CANCELLED",

        // user survey modal
        "user-survey-step2-question-good": "What were you especially happy with?",
        "user-survey-step2-question-bad": "What could we have done better?",
        "user-survey-add-comment-button": "\n {$comment ->\n      *[0] Write a\n      [other] Edit \n} comment",
        "user-survey-comment-drawer": "\n   .title = Comment",
        "user-survey-comment-field": "\n   .label = Leave your comment here",
        "user-survey-thank-you-message": "Thank you for your participation",
        "user-survey-next-button": "Next",
        "user-survey-skip-button": "Skip",
        "user-survey-submit-button": "Submit\n .loadingLabel = Sending...",
        "user-survey-close-button": "Close",
        "user-survey-comment-submit-button": "Save",
        "user-survey-comment-cancel-button": "Cancel",


        // navigation
        "bottom-navigation-home": "\n   .label = Home",
        "bottom-navigation-todays-purchases": "\n   .label = Today",
        "bottom-navigation-order": "\n   .label = Order",
        "bottom-navigation-messages": "\n   .label = Messages",
        "bottom-navigation-me": "\n   .label = Me",

        "app-navigation-to-shop-select": "Go to Shops",
        "app-navigation-to-login": "Go to Login",
        "app-navigation-to-home": "Go to Home",


        // Consent dialog
        "consent-dialog-personal-information-header": "Personal Information",
        "consent-dialog-pii-category-contact-info": "Contact Information",
        "consent-dialog-pii-category-identifiers": "Identifiers",
        "consent-dialog-pii-category-purchases": "Purchases",
        "consent-dialog-pii-category-user-content": "User Content",
        "consent-dialog-pii-item-name": "Name",
        "consent-dialog-pii-item-email-address": "Email",
        "consent-dialog-pii-item-phone-number": "Phone Number",
        "consent-dialog-pii-item-user-id": "User ID",
        "consent-dialog-pii-item-device-id": "Device ID",
        "consent-dialog-pii-item-customer-support": "Customer Service",
        "consent-dialog-pii-item-purchase-history": "Purchase History",
        "consent-dialog-privacy-policy-header": "Read our privacy policy terms",
        "consent-dialog-privacy-policy-link": "See company's privacy policy",


        // My Meetings
        "my-meetings-sittings": "{$amount} sittings",
        "my-meetings-header-label": "\n   .primaryTopBarTitle = My Meetings",
        "my-meetings-tab-meeting-booking": "\n .label = Resource bookings",
        "my-meetings-tab-meeting-service": "\n .label = Meeting service",
        "my-meetings-now-badge": "\n      .label = Now",
        "my-meetings-search-label": "\n   .label = Search",
        "my-meetings-no-filtered-label": "No matches",
        "my-meetings-no-meetings-label": "No planned meetings",


        // Meeting details
        "meeting-details-delete-dialog": "\n  .description = Delete delivery on {$date}?\n .loadingDescription = Deleting...\n  .confirmLabel = Delete",


        // Todays purchases
        "todays-orders-receipt-top-bar-title": "Todays Purchases",
        "todays-orders-receipt-top-bar-title-length": "Todays Purchase {$selectedOrder} of {$maxOrders}",
        "todays-orders-none-found": "\n .title = No purchases\n .description = You do not have purchases for today.",
        "todays-orders-no-such-order": "Order not found",


        // My Orders
        "my-orders-page": "\n  .primaryTopBarTitle = My Orders",
        "my-orders-page-header": "\n .title = My Orders",

        // My Orders tabs
        "my-orders-tab-purchases": "\n .label = Daily purchases",
        "my-orders-tab-wallet-refills": "\n .label = { -gopay-wallet-refill(pos: \"initial\", count: 100) }",

        // My Orders expenses chart tabs
        "my-orders-expenses-chart-tab-last-week": "\n .label = Last 7 days",
        "my-orders-expenses-chart-tab-last-month": "\n .label = Last 30 days",
        "my-orders-expenses-chart-tab-last-year": "\n .label = Last 365 days",
        "my-orders-expenses-chart-title": "Private purchases",

        // My Orders filter button
        "my-orders-filter-button-none": "None",
        "my-orders-filter-button-all": "All",
        "my-orders-filter-button-some": "{$selected} / {$available} selected",
        "my-orders-filter-button-lunch": "{ -order-type-lunch(count: 100) }",
        "my-orders-filter-button-take-away": "{ -order-type-take-away(count: 100) }",
        "my-orders-filter-button-catering": "{ -order-type-catering(count: 100) }",
        "my-orders-filter-button-meeting": "{ -order-type-meeting(count: 100) }",
        "my-orders-filter-button-refill-account": "{ -gopay-wallet-refill(pos: \"initial\", count: 100) }",
        "my-orders-filter-button-auto-refill-account": "Automatic { -gopay-wallet-refill(count: 100) }",
        "my-orders-filter-button-lunch-subscription": "{ -order-type-lunch-subscription(count: 100) }",
        "my-orders-filter-button-subscribe-payment-card": "{ -order-type-subscribe-payment-card(count: 100) }",
        "my-orders-filter-button-refund": "{ -order-type-refund(count: 100) }",

        // My Orders filter order type
        "my-orders-filter-order-type-lunch": "\n .label = { -order-type-lunch(count: 100) }",
        "my-orders-filter-order-type-take-away": "\n .label = { -order-type-take-away(count: 100) }",
        "my-orders-filter-order-type-catering": "\n .label = { -order-type-catering(count: 100) }",
        "my-orders-filter-order-type-meeting": "\n .label = { -order-type-meeting(count: 100) }",
        "my-orders-filter-order-type-refill-account": "\n .label = { -gopay-wallet-refill(pos: \"initial\", count: 100) }",
        "my-orders-filter-order-type-auto-refill-account": "\n .label = Automatic { -gopay-wallet-refill(count: 100) }",
        "my-orders-filter-order-type-lunch-subscription": "\n .label = { -order-type-lunch-subscription(count: 100) }",
        "my-orders-filter-order-type-subscribe-payment-card": "\n .label = { -order-type-subscribe-payment-card(count: 100) }",
        "my-orders-filter-order-type-refund": "\n .label = { -order-type-refund(count: 100) }",

        "my-orders-filter-modal": "\n .title = Order Types\n .cancelButtonLabel = Close",
        "my-orders-filter-order-type-label": "Order Types",
        "my-orders-results": "{$amount} results",
        "my-orders-load-more-button": "Load more",
        "my-orders-price-total": "Total",
        "my-orders-print": "Print",
        "my-orders-edit": "Edit",
        "my-orders-share": "Share",
        "my-orders-delete": "Delete",
        "my-orders-receipt": "Receipts",


        // Transactions
        "transaction-generic-processing": "Please wait...",
        "transaction-generic-success": "Transaction completed",
        "transaction-missing-payment-window-url": "API did not send link to payment gateway. Please contact support",


        // Shop - payment methods
        "-payment-accept-terms": "I accept the terms and conditions",

        "payment-method-initial-message": "Choose payment method",
        "payment-method-mobile-pay-alert-message": "You will need to use the MobilePay app on your phone to complete the payment. You must approve the payment in MobilePay within 5 minutes and return here.",
        "payment-method-free": "Free",
        "payment-method-credit-card": "Credit card",
        "payment-method-mobile-pay": "MobilePay",
        "payment-method-one-click": "Pay using { $card }",
        "payment-method-salary-reduction": "Payroll deduction",
        "payment-method-lunch-registration": "Lunch registration",
        "payment-method-guest-lunch-registration": "Lunch registration",
        "payment-method-facilitynet-account": "{ -gopay-wallet } (<balance>{ $balance }</balance>)",
        "payment-method-invoice": "Invoice",
        "payment-method-card-expired": "expired",


        // --- Shopping basket ---

        // Basket section
        "shopping-basket-product-to-pay-by-free": "(for free)",
        "shopping-basket-product-to-pay-by-credit-card": "(by\u00A0card)",
        "shopping-basket-product-to-pay-by-one-click-payment": "(by\u00A0card)",
        "shopping-basket-product-to-pay-by-mobile-pay": "(by\u00A0MobilePay)",
        "shopping-basket-product-to-pay-by-payroll-deduction": "(by\u00A0payroll)",
        "shopping-basket-product-to-pay-by-lunch-registration": "(by\u00A0lunch\u00A0registration)",
        "shopping-basket-product-to-pay-by-guest-lunch-registration": "(by\u00A0lunch\u00A0registration)",
        "shopping-basket-product-to-pay-by-gopay-account": "(by\u00A0{ -gopay-wallet })",
        "shopping-basket-product-to-pay-by-invoice": "(by\u00A0invoice)",

        // Section total
        "shopping-basket-total-section-header": "To pay:",
        "shopping-basket-total-line": "\n .label = To pay",
        "shopping-basket-total-private-price": "Your payment: { $privateTotal }",
        "shopping-basket-total-company-price": "Company payment: { $companyTotal }",

        "shopping-basket-total-to-pay-by-free": "\n .label = Free",
        "shopping-basket-total-to-pay-by-credit-card": "\n .label = By card",
        "shopping-basket-total-to-pay-by-one-click-payment": "\n .label = By card",
        "shopping-basket-total-to-pay-by-mobile-pay": "\n .label = By MobilePay",
        "shopping-basket-total-to-pay-by-payroll-deduction": "\n .label = By payroll",
        "shopping-basket-total-to-pay-by-lunch-registration": "\n .label = By lunch registration",
        "shopping-basket-total-to-pay-by-guest-lunch-registration": "\n .label = By lunch registration",
        "shopping-basket-total-to-pay-by-gopay-account": "\n .label = By { -gopay-wallet }",
        "shopping-basket-total-to-pay-by-invoice": "\n .label = By invoice",

        // Accounting section
        "shopping-basket-accounting-header": "\n .description = Accounting",

        // Conditions section
        "shopping-basket-conditions-terms-label": "I accept the <conditionsLink>terms and conditions</conditionsLink>",
        "shopping-basket-conditions-terms-not-accepted": "You should accept the terms and conditions.",

        // Refill account
        "-refill-account-balance": "{ $pos -> \n [initial] Balance \n *[other] balance \n }",
        "refill-account-balance-limit": "Your { -refill-account-balance } may not exceed { $maxBalance }",

        "refill-title": "\n .primaryTopBarTitle = { -gopay-wallet }",
        "refill-tab": "\n .label = { -gopay-wallet-refill(pos: \"initial\") }",
        "refill-current-balance": "Current { -refill-account-balance }: { $currentBalanceFormatted }",
        "refill-step-description-1": "\n    .description = Select amount",
        "refill-step-description-1-custom": "Other amount:",
        "refill-no-preset-amounts": "No preset amounts available",
        "refill-step-amount-custom-amount": "\n .label = Amount ({ $currencySymbol })\n .helperText = New { -refill-account-balance } cannot exceed { $maxBalance }",
        "refill-menu-item-custom-amount": "Other",
        "refill-terms-not-provided": "The merchant did not configure terms and conditions. Please contact them to solve the problem.",
        "refill-account-perform-refill-button": "Complete { -gopay-wallet-refill } ({ $toPay })\n .loadingLabel = Performing { -gopay-wallet-refill }...",


        // Automatic account refill
        "automatic-refill-page-title": "\n .primaryTopBarTitle = Automatic Top Up",
        "automatic-refill-tab": "\n .label = Automatic { -gopay-wallet-refill }",
        "automatic-refill-description": "Automatically { -gopay-wallet-refill } account when your { -refill-account-balance } falls below a certain amount. Your { -refill-account-balance } is never allowed to surpass { $maxBalance }",
        "automatic-refill-step-description-1": "\n    .description = { -gopay-wallet-refill(pos: \"initial\") } amount",
        "automatic-refill-step-when-balance-below": "\n .description = When balance below",
        "automatic-refill-step-when-balance-below-input-amount": "\n    .label = Amount ({ $currencySymbol })",
        "automatic-refill-confirm-button": "Activate automatic { -gopay-wallet-refill }\n .loadingLabel = Activating...",
        "automatic-refill-confirm-button-activated": "Update\n .loadingLabel = Updating...",
        "automatic-refill-account": "Update automatic { -gopay-wallet-refill }",
        "automatic-refill-cancel": "Deactivate automatic { -gopay-wallet-refill }",
        "automatic-refill-cancel-modal": "\n    .description = Deactivate automatic { -gopay-wallet-refill }?\n .loadingDescription = Deactivating...\n  .confirmLabel = Confirm",
        "automatic-refill-activated-chip": "\n  .label = Active",
        "automatic-refill-limit-amount-too-low": "Balance refill limit cannot be lower than { $minLimitAmount }",
        "automatic-refill-would-exceed-max-balance": "New { -refill-account-balance } cannot exceed { $maxBalance }",

        // Top-up
        "automatic-refill-no-subscription-banner-text": "Activate automatic top up",
        "automatic-refill-no-subscription-help-link": "How does it work?",
        "automatic-refill-no-subscription-help-drawer": "By activating automatic top up you will always have money in your digital wallet.\n\n You decide how much to top up and a threshold, then the system will top up your wallet whenever the balance falls below the threshold.\n\n When activating automatic top up your credit card will be saved securely at our certified payment gateway.\n .title = How auto top up works",
        "automatic-refill-active-subscription-banner-text": "Auto top up is active",
        "automatic-refill-active-subscription-banner-below": "When balance falls below {$sum}",
        "automatic-refill-active-subscription-banner-amount": "Top up with {$amount}",

        // Lunch subscription
        "lunch-subscription-page-title": "\n .primaryTopBarTitle = My Subscriptions",
        "my-lunch-subscriptions-no-active-subscriptions": "You have no active subscriptions.",
        "lunch-subscription-subscribe-button": "Add new subscription",
        "lunch-subscription-subscribe-page-title": "\n .primaryTopBarTitle = Choose Subscription",
        "lunch-subscription-subscribe-no-offers-message": "You have no available subscriptions.",
        "lunch-subscription-choose-offer": "Choose",
        "lunch-subscription-unsubscribe-screen": "\n .primaryTopBarTitle = Cancel subscription",
        "lunch-subscription-unsubscribe-title": "Are you sure you want to cancel your subscription?",
        "lunch-subscription-unsubscribe-terms": "Read terms and conditions",

        "lunch-subscription-menu-cancel-subscription-option": "Cancel subscription",
        "lunch-subscription-subscription-conditions-modal": "\n .title = Subscription Conditions",

        "lunch-subscription-basket": "\n .primaryTopBarTitle = Add subscription",
        "lunch-subscription-basket-step-subperiod-label": "Subscription period",
        "lunch-subscription-basket-step-payment-label": "Payment",
        "lunch-subscription-basket-period": "\n .description = Select subscription start",
        "lunch-subscription-basket-period-too-late-notice": "(too late)",
        "lunch-subscription-basket-period-next-button": "Next",
        "lunch-subscription-basket-step-two": "\n .description = Diet",
        "lunch-subscription-basket-step-three": "\n .description = Payment",
        "lunch-subscription-basket-diet-vegetarian": "Vegetarian",
        "lunch-subscription-terms": "{ -payment-accept-terms }",
        "lunch-subscription-payment-method-subscribe-payment-card": "Add new payment card",
        "lunch-subscription-payment-method-one-click-payment": "Pay using { $card }",
        "lunch-subscription-payment-method-payroll-deduction": "Payroll deduction",

        "lunch-subscription-price-per-month": "{ $amount } per month",

        "lunch-subscription-order": "Subscribe\n .loadingLabel = Subscribing...",
        "lunch-subscription-cancel": "Unsubscribe",
        "lunch-subscription-cancel-button": "Unsubscribe\n .loadingLabel = Unsubscribing...",
        "lunch-subscription-closed": "Closed",
        "lunch-subscription-tab-month": "\n .label = Month",
        "lunch-subscription-tab-week": "\n .label = Week",
        "lunch-subscription-tab-day": "\n .label = Day",
        "lunch-subscription-monthly-overview-description-closed": "Kitchen is closed",
        "lunch-subscription-monthly-overview-description-subscribed": "Subscribed days",
        "lunch-subscription-on-locations": "\n .primaryTopBarTitle = Subscription locations",
        "lunch-subscription-locations": "{$amount} locations",
        "lunch-subscription-conditions": "Subscription terms",
        "lunch-subscription-start-date": "Starts on {$date}",

        "lunch-subscription-weekly-overview": "Week Overview",
        "lunch-subscription-weekly-overview-dish-of-the-day": "Dish of the Day",
        "lunch-subscription-weekly-overview-description": "Unsubscribe the days you don't want to order lunch.",


        // New Order
        "order-cart-cleared": "Basket has been emptied.",


        // Payment cards
        "-payment-cards": "{ $pos -> \n [title] Payment Cards \n [initial] Payment cards \n *[other] payment cards \n [one] payment card \n }",
        "paymentcards-title": "\n .primaryTopBarTitle = { -payment-cards(pos: \"title\") }",
        "paymentcards-card": "\n    .primary = {$number} ({$type})",
        "paymentcards-empty": "You don't have any saved { -payment-cards } on file. If you save a card here, it'll make paying easier because you won't have to enter your credit card information each time.",
        "paymentcards-too-many-cards": "\n  .text = You can have up to { $maxPaymentCards } registered { -payment-cards }. Delete a card in order to add a new one.",
        "paymentcards-button-add-payment-card": "Add { -payment-cards(pos: \"one\") }\n .loadingLabel = Adding card...",
        "paymentcards-remove-card": "Remove { -payment-cards(pos: \"one\") }",
        "paymentcards-options-dialog": "\n .loadingDescription = Removing card...",

        // Conditions section
        "paymentcards-conditions-terms-error-dialog-label": "\n .text = Your company did not configure terms and conditions. Please contact them to solve the problem.",
        "paymentcards-conditions-terms-error-label": "Cannot add cards without T&C",
        "paymentcards-conditions-terms-not-accepted": "You must accept the terms and conditions",
        "paymentcards-conditions-iframe-done-button": 'Done',


        // QuickPay
        "quickpay-generic-success": "Your payment succeeded",
        "quickpay-finalization-error-timeout": "We haven't received confirmation from your payment provider. Please check your netbank to be sure that the payment was completed.",
        "quickpay-finalization-error-other": "Payment could not be completed due to an unknown error. Please try again later.",
        "quickpay-source-mismatch-title-should-be-app": "Client mismatch",
        "quickpay-source-mismatch-title-should-be-web": "Client mismatch",
        "quickpay-source-mismatch-message-should-be-app": "If you see this message we could not switch back to your GoPay app automatically. Please switch back to the app manually.",
        "quickpay-source-mismatch-message-should-be-web": "This is your GoPay app. You used GoPay in your browser but have the app installed. This causes a conflict during credit card and MobilePay transactions. Prefer to use the app when it is installed.",
        "quickpay-canceled": "\n .title = Purchase canceled\n .message = You canceled the payment of your order.",


        // Me page
        "me-user-greeting": "Hi {$firstName}",
        "me-my-wallets-section": "\n .title = My Wallets",
        "me-my-wallets-status-auto-refill-active": "Auto top-up",
        "me-my-wallets-wallet-name": "My wallet",
        "me-my-wallets-refill-select-card-balance": "Balance:",
        "me-my-wallets-wallet-brand": "GoPay Wallet",
        "me-my-facility-net": "My { -gopay-app-name }",
        "me-my-meetings": "My Meetings",
        "me-my-orders": "My Orders",
        "me-customer-service-category": "Customer Service",
        "me-customer-service-intercom-consent-dialog": "\n .title = Allow Chat\n .message = We use Intercom for our chat support. If you consent we will share the following information with Intercom.\n .skipLabel = Not now\n .consentLabel = Allow",
        "me-customer-service-intercom-error-dialog": "\n .title = Chat Failed\n .message = Something went wrong when loading the chat. You can send an email to support instead.\n .buttonLabel = Understood",
        "me-customer-service-intercom-loading-dialog": "\n .description = Chat is loading...",
        "me-help-center": "Help Center",
        "me-chat-support": "Chat",
        "me-email-support": "Email support",
        "me-shortcuts": "Shortcuts",
        "me-payment-cards": "{ -payment-cards(pos: \"title\") }",
        "me-lunch-subscription-menu-item": "Subscription",
        "me-settings": "Settings",
        "me-switch-account": "Switch user account",
        "me-switch-account-top-bar-title": "\n .primaryTopBarTitle = Switch user account",
        "me-switch-account-description": "You have the following accounts with the email address <email>{ $email }</email>.",
        "me-switch-account-email-confirm": " Before you can switch between user accounts, you must first confirm that you own the email account <email>{ $email }</email>.",
        "me-switch-account-no-accounts": " No other accounts were found with the email address <email>{ $email }</email>.",
        "me-switch-account-invalid-authentication": "Invalid authentication response",
        "me-switch-account-send-confirmation-link": "Send confirmation link",
        "me-switch-account-confirmation-send": "An email has been send. Please check your inbox.",
        "me-switch-account-failed-to-load": "Failed to load your user accounts.",
        "me-switch-account-contact-support": "Contact support",
        "me-switch-account-send-confirmation-submit-button": "{ -authenticate-submit-button }\n .loadingLabel = Pending approval...",
        "me-logout": "Logout",

        // User account page
        "user-account-page": "\n .primaryTopBarTitle = User Account",
        "user-account-type-company-account": "\n .label = Account type\n .value = Company account",
        "user-account-type-private-account": "\n .label = Account type\n .value = Private account",
        "user-account-company": "\n  .label = Company",
        "user-account-username": "\n  .label = Username",
        "user-account-name": "\n  .label = Name",
        "user-account-email": "\n  .label = Email",
        "user-account-mobile": "\n  .label = Mobile",
        "user-account-department": "\n  .label = Department",
        "user-account-about-link": "About this account",
        "user-account-about-drawer-company-account": "### Company Account\n\n Companies and organizations can offer services for payment via GoPay, like purchasing canteen meals. With your company account, you can access these shops.\n  .title = About your account",
        "user-account-about-drawer-private-account": "### Private Account\n\n Besides company shops, there are public shops in GoPay where individuals can make purchases. With your private account, you can access all public shops.\n  .title = About your account",


        // Me settings module
        "settings-title": "\n .primaryTopBarTitle = Settings",
        "settings-change-language": "Change language",
        "settings-english": "English",
        "settings-danish": "Danish",
        "settings-norwegian": "Norwegian",
        "settings-swedish": "Swedish",
        "settings-notifications": "Notifications",
        "settings-push-notifications": "Push notifications",
        "settings-receipts-via-email": "Receipts on email",
        "settings-receipts-meeting-service": "Meeting service receipts",
        "settings-receipts-canteen": "Private canteen purchases",
        "settings-sms-notifications": "Text (SMS) notifications",
        "settings-customer-service": "Customer Service",
        "settings-social-media": "Social Media",
        "settings-policies": "Policies",
        "settings-HELP_CENTER": "Help Center",
        "settings-HELP_CENTER_USER_GUIDE_MOBILE_APP": "User Guide",
        "settings-SOCIAL_FACILITYNET_FACEBOOK": "Follow us on Facebook",
        "settings-SOCIAL_FACILITYNET_LINKEDIN": "Follow us on LinkedIn",
        "settings-PRIVACY_TERMS": "Privacy Policy",
        "settings-COOKIES_TERMS": "Cookie Policy",
        "settings-EULA": "EULA",


        // Fast track menu
        "fast-track-menu-show-shop": "\n .title = Fast Track\n .contentActionText = Go to shop",
        "fast-track-menu-no-menus": "No menus available. Contact kitchen.",


        // Anonymous enter kitchen
        "anonymous-enter-kitchen-header": "Enter shop number",
        "anonymous-enter-kitchen-shop-number-input": "\n .label = Shop number",
        "anonymous-enter-kitchen-submit-button": "{ -authenticate-submit-button }",
        "anonymous-enter-kitchen-loading-dialog": "\n .description = Loading Fast Track menu...",
        "anonymous-enter-kitchen-shop-number-requirements": "Please enter a shop number of { $minDigits }-{ $maxDigits} digits",


        // To fast track menu via route
        "to-fast-track-menu-via-route-invalid-kitchen-id": "The shop number {$kitchenCode} does not exist.",
        "to-fast-track-menu-via-route-error": "Something went wrong",
        "to-fast-track-menu-via-route-contact": "Contact kitchen staff if the error happens again.",


        // Choose location
        "choose-location-picker-no-location": "Choose location",
        "choose-location-header-company-user": "\n .title = Choose your location",
        "choose-location-header-private-user": "\n .title = Choose sales location",
        "choose-location-shop-number-field": "\n .label = Enter shop number",
        "choose-location-shop-number-submit-button": "Confirm\n .loadingLabel = Loading...",
        "choose-location-about-shop-number-link": "What is a shop number?",
        "choose-location-alternative-method-header": "⸺ or ⸺",
        "choose-location-search-for-location-button": "Show all sales locations",
        "choose-location-search-field": "\n .label = Search on name, address, shop number...",
        "choose-location-help-text": "Contact your administrator if your location is not on the list.",
        "choose-location-dialog": "\n .title = Change Location",
        "choose-location-dialog-private-user": "\n .title = Change Sales Location",
        "choose-location-shop-numbers": "Shops: { $numbers }",
        "choose-location-select-location-button": "Select\n .disabledLabel = Active\n .loadingLabel = Saving choice...",
        "choose-location-active-badge": "Active",
        "choose-location-confirm-button": "Confirm location\n .loadingLabel = Saving choice...",
        "choose-location-no-results": "No locations match your search.",


        // Order takeaway
        "order-takeaway-page-title": "\n .primaryTopBarTitle = Takeaway",

        // Order catering
        "order-catering-page-title": "\n .primaryTopBarTitle = Lunch",

        // --- Products page --- Offer tag ---
        "offer-tag-good-price": "Good price",
        "offer-tag-great-offer": "Great offer",
        "offer-tag-buy-now": "Buy now",
        "offer-tag-discount": "Discount",
        "offer-tag-week-offer": "Week's offer",
        "offer-tag-day-offer": "Today's offer",
        "offer-tag-save-x-price": "Save { $amount } kr",
        "offer-tag-save-x-percentage": "Save { $amount }%",
        "offer-tag-limited-on-sale": "Only { $amount } left",

        // --- Products page --- Allergens ---
        "order-food-allergens-list": "Allergens: { $allergens }",

        // Order takeaway/catering
        // "webshop-canteen-not-found-dialog": "\n .title = Not Found\n .message = Kitchen not found. Please contact support.\n .buttonLabel = Close",
        "order-basket-title": "\n .primaryTopBarTitle = Basket",
        "webshop-shop-type-lunch": "Lunch",
        "webshop-shop-type-take-away": "Takeaway",
        "webshop-shop-type-catering": "Meeting services",
        "order-food-add": "Add",
        "order-food-amount-private": "For me",
        "order-food-amount-company": "For guest",
        "order-food-pickup": "Pickup: {$time} {$place}",
        "order-food-items-left": "{$amount} left",
        "order-food-basket-button": "Checkout ({ $items }) { $price }",
        "order-food-item-out-of-stock": "Out of stock",
        "order-food-item-past-deadline": "Past deadline",
        "order-food-item-quick-add": "Add",
        "order-food-basket-overview": "\n .description = Items",
        "order-food-basket-private": "Private",
        "order-food-basket-company": "Company",
        "order-food-basket-guest": "Guest:",
        "order-food-basket-guest-information": "\n .description = Guest information",
        "order-food-basket-payment": "\n .description = Payment method",
        "order-food-basket-payment-methods": "\n .title = Payment Methods",
        "order-food-basket-delivery": "\n .description = Delivery",
        "order-food-basket-pickup": "For pickup",
        "order-food-basket-paid": "Paid - {$selectedOrder} of {$maxOrders}",
        "order-food-basket-name": "\n .label = Your name\n .emptyErrorText = This field is required",
        "order-food-basket-email": "\n .label = Your email\n .emptyErrorText = This field is required\n .invalidErrorText = Invalid email",
        "order-food-basket-phone": "\n .label = Phone number (optional)",
        "order-food-basket-comment": "\n .label = Write a comment",
        "order-food-basket-purchase-button": "Complete payment\n .loadingLabel = Performing purchase...",
        "order-food-basket-seller":"Seller: {$seller}",
        "order-food-canteen-details-contact": "Contact",
        "order-food-canteen-details-address": "Address",
        "order-food-canteen-details-closed": "Closed",
        "order-food-receipt-scan-to-checkin": "Scan to check-in",
        "order-food-receipt-scan-to-checkin-offline": "The QR-code could not be loaded.",
        "order-food-featured-deliverydate": "Choose delivery date",
        "order-food-upcoming-menus": "Upcoming menus",
        "order-food-empty-state-text": "Kitchen does not offer food today. Try another day.",
        "order-food-sold-out-snackbar": "Item is out of stock",

        "product-unavailable-product-not-found-modal": "\n .title = Product not found\n .message = The requested product is not included in the catalog for the chosen date. Contact the kitchen.\n .buttonLabel = Understood",
        "product-unavailable-product-unavailable-modal": "\n .buttonLabel = Understood",


        // Product modal
        "canteenProductModal-preparation-label": "Preparation",
        "canteenProductModal-declaration-label": "Declaration",
        "canteenProductModal-allergens-label": "Allergens",


        // Proof of Purchase
        "proof-of-purchase-order-id": "Order id: { NUMBER($orderId, useGrouping: 0) }",
        "proof-of-purchase-open-receipt-button": "Show Receipt",
        "proof-of-purchase-subscription-type": "Subscription type: { $limit } days per { $period }",
        "proof-of-purchase-usage-remaining": "Usage: { $remaining } days remaining",
        "usage-period-week": "week",
        "usage-period-month": "month",
        "proof-of-purchase-order-type-lunch": "{ -order-type-lunch }",
        "proof-of-purchase-order-type-takeaway": "{ -order-type-take-away }",
        "proof-of-purchase-order-type-take-away": "{ -order-type-take-away }",
        "proof-of-purchase-order-type-catering": "{ -order-type-catering }",
        "proof-of-purchase-order-type-meeting": "{ -order-type-meeting }",
        "proof-of-purchase-order-type-refill-account": "{ -gopay-wallet } { -gopay-wallet-refill(pos: \"initial\") }",
        "proof-of-purchase-order-type-auto-refill-account": "Automatic { -gopay-wallet-refill } subscription",
        "proof-of-purchase-order-type-lunch-subscription": "{ -order-type-lunch-subscription }",
        "proof-of-purchase-order-type-subscribe-payment-card": "{ -order-type-subscribe-payment-card }",
        "proof-of-purchase-order-type-refund": "{ -order-type-refund }",
        "receipt-qr-dialog-back": "Back",
        "receipt-no-deliveries": "The order does not contain deliveries",
        "receipt-company-orderlines": "Paid by company",

        "receipt-product-paid-by-free": "(for free)",
        "receipt-product-paid-by-credit-card": "(by\u00A0card)",
        "receipt-product-paid-by-one-click-payment": "(by\u00A0card)",
        "receipt-product-paid-by-mobile-pay": "(by\u00A0MobilePay)",
        "receipt-product-paid-by-payroll-deduction": "(by\u00A0payroll)",
        "receipt-product-paid-by-lunch-registration": "(by\u00A0lunch registration)",
        "receipt-product-paid-by-guest-lunch-registration": "(by\u00A0lunch registration)",
        "receipt-product-paid-by-gopay-account": "(by\u00A0{ -gopay-wallet })",
        "receipt-product-paid-by-invoice": "(by\u00A0invoice)",


        // Receipt
        "receipt-title": "Receipt",
        "receipt-forward-receipt-dialog": "\n .title = { $receipts -> \n *[one] Send Receipt \n [other] Send Receipts \n } \n .cancelButtonLabel = Cancel\n .submitButtonLabel = Send\n .submitButtonLoadingLabel = Sending...",
        "receipt-forward-receipt-email-field": "\n .label = Email Address",
        "receipt-forward-receipt-email-field-is-required": "Email address is required",
        "receipt-forward-receipt-email-field-must-be-valid": "Invalid email address",

        // Purchase section
        "receipt-purchase-details-section": "\n .heading = Purchase Details",
        "receipt-purchase-payment-status-paid": "PAID",
        "receipt-purchase-payment-status-unpaid": "UNPAID",
        "receipt-purchase-payment-status-invoiced": "INVOICED",
        "receipt-purchase-payment-status-refunded": "REFUNDED",
        "receipt-purchase-payment-status-cancelled": "REFUNDED",
        "receipt-purchase-payment-status-failed": "FAILED",
        "receipt-purchase-address-item": "\n .label = Location:",
        "receipt-purchase-seller-vat-number": "VAT\u00A0No:\u00A0{ $vatNumber }",
        "receipt-purchase-customer-item": "\n .label = Customer:",
        "receipt-purchase-time-item": "\n .label = Purchase time:",
        "receipt-purchase-payment-method-item": "\n .label = Payment method:",
        "receipt-purchase-order-id-item": "\n .label = Order id:",
        "receipt-purchase-shop-channel-item": "\n .label = Channel:",
        "receipt-purchase-cost-center-item": "\n .label = Cost center:",

        // Actions section
        "receipt-actions-forward-receipt-button": "Send Receipt",
        "receipt-actions-cancel-order-button": "Cancel Order",
        "receipt-actions-delete-order-button": "Delete Order",
        "receipt-actions-edit-order-button": "Edit",
        "receipt-actions-update-order-button": "Edit Order",
        "receipt-actions-confirm-cancel-dialog": "\n .description = You are about to cancel your order. Are you sure?\n .confirmLabel = Yes\n .cancelLabel = No\n .loadingDescription = Cancelling order...",
        "receipt-actions-cancel-unavailable-dialog": "\n .buttonLabel = Understood",

        // Delivery section
        "receipt-delivery-details-section": "\n .heading = Delivery",
        "receipt-delivery-order-type-item": "\n .label = Order type:",
        "receipt-order-type-lunch": "{ -order-type-lunch }",
        "receipt-order-type-takeaway": "{ -order-type-take-away }",
        "receipt-order-type-take-away": "{ -order-type-take-away }",
        "receipt-order-type-catering": "{ -order-type-catering }",
        "receipt-order-type-meeting": "{ -order-type-meeting }",
        "receipt-order-type-meeting-service": "{ -order-type-meeting-service }",
        "receipt-order-type-refill-account": "{ -gopay-wallet } { -gopay-wallet-refill(pos: \"initial\") }",
        "receipt-order-type-auto-refill-account": "Automatic { -gopay-wallet-refill } subscription",
        "receipt-order-type-lunch-subscription": "{ -order-type-lunch-subscription }",
        "receipt-order-type-subscribe-payment-card": "{ -order-type-subscribe-payment-card }",
        "receipt-order-type-refund": "{ -order-type-refund }",
        "receipt-delivery-type-item": "\n .label = Delivery type:",
        "receipt-seats-item": "\n .label = Seats",
        "receipt-delivery-type-pick-up": "Pick up",
        "receipt-delivery-time-item": "\n .label = Delivery time:",
        "receipt-delivery-comment-item": "\n .label = Comment:",

        // Order details section
        "receipt-payment-method-free": "Free",
        "receipt-payment-method-credit-card": "Credit card",
        "receipt-payment-method-one-click-payment": "Credit card",
        "receipt-payment-method-mobile-pay": "MobilePay",
        "receipt-payment-method-salary-reduction": "Payroll deduction",
        "receipt-payment-method-payroll-deduction": "Payroll deduction",
        "receipt-payment-method-lunch-registration": "Lunch registration",
        "receipt-payment-method-guest-lunch-registration": "Lunch registration",
        "receipt-payment-method-gopay-account": "{ -gopay-wallet }",
        "receipt-payment-method-invoice": "Invoice",
        "receipt-payment-method-subscribe-payment-card": "Subscribe payment card",

        "receipt-order-details-buyerparty-private": "Private purchase",
        "receipt-order-details-buyerparty-company": "Company purchase",
        "receipt-order-details-vat-total": "\n .label = Contained VAT:",

        // Accounting details section
        "receipt-accounting-details-section": "\n .heading = Accounting",


        // Order details
        "orderDetails-deliveryType-delivery": "\n .title = Delivery",
        "orderDetails-deliveryType-pick-up": "\n .title = Pick up",
        "orderDetails-deliveryType-eating-in-canteen": "\n .title = Eat in canteen",
        "orderDetails-attendees-subject-label" : "\n    .title = Participants ({$count})",
        "orderDetails-note-label" : "\n    .title = Note",
        "orderDetails-catering-totalAmountIncludeVat-label" : "Amount incl. vat",
        "orderDetails-header-label" : "\n    .title = My meeting",
        "orderDetails-meeting-privacyStatus-label" : "\n    .label = Private",
        "orderDetails-resources-subject-label" : "Resources",


        // Confirm delete meeting dialog
        "deleteMeeting-labels" : "\n    .title = Are you sure you want to delete meeting?\n    .buttonLabel = Confirm",


        // Confirmation-dialog
        "confirmation-dialog-cancel": "Cancel",


        // Notifications
        "notification-snackbar-action-view": "View",
        "notification-snackbar-default-message": "New message",
        "notifications-page-title": "\n .primaryTopBarTitle = Messages",
        "notifications-no-notifications" : "\n .title = No messages right now\n .description = Here you will be able to read messages from the merchant and service center.",
        "notifications-no-notifications-notification-time-type-news": "News",
        "notifications-no-notifications-notification-time-type-info": "Information",
        "notifications-no-notifications-notification-time-type-important": "Important",
        "notifications-no-notifications-notification-time-type-offer": "Offer",
        "notifications-no-notifications-notification-time-type-event": "Event",
        "notifications-no-notifications-notification-time-type-reminder": "Reminder",
        "notifications-no-notifications-notification-time-type-menu-change": "Menu",
        "notifications-no-notifications-notification-time-type-feedback": "Feedback",
        "notifications-no-notifications-notification-time-type-health": "Health",
        "notifications-sender-type-merchant": "Merchant",
        "notifications-sender-type-service-center": "Service",


        // Room Finder
        "roomfinder-tabs-searcher": "Search",
        "roomfinder-tabs-overview": "Overview",
        "roomfinder-overview-find-room": "\n    .label = Find room",
        // "roomFinder-availableResourceGroup-1": "Available Rooms",
        // "roomFinder-availableResourceGroup-2": "Available AV Equipments",
        // "roomFinder-availableResourceGroup-4": "Available Cars",


        // Basket
        "basket-removeAll-label" : "Remove all",
        "basket-meeting-catering-title" : "Mødeforplejning",

        // Week menu
        "home-week-menu-title": "\n .title = Week menu",
        "weekMenu-badge": "Week menu",
        "weekMenu-header-label": "\n .title = Menu of the week",
        "weekMenu-no-menu-label": "Unfortunately, no menus have been created this week.",
        "weekMenu-no-menu-subtitle": "Here you will be able to see the menu for the upcoming days if available. You cannot purchase from here.",

        // Home Booking Meeting
        "home-book-meeting-title": "\n .title = Meeting planning",


        // Meeting Planning Dialog
        "order-meeting-service": "Order meeting service",
        "book-resources": "Book resources",


        // Booking Meeting Screen
        "meeting-service-screen-add-title": "Add meeting service",
        "meeting-service-screen-edit-title": "Edit meeting service",
        "meeting-planning-screen-title": "Add meeting service delivery",
        "delivery-form-delivery-type-field-title": "Delivery type",
        "cancel-delivery": "\n .description = Do you want to cancel current order?\n .confirmLabel = Yes\n .cancelLabel = No",

        // Add Initial Delivery Form
        "delivery-form-meeting-field-label": "\n .label = Meeting name\n .title = Meeting name\n .placeholder = Enter meeting name",
        "delivery-form-date-from-field-label": "\n .label = From",
        "delivery-form-date-to-field-label": "\n .label = To",
        "delivery-form-meeting-field-date-label": "\n .label = Delivery date\n .placeholder = Select delivery date",
        "delivery-form-delivery-type-field-label": "\n .label = Delivery type\n .placeholder = Select delivery type",
        "delivery-form-delivery-location-field-label": "\n .label = Delivery location\n .placeholder = Select delivery location",
        "delivery-form-delivery-other-location-field-label": "\n .label = Other location",
        "delivery-form-delivery-other-location-field-button": "+ Other location",
        "delivery-form-delivery-time-field-label": "\n .label = Delivery time\n .placeholder = Select delivery time",
        "delivery-form-delivery-time-field-select-label": "Select time",
        "delivery-form-delivery-seats-field-label": "\n .label = Seats",
        "delivery-form-delivery-seats-field-placeholder": "Enter or select seat",
        "delivery-form-delivery-other-message-field-label": "\n .label = Order message\n .title = Order message\n .placeholder = Write a message for your order",
        "delivery-payment-form-delivery-bill-receiver-field-label": "Bill receiver",
        "delivery-payment-form-delivery-bill-receiver-field-placeholder": "Select Bill receiver",
        "delivery-payment-form-delivery-cost-center-field-label": "Cost center",

        "delivery-form-meeting-field-name-required": "Meeting name is required",
        "delivery-form-meeting-field-name-max-length": "Maximum length is { $maxLength} characters",
        "delivery-form-meeting-field-other-location-max-length": "Maximum length for Other Location is { $maxLength} characters",
        "delivery-form-meeting-field-other-message-max-length": "Maximum length for Other Message is { $maxLength} characters",
        "delivery-form-meeting-field-from-date-required": "From date is required",
        "delivery-form-meeting-field-date-required": "Delivery date is required",
        "delivery-form-meeting-field-from-date-should-be-before-to-date": "From date must be earlier than To date",
        "delivery-form-meeting-field-to-date-required": "To date is required",
        "delivery-form-meeting-field-delivery-type-required": "Delivery type is required",
        "delivery-form-meeting-field-delivery-time-required": "Delivery time is required",
        "delivery-form-meeting-field-delivery-time-available": "Delivery time on the selected day is available from { $from } to { $to }",
        "delivery-form-meeting-field-delivery-time-need-date": "Please select a delivery date first.",
        "delivery-form-delivery-location-field-required": "Delivery location is required",
        "delivery-form-delivery-other-location-field-required": "Other location is required",
        "delivery-form-meeting-field-seats-min-length": "Minimum number of seats is { $minLength}",
        "delivery-form-meeting-field-seats-max-length": "Maximum number of seats is { $maxLength}",
        "delivery-payment-form-delivery-dynamic-field-required": "{ $field } is required",

        "delivery-form-meeting-submit-button": "Continue ({ $finalPrice})\n .loadingLabel = Loading...",

        "delivery-form-delivery-extra-services-title": "Extra Services",
        "delivery-form-delivery-extra-services-add": "Add",
        "delivery-form-delivery-extra-services-remove": "Remove",
        "delivery-form-delivery-extra-services-per-person": "{ $pricePerSeat} per person",
        "delivery-form-delivery-extra-services-min-max": "Min { $minimumPrice} | Max { $maximumPrice}",

        // Delivery Type Options
        "delivery-type-option-canteen": "Eat in canteen",
        "delivery-type-option-pick-up": "Pick up",
        "delivery-type-option-delivery": "Delivery",

        // Other Location
        "other-location": "Other Location",

        // Meeting options
        "meetingOptions-extendMeeting-label": "Extend meeting",
        "meetingOptions-endMeeting-label": "End meeting",
        "meetingOptions-viewDetails-label": "View meeting details",
        "meetingOptions-delete-label": "Delete meeting",
        "meetingOptions-edit-label": "Edit meeting",
        "meetingOptions-assistance-label": "Assistance",
        "-meetingOptions-startMeeting-base-label": "Start meeting",
        "meetingOptions-startMeeting-expired-label": "{-meetingOptions-startMeeting-base-label} (expired)",
        "meetingOptions-startMeeting-left-label": "{-meetingOptions-startMeeting-base-label} ({$time}} left)",
        "meetingOptions-startMeeting-in-label": "{-meetingOptions-startMeeting-base-label} (in {$time}})",

        "meeting-planned-complete": "Order completed",
        "meeting-planned-added": "Meeting service added",
        "meeting-planned-updated": "Meeting service updated",
        "meetingBooked-next-choice-go-to-resource-booking": "Add resource booking to order",

        // Confirm Meeting Planning Modal
        "confirm-meeting-planning-modal-title": "Deadline has been exceeded",
        "confirm-meeting-planning-modal-description": "You can still place the order, but the order needs to be approved by the staff, which means you can not expect the order to be delivered.",
        "confirm-meeting-planning-modal-button-denied": "Don’t complete order",
        "confirm-meeting-planning-modal-button-accept": "Place order",

        // News
        "allNews-header-label": "\n    .title = News",
        "allNews-noNews-label": "No news",
        "newsDetails-header-label": "\n    .title = My News",
        "newsDetails-by-label": "By {$creator} | {$company}",


        // Misc
        "action-sheet-cancel-button": "Cancel",
        "alert-dialog-close-button": "Close",
        "options-novalue" : "-- Select --",
        "date-time-picker": "\n .okLabel = OK\n .cancelLabel = Cancel",


        "completeBooking-advancedOptions-button-label" : "Next",
        "completeBooking-advancedOptions-button-complete" : "Complete",
        "completeBooking-advancedOptions-booking-time" : "Booking time:",
        "completeBooking-advancedOptions-header-label" : "More options",
        "completeBooking-advancedOptions-privateMeeting-label" : "Private meeting",
        "completeBooking-advancedOptions-sittingsArrangement-label" : "Sitting arrangement",
        "completeBooking-header-label" : "\n    .title = Complete booking",
        "completeBooking-meeting-endTime-label" : "To",
        "completeBooking-meeting-header-label" : "Details",
        "completeBooking-meeting-meetingName-label" : "Meeting name",
        "completeBooking-meeting-startTime-label" : "From",
        "completeBooking-meeting-card-meeting-details-title": "Meeting details",
        "completeBooking-meeting-card-resources-title": "Resources",

        "assistance-header-label": "\n    .title = Request Assistance",
        "assistance-location-label": "Location:",
        "assistance-noLocations-label": "No locations",
        "assistance-button-next-label" : "Next",
        "assistance-chooseMessage-label" : "Choose message:",
        "assistance-yourMessage-label" : "Your message:",
        "assistance-writeMessage-label" : "Write your own message",

        "confirmBooking-header-label" : "\n    .title = Confirm Booking",
        "confirmBooking-button-label" : "Complete order",
        "confirmBooking-empty-basket-label" : "Basket is empty",
        "endMeeting-button-label" : "Confirm",
        "endMeeting-header-label" : "End meeting",
        "endMeeting-success-label" : "The meeting is ended",
        "endMeeting-text-label" : "Are you sure you want to end the meeting?",
        "endMeeting-labels" : "\n    .title = {endMeeting-text-label}\n    .title = {endMeeting-button-label}",
        "startMeeting-button-label" : "Confirm",
        "startMeeting-text-label" : "Are you sure that you want to start this meeting?",
        "startMeeting-labels" : "\n    .title = {startMeeting-text-label}\n    .title = {startMeeting-button-label}",
        "extendMeeting-button-label" : "Confirm",
        "extendMeeting-changeEndTimeTo-label" : "Update meeting end time to:",
        "extendMeeting-header-label" : "\n    .title = Extend meeting",
        "extendMeeting-success-label" : "Meeting is extended",
        "meeting-button-end-label" : "End",
        "meeting-button-extend-label" : "Extend",
        "meetingUpdated-button-label" : "Close",
        "meetingUpdated-header-label" : "Completed",
        "myMeetings-button-delete-label" : "Delete",
        "myMeetings-button-update-label" : "Update",
        "myMeetings-header-label" : "Filter",
        "myMeetings-search-label" : "Search",
        "roomFinder-button-addResource-label" : "Add",
        "roomFinder-button-removeResource-label" : "Remove",
        "roomFinder-button-next-label" : "Next",
        "roomFinder-resourceInfo-av-subject-label" : "Av-equipment",
        "roomFinder-resourceInfo-availableTimeSlots-subject-label" : "Available times",
        "roomFinder-resourceInfo-moreTimeSlots-subject-label" : "More times...",
        "roomFinder-resourceInfo-noAvailableTimeSlots-subject-label" : "No available times",
        "roomFinder-resourceInfo-sittingsArrangements-subject-label" : "Table arrangements",

        "roomFinder-specifyTime-header-label" : "\n    .title = Specify time",

        "roomFinder-search-filter-label" : "Filter",
        "roomFinder-search-nofilter-label" : "No filters",
        "roomFinder-search-unsupported-element" : "Unsupported element type: {$type}",
        "roomFinder-search-hour-label" : "hour",
        "roomFinder-search-hours-label" : "hours",
        "roomFinder-continue-to-checkout-label" : "Continue to check out ({$amount})",
        "roomFinder-subject-label" : "Available {resourceType}",
        "roomFinderFilter-fixedAv-label" : "Av-equipment",
        "roomFinderFilter-floor-label" : "Floor",
        "roomFinderFilter-group-button-label" : "Show",
        "roomFinderFilter-group-fixedAv-label" : "Av-equipment",
        "roomFinderFilter-group-floor-label" : "Floor",
        "roomFinderFilter-group-location-label" : "Locations",
        "roomFinderFilter-group-resourceTypes-label" : "Ressource type",
        "roomFinderFilter-group-tag-label" : "Tags",
        "roomFinderFilter-header-label" : "Filter",
        "roomFinderFilter-location-label" : "Locations",
        "roomFinderFilter-meetingDuration-hour-label" : "Hour",
        "roomFinderFilter-meetingDuration-hours-label" : "Hours",
        "roomFinderFilter-meetingDuration-subject-label" : "Duration",
        "roomFinderFilter-removeAll-label" : "Clear all",
        "roomFinderFilter-remove-label" : "Clear",
        "roomFinderFilter-resourceTypes-av-label" : "Av-equipment",
        "roomFinderFilter-resourceTypes-car-label" : "Car",
        "roomFinderFilter-resourceTypes-label" : "Resource type",
        "roomFinderFilter-resourceTypes-room-label" : "Room",
        "roomFinderFilter-resourceTypes-subject-label" : "Ressource types",
        "roomFinderFilter-room-label" : "Room",
        "roomFinderFilter-room-subject-label" : "Room",
        "roomFinderFilter-sittings-numberOfSittings-label" : "Number of sittings",
        "roomFinderFilter-sittings-subject-label" : "Sittings",
        "roomFinderFilter-tag-label" : "Tags",
        "roomFinderFilter-dateTimePicker-change-date-button": "\n .buttonLabel = Change date",
        "upcomingMeetings-button-end-label" : "End",
        "upcomingMeetings-button-extend-label" : "Extend",
        "upcomingMeetings-meetingStatus-label" : "Now",
        "upcomingMeetings-moreInfo-label" : "More info",


        // Meetings
        "meetingBooked-complete": "Booking completed",
        "meetingBooked-when": "When:",
        "meetingBooked-next-choice": "What do you want to do next?",
        "meetingBooked-next-choice-go-to-meeting-planning": "Add meeting service",
        "meetingBooked-next-choice-finish": "I’m finished",
        "meetingStarted-labels": "\n    .topbarTitle = Meeting Started\n    .title = The meeting has started\n    .buttonLabel = Continue",
        "meetingEnded-labels": "\n    .topbarTitle = Meeting Ended\n    .title = The meeting has ended\n    .buttonLabel = Continue",
        "meetingDeleted-labels": "\n    .topbarTitle = Meeting Deleted\n    .title = The meeting has been deleted\n    .buttonLabel = Continue",
        "meetingBooked-labels": "\n    .topbarTitle = Meeting Booked\n    .buttonLabel = Next",
        "meetingUpdated-labels": "\n    .topbarTitle = Meeting Updated\n    .buttonLabel = Next",
        "meeting-open-in-calendar": "Add meeting to calendar",

        "hours": "hours",


        // Buttons
        "edit-button": "Edit",
        "delete-button": "Delete",

        // Tooltip hints
        "unavailable-date-tooltip": "This date cannot be selected",

        // Errors
        "error-required": "This field is required",
        "error-max-length": "Max length is {$maxLength}",

        // --- API components ---
        "api-query-table-fetch-error-message": "Error loading data",
        "api-query-autocomplete": "Loading data...",
        "api-query-autocomplete-fetch-error-message": "Error loading data. <retryLink>Retry</retryLink>",
    },
    "nb-NO": {
        // Branding
        "-gopay-app-name": "GoPay",
        "-gopay-wallet": "GoPay Wallet",
        "-gopay-wallet-refill": "{ $pos -> \n [initial] { $count -> \n *[one] Påfyll \n [other] Påfyll \n } \n *[other] { $count -> \n *[one] påfyll \n [other] påfyll \n } \n }",
        "date-time-hour-cycle": "h23",


        // Date and time formats as Luxon format strings
        // See: https://moment.github.io/luxon/docs/manual/formatting.html#table-of-tokens
        "date-time-format-date": "dd.MM.yyyy",
        "date-time-format-day-and-date": "EEEE dd.MM",
        "date-time-format-time": "HH:mm",
        "date-time-format-date-and-time": "dd.MM.yyyy\u00A0HH:mm",
        "date-time-format-duration-hours-minutes": "h't.' m'min.'",
        "date-time-format-duration-minutes": "m'min.'",
        "date-time-format-duration-seconds": "s's'",
        "date-time-format-month-and-year": "MMMM yyyy",
        "date-time-short-relative-units": "s|m|t|d|u|mnd|år",


        // success / error handling
        "success-modal-status": "\n .buttonLabel = Lukk",
        "error-route-status": "\n .topbarTitle = Feil",
        "error-dialog-close-button": "\n .buttonLabel = Lukk",
        "action-failed-modal": "\n .buttonLabel = Lukk",
        "errorHandler-labels": "\n    .title = Noe gikk galt\n    .buttonLabel = Gå til forsiden",
        "errorHandlerModal-labels": "\n    .title = Noe gikk galt\n    .buttonLabel = Lukk",
        "error-clientNetworkError-label": "Ingen internettforbindelse",
        "unknown-error-title": "Ukjent feil",
        "unknown-error-message": "Ingen feilopplysninger tilgjengelig",
        "general-error-title": "Noe gikk galt",
        "internal-error-title": "Intern feil",
        "api-call-error-title-network-error": "Nettverksfeil",
        "api-call-error-title-bad-request": "Forespørsel avvist",
        "api-call-error-title-unauthorized": "Ugyldig innlogging",
        "api-call-error-title-forbidden": "Ingen tilgang",
        "api-call-error-title-not-found": "Ikke funnet",
        "api-call-error-title-conflict": "Konflikt",
        "api-call-error-title-too-many-requests": "For mange forespørsler",
        "api-call-error-title-internal-server-error": "Intern serverfeil",
        "api-call-error-title-service-unavailable": "Tjenesten er utilgjengelig",
        "api-call-error-title-request-failed": "Forespørselen mislyktes",
        "api-call-error-no-internet-connection": "Ingen internettforbindelse",
        "api-call-error-could-not-reach-server": "Kunne ikke nå serveren",
        "api-call-error-protocol-error": "Serverfeil: { $responseCode } { $responseStatus }",
        "api-call-error-api-returned-error": "Uventet serverfeil",
        "api-call-error-response-not-json": "Uventet svar fra serveren",
        "api-call-error-retry-button": "Prøv igjen",

        // accept terms and conditions
        "accept-terms-and-conditions": "Jeg godtar <conditionsLink>vilkårene</conditionsLink>",


        // authenticate common
        "-authenticate-submit-button": "Neste",


        // order types shared translations
        "-order-type-lunch": "Dagskjøp",
        "-order-type-take-away": "Takeaway",
        "-order-type-catering": "Møteservering",
        "-order-type-meeting": "{ $count -> \n *[one] Booking \n [other] Bookinger \n }",
        "-order-type-meeting-service": "{ $count -> \n *[one] Møteservering \n [other] Møteserveringer \n }",
        "-order-type-refill-account": "{ $count -> \n *[one] Påfyll \n [other] Påfyll \n }",
        "-order-type-auto-refill-account": "Automatisk påfyll",
        "-order-type-lunch-subscription": "{ $count -> \n *[one] Abonnement \n [other] Abonnementer \n }",
        "-order-type-subscribe-payment-card": "Registrert betalingskort",
        "-order-type-refund": "{ $count -> \n *[one] Refusjon \n [other] Refusjoner \n }",


        // user prompts
        "user-prompt-button-label-saving": "\n .loadingLabel = Lagrer...",

        // onboarding flow (right after login)
        "onboarding-skip-step": "Hopp over",
        "onboarding-step-of": "Trinn { $step } av { $total }",
        "onboarding-location-title": "Velg lokasjon",
        "onboarding-location-title-private-user": "Velg salgssted",
        "onboarding-location-description": "Lokasjonen din er stedet du vanligvis jobber fra. Du kan alltid bytte lokasjon øverst i appen.",
        "onboarding-push-title": "Kan vi sende deg pushvarsler?",
        "onboarding-push-description": "Da får du beskjed når ordren din er bekreftet og klar til henting, hvis noe endrer seg i ordren eller bookingen din, og når et møte nærmer seg. Meldinger fra kantinen kommer bare fra kantinen du har valgt.",
        "onboarding-push-footnote": "Endres under Meg › Innstillinger › Pushvarsler",
        "onboarding-push-continue-button": "Fortsett\n .loadingLabel = Lagrer...",
        "onboarding-done-title": "{ $location } er valgt",
        "onboarding-done-title-no-location": "Du er klar",
        "onboarding-done-push-both": "Du får varsler om drift og ordrer samt nyheter og tilbud. Endres under Meg › Innstillinger › Pushvarsler.",
        "onboarding-done-push-tx": "Du får varsler om drift og ordrer. Endres under Meg › Innstillinger › Pushvarsler.",
        "onboarding-done-push-news": "Du får varsler om nyheter og tilbud. Endres under Meg › Innstillinger › Pushvarsler.",
        "onboarding-done-push-none": "Du får ingen pushvarsler. Du kan slå dem på under Meg › Innstillinger › Pushvarsler.",
        "onboarding-done-button": "Til forsiden",

        // push notification types & settings
        "push-type-tx-title": "Drift og ordrer",
        "push-type-tx-description": "Viktige meldinger, ordrer og møter.",
        "push-type-news-title": "Nyheter og tilbud",
        "push-type-news-description": "Dagens meny, nyheter og tilbud.",
        "push-settings-title": "\n .primaryTopBarTitle = Pushvarsler",
        "push-settings-status-unavailable": "Pushvarsler er bare tilgjengelige i appen. Valgene dine nedenfor lagres og brukes i appen.",
        "push-settings-status-allowed": "Pushvarsler er tillatt på denne telefonen",
        "push-settings-status-denied-title": "Slått av på denne telefonen",
        "push-settings-status-denied-text": "Uansett valgene nedenfor sender vi ingen varsler før du tillater dem på denne telefonen.",
        "push-settings-allow-button": "Tillat pushvarsler\n .loadingLabel = Lagrer...",
        "push-settings-open-settings-link": "Åpne innstillinger",
        "push-settings-turn-off-link": "Slå helt av i telefonens innstillinger",


        // welcome
        "authenticate-welcome-slide-header-buy-lunch": "Kjøp lunsj og firmatjenester",
        "authenticate-welcome-slide-text-buy-lunch": "Kjøp enkelt maten din hos lunsjleverandøren og andre firmatjenester, f.eks. servering ved arrangementer og rens av klær.",
        "authenticate-welcome-slide-header-reduce-food-waste": "Bekjemp matsvinn",
        "authenticate-welcome-slide-text-reduce-food-waste": "Forhåndsbestill og avbestill lunsjen din, så kjøkkenet kan planlegge matproduksjonen. Velg fremhevede menyer som er bedre for miljøet.",
        "authenticate-welcome-slide-header-receive-offers": "Ukens menyer og tilbud",
        "authenticate-welcome-slide-text-receive-offers": "Du kan se ukens menyer med næringsinnhold, motta tilbud og lese nyheter.",
        "authenticate-welcome-slide-header-purchase-overview": "Se kjøpene dine",
        "authenticate-welcome-slide-text-purchase-overview": "Du har god oversikt over kjøpene og kvitteringene dine. Betaler du via lønnstrekk, kan du enkelt avstemme.",
        "authenticate-welcome-continue-button": "Logg inn / Opprett konto",


        // login choices
        "authenticate-login-choices-header": "Bestill mat og andre tjenester",
        "authenticate-login-choices-login-button": "Logg inn",
        "authenticate-login-choices-create-account-button": "Opprett konto",
        "authenticate-login-choices-guest-link": "Kjøp som gjest",


        // login / signup email
        "authenticate-email-header": "Skriv inn e-postadressen din",
        "authenticate-email-signup-header": "Opprett konto",
        "authenticate-email-email-input": "\n .label = E-postadresse",
        "authenticate-email-submit-button": "{ -authenticate-submit-button }\n .loadingLabel = Sjekker e-postadressen...",
        "authenticate-email-email-is-required": "Skriv inn e-postadressen din",
        "authenticate-email-email-must-be-an-email": "Ugyldig e-postadresse",
        "authenticate-email-expect-company-flow": "Du prøver å opprette en konto med e-post­domenet <emailDomain>{ $emailDomain }</emailDomain>, som er knyttet til et firma. Hvis du vil opprette en privat konto, må du bruke din private e-post­adresse.",
        "authenticate-email-expect-private-flow": "E-post­domenet ditt <emailDomain>{ $emailDomain }</emailDomain> er for øyeblikket ikke knyttet til noe firma i systemet. Du må bruke jobb­e-posten din og ikke din private e-post­adresse. Kontakt administratoren din hvis du mener at domenet ditt burde være knyttet til et firma.",
        "authenticate-email-switch-to-login-flow": "Den angitte e-post­adressen finnes allerede i systemet. Vil du logge inn i stedet?",
        "authenticate-email-switch-to-signup-flow": "Den angitte e-post­adressen finnes ikke i systemet. Vil du opprette en konto i stedet?",
        "authenticate-email-switch-flow-no-button": "Nei takk",
        "authenticate-email-switch-flow-yes-button": "Ja takk",
        "authenticate-email-okay-button": "Forstått",


        // activate by phone
        "authenticate-phone-number-header": "Aktiver konto",
        "authenticate-phone-number-text": "Vi trenger mobilnummeret ditt, slik at vi kan sende deg en aktiveringskode:",
        "authenticate-phone-number-phone-number-input": "\n .label = Telefonnummer",
        "authenticate-phone-number-submit-button": "{ -authenticate-submit-button }\n .loadingLabel = Sender kode...",
        "authenticate-phone-number-username-missing": "Brukernavn / e-post mangler. Gå tilbake og fyll ut.",
        "authenticate-phone-number-phone-number-is-required": "Telefonnummer er påkrevd",
        "authenticate-phone-number-phone-number-is-invalid": "Ugyldig telefonnummer",


        // activation code
        "authenticate-activation-code-header": "Aktiver konto",
        "authenticate-activation-code-automatic-activation-text": "Vent mens vi aktiverer kontoen din...",
        "authenticate-activation-code-manual-activation-text": "Skriv inn koden du har mottatt på e-post eller SMS:",
        "authenticate-activation-code-activation-code-input": "\n .label = Aktiveringskode",
        "authenticate-activation-code-submit-button": "{ -authenticate-submit-button }\n .loadingLabel = Aktiverer...",
        "authenticate-activation-code-code-field-required": "Skriv inn en aktiveringskode",
        "authenticate-activation-code-code-field-min-len": "Aktiveringskoden er på minst { $codeLength } tegn",


        // login/password
        "authenticate-password-header": "Skriv inn passordet ditt",
        "authenticate-password-header-with-name": "Velkommen { $displayName }! Logg inn.",
        "authenticate-password-password-input": "\n .label = Passord",
        "authenticate-password-submit-button": "{ -authenticate-submit-button }\n .loadingLabel = Logger inn...",
        "authenticate-password-username-missing": "Brukernavn / e-post mangler. Gå tilbake og fyll ut.",
        "authenticate-password-forgot-password-link": "Glemt passord?",
        "authenticate-password-password-is-required": "Skriv inn passordet ditt",


        // login/single sign on
        "authenticate-sso-header": "Single Sign-on",
        "authenticate-sso-explanation": "Organisasjonen din { $organization } bruker Single Sign-on. Du blir nå sendt til den felles innloggingssiden deres.",
        "authenticate-sso-canceled-message": "Du fullførte ikke innloggingen.",
        "authenticate-sso-retry-button": "Prøv igjen\n .loadingLabel = Videresender...",


        // login/account-type
        "authenticate-account-type-choose-account-type": "Velg kontotype",
        "authenticate-account-type-private-account": "Privat konto",
        "authenticate-account-type-company-account": "Firmakonto",
        "authenticate-account-type-private-account-description": "Med en privat konto kan du registrere deg med din private e-postadresse og få tilgang til offentlige butikker. Du har ikke tilgang til firmabutikker med denne kontotypen.",
        "authenticate-account-type-company-account-description": "En firmakonto gir deg tilgang til butikkene som er knyttet til firmaet ditt. Du må bruke jobb-e-posten din for å opprette en konto.",
        "authenticate-account-type-help-text": "Skal jeg bruke en privat konto eller en firmakonto?",
        "authenticate-account-type-help-drawer": "### Firmakonto\n\n Firmaer og andre organisasjoner kan tilby ulike tjenester med betaling via GoPay, f.eks. kjøp av kantinemat. For å få tilgang til disse butikkene må du ha en firmakonto. Vi finner firmaet ditt ut fra e-post­domenet ditt.\n\n ### Privat konto\n\n I tillegg til firmabutikker finnes det også offentlige butikker i GoPay som privat­personer kan handle i. Med en privat konto har du tilgang til alle offentlige butikker.\n   .title = Forklaring av kontotyper",


        // select company
        "authenticate-select-company-header-reason-multiple-companies-for-domain": "Velg firma",
        "authenticate-select-company-header-reason-multiple-accounts-for-username": "Velg brukerkonto",
        "authenticate-select-company-reason-message-multiple-companies-for-domain": "Vi kunne ikke bestemme firmaet ditt automatisk fordi e-postdomenet ditt <duplicateValue>{ $duplicateValue }</duplicateValue> forekommer flere steder i systemet vårt.",
        "authenticate-select-company-reason-message-multiple-accounts-for-username": "Vi fant flere kontoer i systemet vårt med e-postadressen <duplicateValue>{ $duplicateValue }</duplicateValue>.",
        "authenticate-select-company-action-message-multiple-companies-for-domain": "Velg lokasjonen (firmaet) du vil opprette kontoen din under:",
        "authenticate-select-company-action-message-multiple-accounts-for-username": "Velg brukerkontoen du vil logge inn med.",
        "authenticate-select-company-show-list-button": "Vis alternativer",
        "authenticate-select-company-submit-button": "{ -authenticate-submit-button }\n .loadingLabel = Lagrer...",


        // sign up
        "authenticate-signup-header": "Opprett konto",
        "authenticate-signup-text": "Vi trenger å vite litt mer om deg for å opprette kontoen din:",
        "authenticate-signup-name-input": "\n .label = Navn",
        "authenticate-signup-mobile-input": "\n .label = Mobil (valgfritt)",
        "authenticate-signup-accept-terms-link": "Jeg godtar bruksvilkårene",
        "authenticate-signup-submit-button": "{ -authenticate-submit-button }",
        "authenticate-signup-username-missing": "Brukernavn / e-post mangler. Gå tilbake og fyll ut.",
        "authenticate-signup-name-is-required": "Navn er påkrevd",
        "authenticate-signup-name-must-be-full-name": "Vi trenger det fulle navnet ditt (fornavn og etternavn)",
        "authenticate-signup-phone-number-is-invalid": "Ugyldig telefonnummer",
        "authenticate-signup-terms-must-be-accepted": "Du må godta vilkårene for å bruke appen",


        // confirm account details
        "authenticate-confirm-account-details-header": "Bekreft opplysninger",
        "authenticate-confirm-account-details-email-field": "\n .primary = E-post",
        "authenticate-confirm-account-details-name-field": "\n .primary = Navn",
        "authenticate-confirm-account-details-mobile-field": "\n .primary = Mobil",
        "authenticate-confirm-account-submit-button": "{ -authenticate-submit-button }\n .loadingLabel = Oppretter kontoen din...",
        "authenticate-confirm-account-details-account-missing": "Kontodetaljer mangler. Gå tilbake og fyll ut.",


        // create password
        "authenticate-create-password-header": "Opprett passord",
        "authenticate-create-password-text": "Du må opprette et passord for å kunne ta i bruk kontoen din:",
        "authenticate-create-password-password-input": "\n .label = Passord",
        "authenticate-create-password-repeat-password-input": "\n .label = Gjenta passord",
        "authenticate-create-password-submit-button": "{ -authenticate-submit-button }\n .loadingLabel = Oppretter passordet ditt...",
        "authenticate-create-password-one-time-token-missing": "Finner ikke engangstoken. Gå ett steg tilbake og prøv igjen.",
        "authenticate-create-password-password-is-required": "Du må skrive inn et passord",
        "authenticate-create-password-password-too-short": "Passordet ditt må være på minst { $minPasswordLength } tegn",
        "authenticate-create-password-repeat-is-required": "Du må gjenta passordet ditt",
        "authenticate-create-password-password-mismatch": "Passordene stemmer ikke overens",


        // reset password
        "authenticate-reset-password-header": "Tilbakestill passord",
        "authenticate-reset-password-button": "Tilbakestill\n .loadingLabel = Tilbakestiller...",
        "authenticate-reset-password-username-missing": "Brukernavn / e-post mangler. Gå tilbake og fyll ut.",


        // login route components
        "login-external-guest-dialog": "\n .title = Gjestekjøp\n .message = Du er i ferd med å foreta et gjestekjøp. Butikken åpnes i et nytt vindu.\n .buttonLabel = Fortsett",


        // --- User cards ---
        "user-cards-loading-element": "\n .title = Laster inn kort...",


        // home
        "-show-all": "Vis alle",
        "-home-upcoming-meetings": "Kommende møter",
        "-home-offers": "Gode tilbud",
        "-home-shops": "Butikker",
        "-home-my-favorites": "Mine favoritter",

        "home-label": "Hjem",

        "home-news": "\n    .title = Nyheter\n    .rightOptionText = { -show-all }",
        "home-news-kitchen": "\n    .title = Nytt fra kantinen\n    .rightOptionText = { -show-all }",
        "home-news-company-and-service-center": "\n    .title = Nytt fra {$company}\n    .rightOptionText = { -show-all }",
        "home-news-facilitynet": "\n    .title = Nytt fra { -gopay-app-name }\n    .rightOptionText = { -show-all }",

        "home-my-meetings": "\n    .title = Kommende møter\n    .rightOptionText = { -show-all }",
        "home-upcoming-meetings": "Kommende møter",
        "home-contact-the-canteen": "Kontakt kantinen",
        "home-no-planned-meetings": "Ingen planlagte møter",
        "home-plan-meeting": "Planlegg et møte",

        "home-you-are-here": "Du er her:",
        "home-change-location": "Bytt lokasjon",

        "home-takeaway-offers": "\n     .title = { -home-offers } \n     .rightOptionText = { -show-all }",
        "home-all-takeaway-offers-title": "\n   .primaryTopBarTitle = { -home-offers }",

        "home-takeaway-shops": "\n      .title = { -home-shops } \n      .rightOptionText = { -show-all }",
        "home-all-takeaway-shops-title": "\n   .primaryTopBarTitle = { -home-shops }",

        "home-product-favorites": "\n      .title = { -home-my-favorites } \n      .rightOptionText = { -show-all }",
        "home-all-product-favorites-title": "\n   .primaryTopBarTitle = { -home-my-favorites }",
        "home-all-product-favorites-empty": "Du har ingen favoritter.",
        "home-product-favorites-options": "\n .loadingDescription = Fjerner favoritt...",
        "home-remove-favorite": "Fjern favoritt",

        "home-user-card-survey-rate-now": "Vurder nå",

        "home-user-card-check-in-reminder-check-in": "Takk for at du sjekket inn i dag",
        "home-user-card-check-in-reminder-no-check-in": "Husk å sjekke inn i kantinen i dag",
        "home-user-card-check-in-reminder-learn-more": "Les mer",

        "home-user-card-lunch-subscription-used-count": "{ $used } av { $total }",
        "home-user-card-lunch-subscription-used-this-week": "brukt denne uken",
        "home-user-card-lunch-subscription-used-this-month": "brukt denne måneden",

        "home-user-card-recent-purchase-view-receipt": "Vis kvittering",
        "home-user-card-recent-purchase-status": "Status: { $status }",
        "home-user-card-recent-purchase-status-item-received": "MOTTATT",
        "home-user-card-recent-purchase-status-item-paid": "BETALT",
        "home-user-card-recent-purchase-status-item-failed": "FEILET",
        "home-user-card-recent-purchase-status-item-canceled": "KANSELLERT",

        // user survey modal
        "user-survey-step2-question-good": "Hva gjorde vi spesielt bra?",
        "user-survey-step2-question-bad": "Hva kunne vi ha gjort bedre?",
        "user-survey-add-comment-button": "\n {$comment ->\n      *[0] Skriv en\n      [other] Rediger\n} kommentar",
        "user-survey-thank-you-message": "Takk for at du ville delta",
        "user-survey-next-button": "Neste",
        "user-survey-skip-button": "Hopp over",
        "user-survey-submit-button": "Send inn\n .loadingLabel = Sender...",
        "user-survey-close-button": "Lukk",
        "user-survey-comment-drawer": "\n   .title = Kommentar",
        "user-survey-comment-field": "\n   .label = Skriv kommentaren din her",
        "user-survey-comment-submit-button": "Lagre",
        "user-survey-comment-cancel-button": "Avbryt",

        // navigation
        "bottom-navigation-home": "\n   .label = Hjem",
        "bottom-navigation-todays-purchases": "\n   .label = I dag",
        "bottom-navigation-order": "\n   .label = Bestill",
        "bottom-navigation-messages": "\n   .label = Meldinger",
        "bottom-navigation-me": "\n   .label = Meg",

        "app-navigation-to-shop-select": "Gå til butikker",
        "app-navigation-to-login": "Gå til innlogging",
        "app-navigation-to-home": "Gå til forsiden",


        // Consent dialog
        "consent-dialog-personal-information-header": "Personopplysninger",
        "consent-dialog-pii-category-contact-info": "Kontaktinformasjon",
        "consent-dialog-pii-category-identifiers": "Identifikatorer",
        "consent-dialog-pii-category-purchases": "Kjøp",
        "consent-dialog-pii-category-user-content": "Brukerinnhold",
        "consent-dialog-pii-item-name": "Navn",
        "consent-dialog-pii-item-email-address": "E-post",
        "consent-dialog-pii-item-phone-number": "Telefonnummer",
        "consent-dialog-pii-item-user-id": "Bruker-ID",
        "consent-dialog-pii-item-device-id": "Enhets-ID",
        "consent-dialog-pii-item-customer-support": "Kundeservice",
        "consent-dialog-pii-item-purchase-history": "Kjøpshistorikk",
        "consent-dialog-privacy-policy-header": "Personvernerklæring",
        "consent-dialog-privacy-policy-link": "Se firmaets personvernerklæring",


        // My meetings
        "my-meetings-sittings": "{$amount} kuv.",
        "my-meetings-header-label": "\n   .primaryTopBarTitle = Mine møter",
        "my-meetings-tab-meeting-booking": "\n .label = Ressursbookinger",
        "my-meetings-tab-meeting-service": "\n .label = Møteservering",
        "my-meetings-now-badge": "\n      .label = Nå",
        "my-meetings-search-label": "\n   .label = Søk",
        "my-meetings-no-filtered-label": "Ingen samsvarende møter",
        "my-meetings-no-meetings-label": "Ingen planlagte møter",


        // Meeting details
        "meeting-details-delete-dialog": "\n  .description = Avbestille leveringen {$date}?\n .loadingDescription = Sletter levering...\n  .confirmLabel = Slett",


        // Todays purchases
        "todays-orders-receipt-top-bar-title": "Dagens kjøp",
        "todays-orders-receipt-top-bar-title-length": "Dagens kjøp {$selectedOrder} av {$maxOrders}",
        "todays-orders-none-found": "\n .title = Ingen kjøp\n .description = Du har ingen kjøp i dag.",
        "todays-orders-no-such-order": "Ordren finnes ikke",


        // My orders
        "my-orders-page": "\n  .primaryTopBarTitle = Mine ordrer",
        "my-orders-page-header": "\n .title = Mine ordrer",

        // My Orders tabs
        "my-orders-tab-purchases": "\n .label = Daglige kjøp",
        "my-orders-tab-wallet-refills": "\n .label = Påfyll",

        // My Orders expenses chart tabs
        "my-orders-expenses-chart-tab-last-week": "\n .label = Siste 7 dager",
        "my-orders-expenses-chart-tab-last-month": "\n .label = Siste 30 dager",
        "my-orders-expenses-chart-tab-last-year": "\n .label = Siste 365 dager",
        "my-orders-expenses-chart-title": "Privatkjøp",

        // My Orders filter button
        "my-orders-filter-button-none": "Ingen",
        "my-orders-filter-button-all": "Alle",
        "my-orders-filter-button-some": "{$selected} / {$available} valgt",
        "my-orders-filter-button-lunch": "{ -order-type-lunch(count: 100) }",
        "my-orders-filter-button-take-away": "{ -order-type-take-away(count: 100) }",
        "my-orders-filter-button-catering": "{ -order-type-catering(count: 100) }",
        "my-orders-filter-button-meeting": "{ -order-type-meeting(count: 100) }",
        "my-orders-filter-button-refill-account": "{ -order-type-refill-account(count: 100) }",
        "my-orders-filter-button-auto-refill-account": "{ -order-type-auto-refill-account(count: 100) }",
        "my-orders-filter-button-lunch-subscription": "{ -order-type-lunch-subscription(count: 100) }",
        "my-orders-filter-button-subscribe-payment-card": "{ -order-type-subscribe-payment-card(count: 100) }",
        "my-orders-filter-button-refund": "{ -order-type-refund(count: 100) }",

        // My Orders filter order types
        "my-orders-filter-order-type-lunch": "\n .label = { -order-type-lunch(count: 100) }",
        "my-orders-filter-order-type-take-away": "\n .label = { -order-type-take-away(count: 100) }",
        "my-orders-filter-order-type-catering": "\n .label = { -order-type-catering(count: 100) }",
        "my-orders-filter-order-type-meeting": "\n .label = { -order-type-meeting(count: 100) }",
        "my-orders-filter-order-type-refill-account": "\n .label = { -order-type-refill-account(count: 100) }",
        "my-orders-filter-order-type-auto-refill-account": "\n .label = { -order-type-auto-refill-account(count: 100) }",
        "my-orders-filter-order-type-lunch-subscription": "\n .label = { -order-type-lunch-subscription(count: 100) }",
        "my-orders-filter-order-type-subscribe-payment-card": "\n .label = { -order-type-subscribe-payment-card(count: 100) }",
        "my-orders-filter-order-type-refund": "\n .label = { -order-type-refund(count: 100) }",

        "my-orders-filter-modal": "\n .title = Ordretyper\n .cancelButtonLabel = Lukk",
        "my-orders-filter-order-type-label": "Ordretyper",
        "my-orders-results": "{$amount} resultater",
        "my-orders-load-more-button": "Last inn flere",
        "my-orders-price-total": "Totalt",
        "my-orders-print": "Skriv ut",
        "my-orders-edit": "Rediger",
        "my-orders-share": "Del",
        "my-orders-delete": "Slett",
        "my-orders-receipt": "Kvittering",


        // Transactions
        "transaction-generic-processing": "Vent litt...",
        "transaction-generic-success": "Transaksjonen er gjennomført",
        "transaction-missing-payment-window-url": "API-et sendte ikke en lenke til betalingsvinduet. Vennligst kontakt support",


        // Shop - payment methods
        "-payment-accept-terms": "Jeg godtar vilkårene",

        "payment-method-initial-message": "Velg betalingsmåte",
        "payment-method-mobile-pay-alert-message": "Bruk MobilePay-appen på telefonen din for å fullføre betalingen. Du må godkjenne betalingen i MobilePay innen 5 minutter og komme tilbake hit.",
        "payment-method-free": "Gratis",
        "payment-method-credit-card": "Kredittkort",
        "payment-method-one-click": "Betal med { $card }",
        "payment-method-mobile-pay": "MobilePay",
        "payment-method-salary-reduction": "Lønnstrekk",
        "payment-method-lunch-registration": "Lunsjordning",
        "payment-method-guest-lunch-registration": "Lunsjordning",
        "payment-method-facilitynet-account": "{ -gopay-wallet } (<balance>{ $balance }</balance>)",
        "payment-method-invoice": "Faktura",
        "payment-method-card-expired": "utløpt",


        // --- Shopping basket ---

        // Basket section
        "shopping-basket-product-to-pay-by-free": "(gratis)",
        "shopping-basket-product-to-pay-by-credit-card": "(med\u00A0kort)",
        "shopping-basket-product-to-pay-by-one-click-payment": "(med\u00A0kort)",
        "shopping-basket-product-to-pay-by-mobile-pay": "(med\u00A0MobilePay)",
        "shopping-basket-product-to-pay-by-payroll-deduction": "(via\u00A0lønnstrekk)",
        "shopping-basket-product-to-pay-by-lunch-registration": "(via\u00A0lunsjordning)",
        "shopping-basket-product-to-pay-by-guest-lunch-registration": "(via\u00A0lunsjordning)",
        "shopping-basket-product-to-pay-by-gopay-account": "(med\u00A0{ -gopay-wallet })",
        "shopping-basket-product-to-pay-by-invoice": "(med\u00A0faktura)",

        // Section total
        "shopping-basket-total-section-header": "Å betale:",
        "shopping-basket-total-line": "\n .label = Å betale",
        "shopping-basket-total-private-price": "Du betaler: { $privateTotal }",
        "shopping-basket-total-company-price": "Firmaet betaler: { $companyTotal }",

        "shopping-basket-total-to-pay-by-free": "\n .label = Gratis",
        "shopping-basket-total-to-pay-by-credit-card": "\n .label = Med kort",
        "shopping-basket-total-to-pay-by-one-click-payment": "\n .label = Med kort",
        "shopping-basket-total-to-pay-by-mobile-pay": "\n .label = Med MobilePay",
        "shopping-basket-total-to-pay-by-payroll-deduction": "\n .label = Via lønnstrekk",
        "shopping-basket-total-to-pay-by-lunch-registration": "\n .label = Via lunsjordning",
        "shopping-basket-total-to-pay-by-guest-lunch-registration": "\n .label = Via lunsjordning",
        "shopping-basket-total-to-pay-by-gopay-account": "\n .label = Med { -gopay-wallet }",
        "shopping-basket-total-to-pay-by-invoice": "\n .label = Med faktura",

        // Accounting section
        "shopping-basket-accounting-header": "\n .description = Kontering",

        // Conditions section
        "shopping-basket-conditions-terms-label": "Jeg godtar <conditionsLink>vilkårene</conditionsLink>",
        "shopping-basket-conditions-terms-not-accepted": "Du må godta vilkårene",

        // Refill account
        "-refill-account-balance": "{ $pos -> \n [initial] Saldo \n *[other] saldo \n }",
        "refill-account-balance-limit": "Din { -refill-account-balance } kan aldri overstige { $maxBalance }",

        "refill-title": "\n .primaryTopBarTitle = { -gopay-wallet }",
        "refill-tab": "\n .label = Fyll på",
        "refill-current-balance": "Nåværende { -refill-account-balance }: { $currentBalanceFormatted }",
        "refill-step-description-1": "\n    .description = Skriv inn beløp",
        "refill-step-description-1-custom": "Annet beløp:",
        "refill-no-preset-amounts": "Ingen forhåndsdefinerte beløp tilgjengelig",
        "refill-step-amount-custom-amount": "\n .label = Beløp ({ $currencySymbol })\n .helperText = Ny { -refill-account-balance } kan ikke overstige { $maxBalance }",
        "refill-menu-item-custom-amount": "Annet",
        "refill-terms-not-provided": "Salgsstedet har ikke definert vilkår. Kontakt salgsstedet for å løse problemet.",
        "refill-account-perform-refill-button": "Fullfør påfyll ({ $toPay })\n .loadingLabel = Fyller på...",

        // Automatic account refill
        "automatic-refill-page-title": "\n .primaryTopBarTitle = Automatisk påfyll",
        "automatic-refill-tab": "\n .label = Automatisk påfyll",
        "automatic-refill-description": "Aktiver automatisk påfyll når din { -refill-account-balance } kommer under et bestemt beløp. Din { -refill-account-balance } kan aldri overstige { $maxBalance }",
        "automatic-refill-step-description-1": "\n    .description = Fyll på med",
        "automatic-refill-step-when-balance-below": "\n .description = Når saldoen er under",
        "automatic-refill-step-when-balance-below-input-amount": "\n    .label = Beløp ({ $currencySymbol })",
        "automatic-refill-confirm-button": "Aktiver automatisk påfyll\n .loadingLabel = Aktiverer...",
        "automatic-refill-confirm-button-activated": "Oppdater\n .loadlingLabel = Oppdaterer...",
        "automatic-refill-account": "Oppdater automatisk påfyll",
        "automatic-refill-cancel": "Avslutt automatisk påfyll",
        "automatic-refill-cancel-modal": "\n    .description = Avslutte automatisk påfyll?\n .loadingDescription = Avslutter...\n  .confirmLabel = Avslutt",
        "automatic-refill-activated-chip": "\n  .label = Aktiv",
        "automatic-refill-limit-amount-too-low": "Saldogrensen for påfyll kan ikke være lavere enn { $minLimitAmount }",
        "automatic-refill-would-exceed-max-balance": "Ny { -refill-account-balance } kan ikke overstige { $maxBalance }",

        // Top-up
        "automatic-refill-no-subscription-banner-text": "Aktiver automatisk påfyll",
        "automatic-refill-no-subscription-help-link": "Hvordan fungerer det?",
        "automatic-refill-no-subscription-help-drawer": "Ved å aktivere automatisk påfyll har du alltid penger i din digitale wallet.\n\n Du velger påfyllingsbeløp og en beløpsgrense, så fyller systemet automatisk på din wallet når saldoen faller under grenseverdien.\n\n Når du aktiverer automatisk påfyll, lagres betalingskortet ditt sikkert hos vår sertifiserte betalingsgateway.\n .title = Slik fungerer automatisk påfyll",
        "automatic-refill-active-subscription-banner-text": "Automatisk påfyll er aktivt",
        "automatic-refill-active-subscription-banner-below": "Når saldoen er under {$sum}",
        "automatic-refill-active-subscription-banner-amount": "Fyll på med {$amount}",

        // Lunch subscription
        "lunch-subscription-page-title": "\n .primaryTopBarTitle = Mine abonnementer",
        "my-lunch-subscriptions-no-active-subscriptions": "Du har ingen aktive abonnementer.",
        "lunch-subscription-subscribe-button": "Tegn nytt abonnement",
        "lunch-subscription-subscribe-page-title": "\n .primaryTopBarTitle = Velg abonnement",
        "lunch-subscription-subscribe-no-offers-message": "Det er ingen tilgjengelige abonnementer.",
        "lunch-subscription-choose-offer": "Velg",
        "lunch-subscription-unsubscribe-screen": "\n .primaryTopBarTitle = Avslutt abonnement",
        "lunch-subscription-unsubscribe-title": "Er du sikker på at du vil avslutte abonnementet ditt?",
        "lunch-subscription-unsubscribe-terms": "Les vilkårene",

        "lunch-subscription-menu-cancel-subscription-option": "Avslutt abonnement",
        "lunch-subscription-subscription-conditions-modal": "\n .title = Abonnements­vilkår",

        "lunch-subscription-basket": "\n .primaryTopBarTitle = Legg til abonnement",
        "lunch-subscription-basket-step-subperiod-label": "Oppstart",
        "lunch-subscription-basket-step-payment-label": "Betaling",
        "lunch-subscription-basket-period": "\n .description = Velg abonnementsstart",
        "lunch-subscription-basket-period-too-late-notice": "(for sent)",
        "lunch-subscription-basket-period-next-button": "Neste",
        "lunch-subscription-basket-step-two": "\n .description = Diett",
        "lunch-subscription-basket-step-three": "\n .description = Betaling",
        "lunch-subscription-basket-diet-vegetarian": "Vegetar",
        "lunch-subscription-terms": "{ -payment-accept-terms }",
        "lunch-subscription-payment-method-subscribe-payment-card": "Registrer nytt betalingskort",
        "lunch-subscription-payment-method-one-click-payment": "Betal med { $card }",
        "lunch-subscription-payment-method-payroll-deduction": "Lønnstrekk",

        "lunch-subscription-price-per-month": "{$amount} per måned",

        "lunch-subscription-order": "Abonner\n .loadingLabel = Abonnerer...",
        "lunch-subscription-cancel": "Avslutt abonnement",
        "lunch-subscription-cancel-button": "Avslutt\n .loadingLabel = Avslutter...",
        "lunch-subscription-closed": "Stengt",
        "lunch-subscription-tab-month": "\n .label = Måned",
        "lunch-subscription-tab-week": "\n .label = Uke",
        "lunch-subscription-tab-day": "\n .label = Dag",
        "lunch-subscription-monthly-overview-description-closed": "Kjøkkenet er stengt",
        "lunch-subscription-monthly-overview-description-subscribed": "Påmeldte dager",
        "lunch-subscription-on-locations": "\n .primaryTopBarTitle = Abonnementslokasjoner",
        "lunch-subscription-locations": "{$amount} lokasjoner",
        "lunch-subscription-conditions": "Abonnementsvilkår",
        "lunch-subscription-start-date": "Starter {$date}",

        "lunch-subscription-weekly-overview": "Ukeoversikt",
        "lunch-subscription-weekly-overview-dish-of-the-day": "Dagens rett",
        "lunch-subscription-weekly-overview-description": "Fravelg dagene du ikke ønsker å bestille lunsj.",


        // New Order
        "order-cart-cleared": "Handlekurven ble tømt.",
        "-payment-cards": "{ $pos -> \n [title] Betalingskort \n [initial] Betalingskort \n *[other] betalingskort \n [one] betalingskort \n }",


        // Paymentcards
        "paymentcards-title": "\n .primaryTopBarTitle = Betalingskort",
        "paymentcards-card": "\n    .primary = {$number} ({$type})",
        "paymentcards-empty": "Du har ingen registrerte betalingskort. Hvis du lagrer et kort her, blir det enklere å betale, fordi du ikke trenger å taste inn kortopplysningene dine hver gang.",
        "paymentcards-too-many-cards": "\n  .text = Du kan ha { $maxPaymentCards} registrerte betalingskort. Fjern et kort for å kunne legge til et nytt.",
        "paymentcards-button-add-payment-card": "Legg til betalingskort\n .loadingLabel = Legger til kort...",
        "paymentcards-remove-card": "Fjern betalingskort",
        "paymentcards-options-dialog": "\n .loadingDescription = Fjerner kort...",

        // Conditions section
        "paymentcards-conditions-terms-error-dialog-label": "\n .text = Firmaet ditt har ikke definert vilkår. Kontakt dem for å løse problemet.",
        "paymentcards-conditions-terms-error-label": "Kan ikke legge til kort uten vilkår",
        "paymentcards-conditions-terms-not-accepted": "Du må godta vilkårene",
        "paymentcards-conditions-iframe-done-button": "Lukk",


        // QuickPay
        "quickpay-generic-success": "Betalingen din ble gjennomført",
        "quickpay-finalization-error-timeout": "Vi har ennå ikke mottatt bekreftelse fra betalingsleverandøren din. Sjekk nettbanken din for å være sikker på at betalingen ble gjennomført.",
        "quickpay-finalization-error-other": "Betalingen kunne ikke gjennomføres på grunn av en uventet feil. Prøv å gjennomføre en ny betaling senere.",
        "quickpay-source-mismatch-title-should-be-app": "Handling fra appen",
        "quickpay-source-mismatch-title-should-be-web": "Handling fra nettleseren",
        "quickpay-source-mismatch-message-should-be-app": "Vi klarte ikke å bytte tilbake til GoPay-appen din automatisk. Du må gå tilbake til appen selv.",
        "quickpay-source-mismatch-message-should-be-web": "Dette er GoPay-appen din. Du brukte GoPay i nettleseren, men har appen installert. Dette gir konflikt ved kredittkort- og MobilePay-transaksjoner. Bruk helst appen når den er installert.",
        "quickpay-canceled": "\n .title = Kjøp avbrutt\n .message = Du avbrøt betalingen av kjøpet ditt",


        // Me page
        "me-user-greeting": "Hei {$firstName}",
        "me-my-wallets-section": "\n .title = Mine wallets",
        "me-my-wallets-status-auto-refill-active": "Automatisk påfyll",
        "me-my-wallets-wallet-name": "Min wallet",
        "me-my-wallets-refill-select-card-balance": "Saldo:",
        "me-my-wallets-wallet-brand": "GoPay Wallet",
        "me-my-facility-net": "Mitt { -gopay-app-name }",
        "me-my-meetings": "Mine møter",
        "me-my-orders": "Mine ordrer",
        "me-customer-service-category": "Kundeservice",
        "me-customer-service-intercom-consent-dialog": "\n .title = Tillat chat \n .message = Vi bruker Intercom til chatstøtten vår. Hvis du samtykker, deler vi opplysningene nedenfor med Intercom. \n .skipLabel = Ikke nå \n .consentLabel = Tillat",
        "me-customer-service-intercom-error-dialog": "\n .title = Chat mislyktes\n .message = Noe gikk galt under innlasting av chatten. Du kan sende en e-post til support i stedet.\n .buttonLabel = Forstått",
        "me-customer-service-intercom-loading-dialog": "\n .description = Chatten lastes inn...",
        "me-help-center": "Hjelpesenter",
        "me-chat-support": "Chat",
        "me-email-support": "E-postsupport",
        "me-shortcuts": "Snarveier",
        "me-payment-cards": "Betalingskort",
        "me-lunch-subscription-menu-item": "Abonnement",
        "me-settings": "Innstillinger",
        "me-switch-account": "Bytt brukerkonto",
        "me-switch-account-top-bar-title": "\n .primaryTopBarTitle = Bytt brukerkonto",
        "me-switch-account-description": "Du har følgende kontoer med e-postadressen <email>{ $email }</email>.",
        "me-switch-account-email-confirm": "Før du kan bytte mellom brukerkontoene dine, må du bekrefte at du eier e-postkontoen <email>{ $email }</email>.",
        "me-switch-account-no-accounts": "Fant ingen andre brukerkontoer med denne e-postadressen.",
        "me-switch-account-invalid-authentication": "Ugyldig serversvar",
        "me-switch-account-send-confirmation-link": "Send bekreftelseslenke",
        "me-switch-account-confirmation-send": "En e-post er sendt. Sjekk innboksen din.",
        "me-switch-account-failed-to-load": "Kunne ikke laste inn brukerkontoene dine.",
        "me-switch-account-contact-support": "Kontakt support",
        "me-switch-account-send-confirmation-submit-button": "{ -authenticate-submit-button }\n .loadingLabel = Venter på bekreftelse...",
        "me-logout": "Logg ut",

        // User account page
        "user-account-page": "\n .primaryTopBarTitle = Brukerkonto",
        "user-account-type-company-account": "\n .label = Kontotype\n .value = Firmakonto",
        "user-account-type-private-account": "\n .label = Kontotype\n .value = Privat konto",
        "user-account-company": "\n .label = Firma",
        "user-account-username": "\n .label = Brukernavn",
        "user-account-name": "\n .label = Navn",
        "user-account-email": "\n .label = E-post",
        "user-account-mobile": "\n  .label = Mobil",
        "user-account-department": "\n .label = Avdeling",
        "user-account-about-link": "Om denne kontoen",
        "user-account-about-drawer-company-account": "### Firmakonto\n\n Firmaer og andre organisasjoner kan tilby ulike tjenester med betaling via GoPay, f.eks. kjøp av kantinemat. Med firmakontoen din har du tilgang til disse butikkene.\n .title = Om brukerkontoen din",
        "user-account-about-drawer-private-account": "### Privat konto\n\n I tillegg til firmabutikker finnes det også offentlige butikker i GoPay som privat­personer kan handle i. Med den private kontoen din har du tilgang til alle offentlige butikker.\n .title = Om brukerkontoen din",


        // Me settings module
        "settings-title": "\n .primaryTopBarTitle = Innstillinger",
        "settings-change-language": "Bytt språk",
        "settings-english": "Engelsk",
        "settings-danish": "Dansk",
        "settings-norwegian": "Norsk",
        "settings-swedish": "Svensk",
        "settings-notifications": "Varsler",
        "settings-push-notifications": "Pushvarsler",
        "settings-receipts-via-email": "Kvittering på e-post",
        "settings-receipts-meeting-service": "Kvitteringer for møteservice",
        "settings-receipts-canteen": "Private kantinekjøp",
        "settings-sms-notifications": "SMS-varsler",
        "settings-customer-service": "Kundeservice",
        "settings-social-media": "Sosiale medier",
        "settings-policies": "Retningslinjer",
        "settings-HELP_CENTER": "Hjelpesenter",
        "settings-HELP_CENTER_USER_GUIDE_MOBILE_APP": "Brukerveiledning",
        "settings-SOCIAL_FACILITYNET_FACEBOOK": "Følg oss på Facebook",
        "settings-SOCIAL_FACILITYNET_LINKEDIN": "Følg oss på LinkedIn",
        "settings-PRIVACY_TERMS": "Personvernerklæring",
        "settings-COOKIES_TERMS": "Cookie-erklæring",
        "settings-EULA": "EULA",


        // Fast track menu
        "fast-track-menu-show-shop": "\n .title = Fast Track\n .contentActionText = Vis butikk",
        "fast-track-menu-no-menus": "Ingen menyer tilgjengelig. Kontakt kantinen.",


        // Anonymous enter kitchen
        "anonymous-enter-kitchen-header": "Skriv inn butikknummer",
        "anonymous-enter-kitchen-shop-number-input": "\n .label = Butikknummer",
        "anonymous-enter-kitchen-submit-button": "{ -authenticate-submit-button }",
        "anonymous-enter-kitchen-loading-dialog": "\n .description = Laster inn Fast Track-meny...",
        "anonymous-enter-kitchen-shop-number-requirements": "Skriv inn et butikknummer på { $minDigits }-{ $maxDigits} sifre",


        // To fast track menu via route
        "to-fast-track-menu-via-route-invalid-kitchen-id": "Butikknummeret {$kitchenCode} finnes ikke.",
        "to-fast-track-menu-via-route-error": "Noe gikk galt",
        "to-fast-track-menu-via-route-contact": "Kontakt kjøkkenpersonalet hvis feilen gjentar seg.",


        // Choose location
        "choose-location-picker-no-location": "Velg lokasjon",
        "choose-location-header-company-user": "\n .title = Velg lokasjonen din",
        "choose-location-header-private-user": "\n .title = Velg salgssted",
        "choose-location-shop-number-field": "\n .label = Skriv inn butikknummer",
        "choose-location-shop-number-submit-button": "Velg\n .loadingLabel = Laster inn...",
        "choose-location-about-shop-number-link": "Hva er et butikknummer?",
        "choose-location-alternative-method-header": "⸺ eller ⸺",
        "choose-location-search-for-location-button": "Vis alle salgssteder",
        "choose-location-search-field": "\n .label = Søk på navn, adresse, butikknummer...",
        "choose-location-help-text": "Finner du ikke lokasjonen din, må du kontakte administratoren deres.",
        "choose-location-dialog": "\n .title = Bytt lokasjon",
        "choose-location-dialog-private-user": "\n .title = Bytt salgssted",
        "choose-location-shop-numbers": "Butikker: { $numbers }",
        "choose-location-select-location-button": "Velg\n .disabledLabel = Aktiv\n .loadingLabel = Lagrer valg...",
        "choose-location-active-badge": "Aktiv",
        "choose-location-confirm-button": "Bekreft lokasjon\n .loadingLabel = Lagrer valg...",
        "choose-location-no-results": "Ingen lokasjoner samsvarer med søket ditt.",


        // Order takeaway
        "order-takeaway-page-title": "\n .primaryTopBarTitle = Takeaway",

        // Order catering
        "order-catering-page-title": "\n .primaryTopBarTitle = Lunsj",

        // --- Products page --- Offer tag ---
        "offer-tag-good-price": "God pris",
        "offer-tag-great-offer": "Flott tilbud",
        "offer-tag-buy-now": "Kjøp nå",
        "offer-tag-discount": "Tilbud",
        "offer-tag-week-offer": "Ukens tilbud",
        "offer-tag-day-offer": "Dagens tilbud",
        "offer-tag-save-x-percentage": "Spar { $amount }%",
        "offer-tag-save-x-price": "Spar { $amount } kr",
        "offer-tag-limited-on-sale": "Kun { $amount } igjen",

        // --- Products page --- Allergens ---
        "order-food-allergens-list": "Allergener: { $allergens }",

        // Order takeaway/catering
        // "webshop-canteen-not-found-dialog": "\n .title = Ikke fundet\n .message = Køkkenet blev ikke fundet. Kontakt support.\n .buttonLabel = Luk",
        "order-basket-title": "\n .primaryTopBarTitle = Kurv",
        "webshop-shop-type-lunch": "Lunsjordning",
        "webshop-shop-type-take-away": "Takeaway",
        "webshop-shop-type-catering": "Møteservering",
        "order-food-add": "Legg til",
        "order-food-amount-private": "Til meg",
        "order-food-amount-company": "Til gjest",
        "order-food-pickup": "Hentes: {$time} {$place}",
        "order-food-items-left": "{$amount} igjen",
        "order-food-basket-button": "Gå til kassen ({ $items }) { $price }",
        "order-food-item-out-of-stock": "Varen er utsolgt",
        "order-food-item-past-deadline": "Fristen er overskredet",
        "order-food-item-quick-add": "Legg til",
        "order-food-basket-overview": "\n .description = Varekjøp",
        "order-food-basket-private": "Privat",
        "order-food-basket-company": "Firma",
        "order-food-basket-guest": "Gjest:",
        "order-food-basket-guest-information": "\n .description = Gjesteinformasjon",
        "order-food-basket-payment": "\n .description = Betalingsmåte",
        "order-food-basket-payment-methods": "\n .title = Betalingsmåter",
        "order-food-basket-delivery": "\n .description = Levering",
        "order-food-basket-pickup": "Skal hentes",
        "order-food-basket-paid": "Betalt - {$selectedOrder} av {$maxOrders}",
        "order-food-basket-name": "\n .label = Ditt navn\n .emptyErrorText = Feltet må fylles ut",
        "order-food-basket-email": "\n .label = Din e-post\n .emptyErrorText = Feltet må fylles ut\n .invalidErrorText = E-postadressen er ugyldig",
        "order-food-basket-phone": "\n .label = Telefonnr. (valgfritt)",
        "order-food-basket-comment": "\n .label = Skriv en kommentar",
        "order-food-basket-purchase-button": "Gjennomfør betaling\n .loadingLabel = Gjennomfører kjøp...",
        "order-food-basket-seller": "Selger: {$seller}",
        "order-food-canteen-details-contact": "Kontakt",
        "order-food-canteen-details-address": "Adresse",
        "order-food-canteen-details-closed": "Stengt",
        "order-food-receipt-scan-to-checkin": "Skann for å sjekke inn",
        "order-food-receipt-scan-to-checkin-offline": "QR-koden kunne ikke lastes inn.",
        "order-food-featured-deliverydate": "Velg leveringstid",
        "order-food-upcoming-menus": "Kommende menyer",
        "order-food-empty-state-text": "Kjøkkenet tilbyr ingen mat i dag. Prøv en av de andre dagene.",
        "order-food-sold-out-snackbar": "Varen er utsolgt",

        "product-unavailable-product-not-found-modal": "\n .title = Varen ble ikke funnet\n .message = Den ønskede varen finnes ikke i sortimentet for valgt dato. Kontakt kantinen.\n .buttonLabel = Forstått",
        "product-unavailable-product-unavailable-modal": "\n .buttonLabel = Forstått",


        // Product modal
        "canteenProductModal-preparation-label": "Tilberedning",
        "canteenProductModal-declaration-label": "Varedeklarasjon",
        "canteenProductModal-allergens-label": "Allergener",


        // Proof of Purchase
        "proof-of-purchase-order-id": "Ordre-ID: { NUMBER($orderId, useGrouping: 0) }",
        "proof-of-purchase-open-receipt-button": "Vis kvittering",
        "proof-of-purchase-subscription-type": "Abonnementstype: { $limit } dager per { $period }",
        "proof-of-purchase-usage-remaining": "Forbruk: { $remaining } dager igjen",
        "usage-period-week": "uke",
        "usage-period-month": "måned",
        "proof-of-purchase-order-type-lunch": "{ -order-type-lunch }",
        "proof-of-purchase-order-type-takeaway": "{ -order-type-take-away }",
        "proof-of-purchase-order-type-take-away": "{ -order-type-take-away }",
        "proof-of-purchase-order-type-catering": "{ -order-type-catering }",
        "proof-of-purchase-order-type-meeting": "{ -order-type-meeting }",
        "proof-of-purchase-order-type-refill-account": "{ -order-type-refill-account }",
        "proof-of-purchase-order-type-auto-refill-account": "{ -order-type-auto-refill-account }",
        "proof-of-purchase-order-type-lunch-subscription": "{ -order-type-lunch-subscription }",
        "proof-of-purchase-order-type-subscribe-payment-card": "{ -order-type-subscribe-payment-card }",
        "proof-of-purchase-order-type-refund": "{ -order-type-refund }",
        "receipt-qr-dialog-back": "Tilbake",
        "receipt-no-deliveries": "Ordren inneholder ingen leveranser",
        "receipt-company-orderlines": "Firmakjøp",

        "receipt-product-paid-by-free": "(gratis)",
        "receipt-product-paid-by-credit-card": "(med\u00A0kort)",
        "receipt-product-paid-by-one-click-payment": "(med\u00A0kort)",
        "receipt-product-paid-by-mobile-pay": "(med\u00A0MobilePay)",
        "receipt-product-paid-by-payroll-deduction": "(via\u00A0lønnstrekk)",
        "receipt-product-paid-by-lunch-registration": "(via\u00A0lunsjordning)",
        "receipt-product-paid-by-guest-lunch-registration": "(via\u00A0lunsjordning)",
        "receipt-product-paid-by-gopay-account": "(med\u00A0{ -gopay-wallet })",
        "receipt-product-paid-by-invoice": "(med\u00A0faktura)",


        // Receipt
        "receipt-title": "Kvittering",
        "receipt-forward-receipt-dialog": "\n .title = { $receipts -> \n *[one] Send kvittering \n [other] Send kvitteringer \n } \n .cancelButtonLabel = Avbryt\n .submitButtonLabel = Send\n .submitButtonLoadingLabel = Sender...",
        "receipt-forward-receipt-email-field": "\n .label = E-postadresse",
        "receipt-forward-receipt-email-field-is-required": "E-postadresse må fylles ut",
        "receipt-forward-receipt-email-field-must-be-valid": "Ugyldig e-postadresse",

        // Purchase section
        "receipt-purchase-details-section": "\n .heading = Kjøpsdetaljer",
        "receipt-purchase-payment-status-paid": "BETALT",
        "receipt-purchase-payment-status-unpaid": "IKKE BETALT",
        "receipt-purchase-payment-status-invoiced": "FAKTURERT",
        "receipt-purchase-payment-status-refunded": "REFUNDERT",
        "receipt-purchase-payment-status-cancelled": "REFUNDERT",
        "receipt-purchase-payment-status-failed": "FEILET",
        "receipt-purchase-address-item": "\n .label = Kjøpssted:",
        "receipt-purchase-seller-vat-number": "Org.nr.:\u00A0{ $vatNumber }",
        "receipt-purchase-customer-item": "\n .label = Kunde:",
        "receipt-purchase-time-item": "\n .label = Kjøpstidspunkt:",
        "receipt-purchase-payment-method-item": "\n .label = Betalingsmåte:",
        "receipt-purchase-order-id-item": "\n .label = Ordre-ID:",
        "receipt-purchase-shop-channel-item": "\n .label = Via:",
        "receipt-purchase-cost-center-item": "\n .label = Kontostreng:",

        // Actions section
        "receipt-actions-forward-receipt-button": "Send kvittering",
        "receipt-actions-cancel-order-button": "Avbestill ordre",
        "receipt-actions-delete-order-button": "Slett ordre",
        "receipt-actions-edit-order-button": "Rediger",
        "receipt-actions-update-order-button": "Rediger ordre",
        "receipt-actions-confirm-cancel-dialog": "\n .description = Du er i ferd med å avbestille ordren din. Er du sikker?\n .confirmLabel = Ja\n .cancelLabel = Nei\n .loadingDescription = Avbestiller ordre...",
        "receipt-actions-cancel-unavailable-dialog": "\n .buttonLabel = Forstått",

        // Delivery section
        "receipt-delivery-details-section": "\n .heading = Levering",
        "receipt-delivery-order-type-item": "\n .label = Ordretype:",
        "receipt-order-type-lunch": "{ -order-type-lunch }",
        "receipt-order-type-takeaway": "{ -order-type-take-away }",
        "receipt-order-type-take-away": "{ -order-type-take-away }",
        "receipt-order-type-catering": "{ -order-type-catering }",
        "receipt-order-type-meeting": "{ -order-type-meeting }",
        "receipt-order-type-meeting-service": "{ -order-type-meeting-service }",
        "receipt-order-type-refill-account": "{ -order-type-refill-account }",
        "receipt-order-type-auto-refill-account": "{ -order-type-auto-refill-account }",
        "receipt-order-type-lunch-subscription": "{ -order-type-lunch-subscription }",
        "receipt-order-type-subscribe-payment-card": "{ -order-type-subscribe-payment-card }",
        "receipt-order-type-refund": "{ -order-type-refund }",
        "receipt-delivery-type-item": "\n .label = Leveringstype:",
        "receipt-seats-item": "\n .label = Plasser",
        "receipt-delivery-type-pick-up": "Henting",
        "receipt-delivery-time-item": "\n .label = Leveringstid:",
        "receipt-delivery-comment-item": "\n .label = Kommentar:",

        // Order details section
        "receipt-payment-method-free": "Gratis",
        "receipt-payment-method-credit-card": "Kredittkort",
        "receipt-payment-method-one-click-payment": "Kredittkort",
        "receipt-payment-method-mobile-pay": "MobilePay",
        "receipt-payment-method-salary-reduction": "Lønnstrekk",
        "receipt-payment-method-payroll-deduction": "Lønnstrekk",
        "receipt-payment-method-lunch-registration": "Lunsjordning",
        "receipt-payment-method-guest-lunch-registration": "Lunsjordning",
        "receipt-payment-method-gopay-account": "{ -gopay-wallet }",
        "receipt-payment-method-invoice": "Faktura",
        "receipt-payment-method-subscribe-payment-card": "Registrert betalingskort",

        "receipt-order-details-buyerparty-private": "Privatkjøp",
        "receipt-order-details-buyerparty-company": "Firmakjøp",
        "receipt-order-details-vat-total": "\n .label = Herav MVA:",

        // Accounting details section
        "receipt-accounting-details-section": "\n .heading = Kontering",


        // Order details
        "orderDetails-deliveryType-delivery": "\n .title = Levering",
        "orderDetails-deliveryType-pick-up": "\n .title = Henting",
        "orderDetails-deliveryType-eating-in-canteen": "\n .title = Spiser i kantinen",
        "orderDetails-attendees-subject-label": "\n    .title = Møtedeltakere ({$count})",
        "orderDetails-note-label": "\n    .title = Notat",
        "orderDetails-catering-totalAmountIncludeVat-label": "Beløp inkl. MVA",
        "orderDetails-header-label": "\n    .title = Mitt møte",
        "orderDetails-meeting-privacyStatus-label": "\n    .label = Privat",
        "orderDetails-resources-subject-label": "Ressurser",
        "deleteMeeting-labels": "\n    .title = Er du sikker på at du vil slette møtet?\n    .buttonLabel = Bekreft",


        // Confirmation-dialog
        "confirmation-dialog-cancel": "Avbryt",


        // Notifications
        "notification-snackbar-action-view": "Vis",
        "notification-snackbar-default-message": "Ny melding",
        "notifications-page-title": "\n .primaryTopBarTitle = Meldinger",
        "notifications-no-notifications": "\n .title = Ingen meldinger akkurat nå\n .description = Her kan du lese meldinger fra kantinen og servicesenteret.",
        "notifications-no-notifications-notification-time-type-news": "Nyhet",
        "notifications-no-notifications-notification-time-type-info": "Informasjon",
        "notifications-no-notifications-notification-time-type-important": "Viktig",
        "notifications-no-notifications-notification-time-type-offer": "Tilbud",
        "notifications-no-notifications-notification-time-type-event": "Arrangement",
        "notifications-no-notifications-notification-time-type-reminder": "Påminnelse",
        "notifications-no-notifications-notification-time-type-menu-change": "Meny",
        "notifications-no-notifications-notification-time-type-feedback": "Tilbakemelding",
        "notifications-no-notifications-notification-time-type-health": "Helse",
        "notifications-sender-type-merchant": "Salgssted",
        "notifications-sender-type-service-center": "Service",


        // Room Finder
        "roomfinder-tabs-searcher": "Søk",
        "roomfinder-tabs-overview": "Oversikt",
        "roomfinder-overview-find-room": "\n    .label = Finn rom",
        // "roomFinder-availableResourceGroup-1": "Ledige rum",
        // "roomFinder-availableResourceGroup-2": "Ledige AV udstyr",
        // "roomFinder-availableResourceGroup-4": "Ledige biler",


        // Week menu
        "home-week-menu-title": "\n .title = Ukens meny",
        "weekMenu-badge": "Ukens meny",
        "weekMenu-header-label": "\n .title = Ukens meny",
        "weekMenu-no-menu-label": "Dessverre er det ikke opprettet noen menyer denne uken.",
        "weekMenu-no-menu-subtitle": "Her kan du se menyen for de kommende dagene, hvis den er tilgjengelig. Du kan ikke handle her.",


        // Home Booking Meeting
        "home-book-meeting-title": "\n .title = Møteplanlegging",


        // Meeting Planning Dialog
        "order-meeting-service": "Bestill møteservering",
        "book-resources": "Book ressurser",


        // Booking Meeting Screen
        "meeting-service-screen-add-title": "Legg til møteservering",
        "meeting-service-screen-edit-title": "Rediger møteservering",
        "meeting-planning-screen-title": "Legg til levering av møteservering",
        "delivery-form-delivery-type-field-title": "Leveringstype",
        "cancel-delivery": "\n .description = Vil du avbryte den nåværende ordren?\n .confirmLabel = Ja\n .cancelLabel = Nei",


        // Add Initial Delivery Form
        "delivery-form-meeting-field-label": "\n .label = Møtenavn\n .title = Møtenavn\n .placeholder = Skriv inn møtenavn",
        "delivery-form-date-from-field-label": "\n .label = Fra",
        "delivery-form-date-to-field-label": "\n .label = Til",
        "delivery-form-meeting-field-date-label": "\n .label = Leveringsdato\n .placeholder = Velg leveringsdato",
        "delivery-form-delivery-type-field-label": "\n .label = Leveringstype\n .placeholder = Velg leveringstype",
        "delivery-form-delivery-location-field-label": "\n .label = Leveringssted\n .placeholder = Velg leveringssted",
        "delivery-form-delivery-other-location-field-label": "\n .label = Annet leveringssted",
        "delivery-form-delivery-other-location-field-button": "+ Annet leveringssted",
        "delivery-form-delivery-time-field-label": "\n .label = Leveringstid\n .placeholder = Velg leveringstid",
        "delivery-form-delivery-time-field-select-label": "Velg tid",
        "delivery-form-delivery-seats-field-label": "\n .label = Kuverter",
        "delivery-form-delivery-seats-field-placeholder": "Skriv inn eller velg antall kuverter",
        "delivery-form-delivery-other-message-field-label": "\n .label = Ordremelding\n .title = Ordremelding\n .placeholder = Skriv en melding til ordren din",
        "delivery-payment-form-delivery-bill-receiver-field-label": "Fakturamottaker",
        "delivery-payment-form-delivery-bill-receiver-field-placeholder": "Velg fakturamottaker",
        "delivery-payment-form-delivery-cost-center-field-label": "Kostnadssted",
        "delivery-payment-form-delivery-cost-center-field-placeholder": "Skriv inn kostnadssted",

        "delivery-form-meeting-field-name-required": "Møtenavn er påkrevd",
        "delivery-form-meeting-field-name-max-length": "Maksimal lengde er { $maxLength} tegn",
        "delivery-form-meeting-field-other-location-max-length": "Maksimal lengde for Annet leveringssted er { $maxLength } tegn",
        "delivery-form-meeting-field-other-message-max-length": "Maksimal lengde for annen melding er { $maxLength } tegn",
        "delivery-form-meeting-field-from-date-required": "Startdato er påkrevd",
        "delivery-form-meeting-field-date-required": "Leveringsdato er påkrevd",
        "delivery-form-meeting-field-from-date-should-be-before-to-date": "Startdato må være før sluttdato",
        "delivery-form-meeting-field-to-date-required": "Sluttdato er påkrevd",
        "delivery-form-meeting-field-delivery-type-required": "Leveringstype er påkrevd",
        "delivery-form-meeting-field-delivery-time-required": "Leveringstidspunkt er påkrevd",
        "delivery-form-meeting-field-delivery-time-available": "Leveringstid på valgt dag er tilgjengelig fra { $from } til { $to }",
        "delivery-form-meeting-field-delivery-time-need-date": "Velg en leveringsdato først.",
        "delivery-form-delivery-location-field-required": "Leveringssted er påkrevd",
        "delivery-form-delivery-other-location-field-required": "Annet leveringssted er påkrevd",
        "delivery-form-meeting-field-seats-min-length": "Minimum antall plasser er { $minLength}",
        "delivery-form-meeting-field-seats-max-length": "Maksimalt antall plasser er { $maxLength}",
        "delivery-payment-form-delivery-dynamic-field-required": "{ $field } er påkrevd",

        "delivery-form-meeting-submit-button": "Fortsett ({ $finalPrice})\n .loadingLabel = Laster inn...",

        "delivery-form-delivery-extra-services-title": "Ekstratjenester",
        "delivery-form-delivery-extra-services-add": "Legg til",
        "delivery-form-delivery-extra-services-remove": "Fjern",
        "delivery-form-delivery-extra-services-per-person": "{ $pricePerSeat} per kuvert",
        "delivery-form-delivery-extra-services-min-max": "Min. { $minimumPrice} | Maks. { $maximumPrice}",


        // Delivery Type Options
        "delivery-type-option-canteen": "Spiser i kantinen",
        "delivery-type-option-pick-up": "Henter selv",
        "delivery-type-option-delivery": "Levering",


        // Other Location
        "other-location": "Annet leveringssted",


        // Basket
        "basket-removeAll-label": "Fjern alle",
        "basket-meeting-catering-title": "Møteservering",


        // Meeting options
        "meetingOptions-extendMeeting-label": "Forleng møte",
        "meetingOptions-endMeeting-label": "Avslutt møte",
        "meetingOptions-viewDetails-label": "Vis detaljer",
        "meetingOptions-delete-label": "Avlys møte",
        "meetingOptions-edit-label": "Rediger møte",
        "meetingOptions-assistance-label": "Assistanse",
        "-meetingOptions-startMeeting-base-label": "Start møte",
        "meetingOptions-startMeeting-expired-label": "{-meetingOptions-startMeeting-base-label} (utløpt)",
        "meetingOptions-startMeeting-left-label": "{-meetingOptions-startMeeting-base-label} ({$time} igjen)",
        "meetingOptions-startMeeting-in-label": "{-meetingOptions-startMeeting-base-label} (om {$time})",

        "meeting-planned-complete": "Ordren er gjennomført",
        "meeting-planned-added": "Møteservering lagt til",
        "meeting-planned-updated": "Møteservering oppdatert",
        "meetingBooked-next-choice-go-to-resource-booking": "Legg til ressursbooking i ordren",

        // Confirm Meeting Planning Modal
        "confirm-meeting-planning-modal-title": "Fristen er overskredet",
        "confirm-meeting-planning-modal-description": "Du kan fortsatt legge inn bestillingen, men den må godkjennes av personalet, noe som betyr at du ikke kan forvente at den blir levert.",
        "confirm-meeting-planning-modal-button-denied": "Ikke gjennomfør bestillingen",
        "confirm-meeting-planning-modal-button-accept": "Gjennomfør bestilling",

        // meetings
        "meetingBooked-complete": "Booking gjennomført",
        "meetingBooked-when": "Når:",
        "meetingBooked-next-choice": "Hva vil du gjøre nå?",
        "meetingBooked-next-choice-go-to-meeting-planning": "Legg til møteservering",
        "meetingBooked-next-choice-finish": "Jeg er ferdig",
        "meetingStarted-labels": "\n    .topbarTitle = Møte startet\n    .title = Møtet har startet\n    .buttonLabel = Fortsett",
        "meetingEnded-labels": "\n    .topbarTitle = Møte avsluttet\n    .title = Møtet er avsluttet\n    .buttonLabel = Fortsett",
        "meetingDeleted-labels": "\n    .topbarTitle = Møte slettet\n    .title = Møtet er slettet\n    .buttonLabel = Fortsett",
        "meetingBooked-labels": "\n    .topbarTitle = Møte booket\n    .buttonLabel = Neste",
        "meetingUpdated-labels": "\n    .topbarTitle = Møte oppdatert\n    .buttonLabel = Neste",
        "meeting-open-in-calendar": "Legg til møtet i kalenderen",
        "hours": "timer",


        // Buttons
        "edit-button": "Rediger",
        "delete-button": "Slett",


        // News
        "allNews-header-label": "\n    .title = Nyheter",
        "allNews-noNews-label": "Ingen nyheter",
        "newsDetails-header-label": "\n    .title = Mine nyheter",
        "newsDetails-by-label": "Av {$creator} | {$company}",

        "completeBooking-advancedOptions-button-label": "Neste",
        "completeBooking-advancedOptions-button-complete": "Fullfør",
        "completeBooking-advancedOptions-booking-time": "Bookingtid:",
        "completeBooking-advancedOptions-header-label": "Flere innstillinger",
        "completeBooking-advancedOptions-privateMeeting-label": "Privat møte",
        "completeBooking-advancedOptions-sittingsArrangement-label": "Sitteoppsett",
        "completeBooking-header-label": "\n .title = Fullfør booking",
        "completeBooking-meeting-endTime-label": "Til",
        "completeBooking-meeting-header-label": "Detaljer",
        "completeBooking-meeting-meetingName-label": "Møtenavn",
        "completeBooking-meeting-startTime-label": "Fra",
        "completeBooking-meeting-card-meeting-details-title": "Møtedetaljer",
        "completeBooking-meeting-card-resources-title": "Ressurser",
        "assistance-header-label": "\n    .title = Be om assistanse",
        "assistance-location-label": "Lokasjon:",
        "assistance-noLocations-label": "Ingen lokasjoner",
        "assistance-button-next-label": "Neste",
        "assistance-chooseMessage-label": "Velg melding:",
        "assistance-yourMessage-label": "Meldingen din:",
        "assistance-writeMessage-label": "Skriv din egen melding",
        "confirmBooking-header-label": "\n    .title = Bekreft booking",
        "confirmBooking-button-label": "Fullfør bestilling",
        "confirmBooking-empty-basket-label": "Kurven er tom",
        "endMeeting-button-label": "Bekreft",
        "endMeeting-header-label": "Avslutt møte",
        "endMeeting-success-label": "Møtet er avsluttet",
        "endMeeting-text-label": "Er du sikker på at du vil avslutte møtet?",
        "endMeeting-labels": "\n    .title = {endMeeting-text-label}\n    .title = {endMeeting-button-label}",
        "startMeeting-button-label": "Bekreft",
        "startMeeting-text-label": "Er du sikker på at du vil starte dette møtet?",
        "startMeeting-labels": "\n    .title = {startMeeting-text-label}\n    .title = {startMeeting-button-label}",
        "extendMeeting-button-label": "Bekreft",
        "extendMeeting-changeEndTimeTo-label": "Endre sluttidspunkt for møtet til:",
        "extendMeeting-header-label": "\n    .title = Forleng møte",
        "extendMeeting-success-label": "Møtet er forlenget",
        "meeting-button-end-label": "Avslutt",
        "meeting-button-extend-label": "Forleng",
        "meetingUpdated-button-label": "Lukk",
        "meetingUpdated-header-label": "Fullført",
        "myMeetings-button-delete-label": "Slett",
        "myMeetings-button-update-label": "Oppdater",
        "myMeetings-header-label": "Filter",
        "myMeetings-search-label": "Søk",
        "roomFinder-button-addResource-label": "Legg til",
        "roomFinder-button-removeResource-label": "Fjern",
        "roomFinder-button-next-label": "Neste",
        "roomFinder-resourceInfo-av-subject-label": "AV-utstyr",
        "roomFinder-resourceInfo-availableTimeSlots-subject-label": "Ledige tider",
        "roomFinder-resourceInfo-moreTimeSlots-subject-label": "Flere tider...",
        "roomFinder-resourceInfo-noAvailableTimeSlots-subject-label": "Ingen ledige tider",
        "roomFinder-resourceInfo-sittingsArrangements-subject-label": "Bordoppsett",
        "roomFinder-specifyTime-header-label": "\n    .title = Angi tid",
        "roomFinder-search-filter-label": "Filter",
        "roomFinder-search-nofilter-label": "Ingen filtre",
        "roomFinder-search-unsupported-element": "Elementtypen støttes ikke: {$type}",
        "roomFinder-search-hour-label": "time",
        "roomFinder-search-hours-label": "timer",
        "roomFinder-continue-to-checkout-label": "Fortsett til betaling ({$amount})",
        "roomFinder-subject-label": "Tilgjengelige {resourceType}",
        "roomFinderFilter-fixedAv-label": "AV-utstyr",
        "roomFinderFilter-floor-label": "Etasje",
        "roomFinderFilter-group-button-label": "Vis",
        "roomFinderFilter-group-fixedAv-label": "AV-utstyr",
        "roomFinderFilter-group-floor-label": "Etasje",
        "roomFinderFilter-group-location-label": "Lokasjoner",
        "roomFinderFilter-group-resourceTypes-label": "Ressurstype",
        "roomFinderFilter-group-tag-label": "Tagger",
        "roomFinderFilter-header-label": "Filter",
        "roomFinderFilter-location-label": "Lokasjoner",
        "roomFinderFilter-meetingDuration-hour-label": "Time",
        "roomFinderFilter-meetingDuration-hours-label": "Timer",
        "roomFinderFilter-meetingDuration-subject-label": "Varighet",
        "roomFinderFilter-removeAll-label": "Fjern alle",
        "roomFinderFilter-remove-label": "Fjern",
        "roomFinderFilter-resourceTypes-av-label": "AV-utstyr",
        "roomFinderFilter-resourceTypes-car-label": "Bil",
        "roomFinderFilter-resourceTypes-label": "Ressurstype",
        "roomFinderFilter-resourceTypes-room-label": "Rom",
        "roomFinderFilter-resourceTypes-subject-label": "Ressurstyper",
        "roomFinderFilter-room-label": "Rom",
        "roomFinderFilter-room-subject-label": "Rom",
        "roomFinderFilter-sittings-numberOfSittings-label": "Antall kuverter",
        "roomFinderFilter-sittings-subject-label": "Kuverter",
        "roomFinderFilter-tag-label": "Tagger",
        "roomFinderFilter-dateTimePicker-change-date-button": "\n .buttonLabel = Endre dato",
        "upcomingMeetings-button-end-label": "Avslutt",
        "upcomingMeetings-button-extend-label": "Forleng",
        "upcomingMeetings-meetingStatus-label": "Nå",
        "upcomingMeetings-moreInfo-label": "Mer info",
        // Misc
        "action-sheet-cancel-button": "Avbryt",
        "alert-dialog-close-button": "Lukk",
        "options-novalue": "-- Velg --",
        "date-time-picker": "\n .okLabel = OK\n .cancelLabel = Avbryt",

        // Tooltip hints
        "unavailable-date-tooltip": "Denne datoen kan ikke velges",

        // Errors
        "error-required": "Feltet må fylles ut",
        "error-max-length": "Maksimal lengde er {$maxLength}",

        // --- API components ---
        "api-query-table-fetch-error-message": "Feil ved innlasting av data",
        "api-query-autocomplete": "Laster inn data...",
        "api-query-autocomplete-fetch-error-message": "Feil ved innlasting av data. <retryLink>Prøv igjen</retryLink>",
    },
    "sv-SE": {
        // Branding
        "-gopay-app-name": "GoPay",
        "-gopay-wallet": "GoPay Wallet",
        "-gopay-wallet-refill": "{ $pos -> \n [initial] { $count -> \n *[one] Påfyllning \n [other] Påfyllningar \n } \n *[other] { $count -> \n *[one] påfyllning \n [other] påfyllningar \n } \n }",
        "date-time-hour-cycle": "h23",


        // Date and time formats as Luxon format strings
        // See: https://moment.github.io/luxon/docs/manual/formatting.html#table-of-tokens
        "date-time-format-date": "yyyy-MM-dd",
        "date-time-format-day-and-date": "EEEE dd/MM",
        "date-time-format-time": "HH:mm",
        "date-time-format-date-and-time": "yyyy-MM-dd\u00A0HH:mm",
        "date-time-format-duration-hours-minutes": "h't.' m'min.'",
        "date-time-format-duration-minutes": "m'min.'",
        "date-time-format-duration-seconds": "s's'",
        "date-time-format-month-and-year": "MMMM yyyy",
        "date-time-short-relative-units": "s|m|t|d|v|mån|år",


        // success / error handling
        "success-modal-status": "\n .buttonLabel = Stäng",
        "error-route-status": "\n .topbarTitle = Fel",
        "error-dialog-close-button": "\n .buttonLabel = Stäng",
        "action-failed-modal": "\n .buttonLabel = Stäng",
        "errorHandler-labels": "\n    .title = Något gick fel\n    .buttonLabel = Gå till startsidan",
        "errorHandlerModal-labels": "\n    .title = Något gick fel\n    .buttonLabel = Stäng",
        "error-clientNetworkError-label": "Ingen internetanslutning",
        "unknown-error-title": "Okänt fel",
        "unknown-error-message": "Ingen ytterligare felinformation tillgänglig",
        "general-error-title": "Något gick fel",
        "internal-error-title": "Internt fel",
        "api-call-error-title-network-error": "Nätverksfel",
        "api-call-error-title-bad-request": "Begäran avvisad",
        "api-call-error-title-unauthorized": "Ogiltig inloggning",
        "api-call-error-title-forbidden": "Åtkomst nekad",
        "api-call-error-title-not-found": "Hittades inte",
        "api-call-error-title-conflict": "Konflikt",
        "api-call-error-title-too-many-requests": "För många begäranden",
        "api-call-error-title-internal-server-error": "Internt serverfel",
        "api-call-error-title-service-unavailable": "Tjänsten är inte tillgänglig",
        "api-call-error-title-request-failed": "Begäran misslyckades",
        "api-call-error-no-internet-connection": "Ingen internetanslutning",
        "api-call-error-could-not-reach-server": "Kunde inte nå servern",
        "api-call-error-protocol-error": "Serverfel: { $responseCode } { $responseStatus }",
        "api-call-error-api-returned-error": "Oväntat serverfel",
        "api-call-error-response-not-json": "Oväntat svar från servern",
        "api-call-error-retry-button": "Försök igen",

        // accept terms and conditions
        "accept-terms-and-conditions": "Jag godkänner <conditionsLink>villkoren</conditionsLink>",


        // authenticate common
        "-authenticate-submit-button": "Nästa",


        // order types shared translations
        "-order-type-lunch": "Dagsköp",
        "-order-type-take-away": "Takeaway",
        "-order-type-catering": "Mötesförtäring",
        "-order-type-meeting": "{ $count -> \n *[one] Bokning \n [other] Bokningar \n }",
        "-order-type-meeting-service": "{ $count -> \n *[one] Mötesförtäring \n [other] Mötesförtäringar \n }",
        "-order-type-refill-account": "{ $count -> \n *[one] Påfyllning \n [other] Påfyllningar \n }",
        "-order-type-auto-refill-account": "Automatisk påfyllning",
        "-order-type-lunch-subscription": "{ $count -> \n *[one] Prenumeration \n [other] Prenumerationer \n }",
        "-order-type-subscribe-payment-card": "Registrerat betalkort",
        "-order-type-refund": "{ $count -> \n *[one] Återbetalning \n [other] Återbetalningar \n }",


        // user prompts
        "user-prompt-button-label-saving": "\n .loadingLabel = Sparar...",

        // onboarding flow (right after login)
        "onboarding-skip-step": "Hoppa över",
        "onboarding-step-of": "Steg { $step } av { $total }",
        "onboarding-location-title": "Välj plats",
        "onboarding-location-title-private-user": "Välj försäljningsställe",
        "onboarding-location-description": "Din plats är där du vanligtvis arbetar. Du kan alltid byta plats högst upp i appen.",
        "onboarding-push-title": "Får vi skicka pushnotiser till dig?",
        "onboarding-push-description": "Då får du besked när din beställning är bekräftad och klar för avhämtning, om något ändras i din beställning eller bokning, och när ett möte närmar sig. Meddelanden från kantinen kommer bara från den kantin du har valt.",
        "onboarding-push-footnote": "Ändras under Jag › Inställningar › Pushnotiser",
        "onboarding-push-continue-button": "Fortsätt\n .loadingLabel = Sparar...",
        "onboarding-done-title": "{ $location } är vald",
        "onboarding-done-title-no-location": "Du är redo",
        "onboarding-done-push-both": "Du får meddelanden om drift och beställningar samt nyheter och erbjudanden. Ändras under Jag › Inställningar › Pushnotiser.",
        "onboarding-done-push-tx": "Du får meddelanden om drift och beställningar. Ändras under Jag › Inställningar › Pushnotiser.",
        "onboarding-done-push-news": "Du får meddelanden om nyheter och erbjudanden. Ändras under Jag › Inställningar › Pushnotiser.",
        "onboarding-done-push-none": "Du får inga pushnotiser. Du kan slå på dem under Jag › Inställningar › Pushnotiser.",
        "onboarding-done-button": "Till startsidan",

        // push notification types & settings
        "push-type-tx-title": "Drift och beställningar",
        "push-type-tx-description": "Viktiga meddelanden, beställningar och möten.",
        "push-type-news-title": "Nyheter och erbjudanden",
        "push-type-news-description": "Dagens meny, nyheter och erbjudanden.",
        "push-settings-title": "\n .primaryTopBarTitle = Pushnotiser",
        "push-settings-status-unavailable": "Pushnotiser är bara tillgängliga i appen. Dina val nedan sparas och används i appen.",
        "push-settings-status-allowed": "Pushnotiser är tillåtna på den här telefonen",
        "push-settings-status-denied-title": "Avstängt på den här telefonen",
        "push-settings-status-denied-text": "Oavsett valen nedan skickar vi inga meddelanden förrän du tillåter dem på den här telefonen.",
        "push-settings-allow-button": "Tillåt pushnotiser\n .loadingLabel = Sparar...",
        "push-settings-open-settings-link": "Öppna inställningar",
        "push-settings-turn-off-link": "Stäng av helt i telefonens inställningar",


        // welcome
        "authenticate-welcome-slide-header-buy-lunch": "Köp lunch och företagstjänster",
        "authenticate-welcome-slide-text-buy-lunch": "Köp enkelt din mat hos lunchleverantören och andra företagstjänster, t.ex. förtäring vid evenemang och kemtvätt.",
        "authenticate-welcome-slide-header-reduce-food-waste": "Bekämpa matsvinn",
        "authenticate-welcome-slide-text-reduce-food-waste": "Förbeställ och avboka din lunch så att köket kan planera matproduktionen. Välj utvalda menyer som är bättre för miljön.",
        "authenticate-welcome-slide-header-receive-offers": "Veckans menyer och erbjudanden",
        "authenticate-welcome-slide-text-receive-offers": "Du kan se veckans menyer med näringsinnehåll, få erbjudanden och läsa nyheter.",
        "authenticate-welcome-slide-header-purchase-overview": "Se dina köp",
        "authenticate-welcome-slide-text-purchase-overview": "Du har god överblick över dina köp och kvitton. Betalar du via löneavdrag kan du enkelt stämma av.",
        "authenticate-welcome-continue-button": "Logga in / Skapa konto",


        // login choices
        "authenticate-login-choices-header": "Beställ mat och andra tjänster",
        "authenticate-login-choices-login-button": "Logga in",
        "authenticate-login-choices-create-account-button": "Skapa konto",
        "authenticate-login-choices-guest-link": "Köp som gäst",


        // login / signup email
        "authenticate-email-header": "Ange din e-postadress",
        "authenticate-email-signup-header": "Skapa konto",
        "authenticate-email-email-input": "\n .label = E-postadress",
        "authenticate-email-submit-button": "{ -authenticate-submit-button }\n .loadingLabel = Kontrollerar e-postadress...",
        "authenticate-email-email-is-required": "Ange din e-postadress",
        "authenticate-email-email-must-be-an-email": "Ogiltig e-postadress",
        "authenticate-email-expect-company-flow": "Du försöker skapa ett konto med e-post­domänen <emailDomain>{ $emailDomain }</emailDomain>, som är kopplad till ett företag. Om du vill skapa ett privat konto ska du använda din privata e-post­adress.",
        "authenticate-email-expect-private-flow": "Din e-post­domän <emailDomain>{ $emailDomain }</emailDomain> är för närvarande inte kopplad till något företag i systemet. Du måste använda din e-post­adress på jobbet och inte din privata e-post­adress. Kontakta din administratör om du anser att din domän borde vara kopplad till ett företag.",
        "authenticate-email-switch-to-login-flow": "Den angivna e-post­adressen finns redan i systemet. Vill du logga in i stället?",
        "authenticate-email-switch-to-signup-flow": "Den angivna e-post­adressen fanns inte i systemet. Vill du skapa ett konto i stället?",
        "authenticate-email-switch-flow-no-button": "Nej tack",
        "authenticate-email-switch-flow-yes-button": "Ja tack",
        "authenticate-email-okay-button": "Förstått",


        // activate by phone
        "authenticate-phone-number-header": "Aktivera konto",
        "authenticate-phone-number-text": "Vi behöver ditt mobilnummer så att vi kan skicka en aktiveringskod:",
        "authenticate-phone-number-phone-number-input": "\n .label = Telefonnummer",
        "authenticate-phone-number-submit-button": "{ -authenticate-submit-button }\n .loadingLabel = Skickar kod...",
        "authenticate-phone-number-username-missing": "Användarnamn / e-post saknas. Gå tillbaka och fyll i.",
        "authenticate-phone-number-phone-number-is-required": "Telefonnummer krävs",
        "authenticate-phone-number-phone-number-is-invalid": "Ogiltigt telefonnummer",


        // activation code
        "authenticate-activation-code-header": "Aktivera konto",
        "authenticate-activation-code-automatic-activation-text": "Vänta medan vi aktiverar ditt konto...",
        "authenticate-activation-code-manual-activation-text": "Ange koden som du har fått via e-post eller SMS:",
        "authenticate-activation-code-activation-code-input": "\n .label = Aktiveringskod",
        "authenticate-activation-code-submit-button": "{ -authenticate-submit-button }\n .loadingLabel = Aktiverar...",
        "authenticate-activation-code-code-field-required": "Ange en aktiveringskod",
        "authenticate-activation-code-code-field-min-len": "Aktiveringskoden är minst { $codeLength } tecken",


        // login/password
        "authenticate-password-header": "Ange ditt lösenord",
        "authenticate-password-header-with-name": "Välkommen { $displayName }! Logga in.",
        "authenticate-password-password-input": "\n .label = Lösenord",
        "authenticate-password-submit-button": "{ -authenticate-submit-button }\n .loadingLabel = Loggar in...",
        "authenticate-password-username-missing": "Användarnamn / e-post saknas. Gå tillbaka och fyll i.",
        "authenticate-password-forgot-password-link": "Glömt lösenord?",
        "authenticate-password-password-is-required": "Ange ditt lösenord",


        // login/single sign on
        "authenticate-sso-header": "Single Sign-on",
        "authenticate-sso-explanation": "Din organisation { $organization } använder Single Sign-on. Du skickas nu vidare till er gemensamma inloggningssida.",
        "authenticate-sso-canceled-message": "Du slutförde inte inloggningen.",
        "authenticate-sso-retry-button": "Försök igen\n .loadingLabel = Omdirigerar...",


        // login/account-type
        "authenticate-account-type-choose-account-type": "Välj kontotyp",
        "authenticate-account-type-private-account": "Privat konto",
        "authenticate-account-type-company-account": "Företagskonto",
        "authenticate-account-type-private-account-description": "Med ett privat konto kan du registrera dig med din privata e-postadress och få tillgång till offentliga butiker. Du har inte tillgång till företagsbutiker med den här kontotypen.",
        "authenticate-account-type-company-account-description": "Ett företagskonto ger dig tillgång till de butiker som är kopplade till ditt företag. Du måste använda din e-postadress på jobbet för att skapa ett konto.",
        "authenticate-account-type-help-text": "Ska jag använda ett privat konto eller ett företagskonto?",
        "authenticate-account-type-help-drawer": "### Företagskonto\n\n Företag och andra organisationer kan erbjuda olika tjänster med betalning via GoPay, t.ex. köp av mat i kantinen. För att komma åt dessa butiker behöver du ett företagskonto. Vi hittar ditt företag utifrån din e-post­domän.\n\n ### Privat konto\n\n Utöver företagsbutiker finns det även offentliga butiker i GoPay där privat­personer kan handla. Med ett privat konto har du tillgång till alla offentliga butiker.\n   .title = Förklaring av kontotyper",


        // select company
        "authenticate-select-company-header-reason-multiple-companies-for-domain": "Välj företag",
        "authenticate-select-company-header-reason-multiple-accounts-for-username": "Välj användarkonto",
        "authenticate-select-company-reason-message-multiple-companies-for-domain": "Vi kunde inte fastställa ditt företag automatiskt eftersom din e-postdomän <duplicateValue>{ $duplicateValue }</duplicateValue> förekommer flera gånger i vårt system.",
        "authenticate-select-company-reason-message-multiple-accounts-for-username": "Vi hittade flera konton i vårt system med e-postadressen <duplicateValue>{ $duplicateValue }</duplicateValue>.",
        "authenticate-select-company-action-message-multiple-companies-for-domain": "Välj den plats (det företag) som du vill skapa ditt konto under:",
        "authenticate-select-company-action-message-multiple-accounts-for-username": "Välj det användarkonto du vill logga in med.",
        "authenticate-select-company-show-list-button": "Visa alternativ",
        "authenticate-select-company-submit-button": "{ -authenticate-submit-button }\n .loadingLabel = Sparar...",


        // sign up
        "authenticate-signup-header": "Skapa användarkonto",
        "authenticate-signup-text": "Vi behöver veta lite mer om dig för att kunna skapa ditt konto:",
        "authenticate-signup-name-input": "\n .label = Namn",
        "authenticate-signup-mobile-input": "\n .label = Mobil (valfritt)",
        "authenticate-signup-accept-terms-link": "Jag godkänner användarvillkoren",
        "authenticate-signup-submit-button": "{ -authenticate-submit-button }",
        "authenticate-signup-username-missing": "Användarnamn / e-post saknas. Gå tillbaka och fyll i.",
        "authenticate-signup-name-is-required": "Namn krävs",
        "authenticate-signup-name-must-be-full-name": "Vi behöver ditt fullständiga namn (förnamn och efternamn)",
        "authenticate-signup-phone-number-is-invalid": "Ogiltigt telefonnummer",
        "authenticate-signup-terms-must-be-accepted": "Du måste godkänna villkoren för att använda appen",


        // confirm account details
        "authenticate-confirm-account-details-header": "Bekräfta uppgifter",
        "authenticate-confirm-account-details-email-field": "\n .primary = E-post",
        "authenticate-confirm-account-details-name-field": "\n .primary = Namn",
        "authenticate-confirm-account-details-mobile-field": "\n .primary = Mobil",
        "authenticate-confirm-account-submit-button": "{ -authenticate-submit-button }\n .loadingLabel = Skapar ditt konto...",
        "authenticate-confirm-account-details-account-missing": "Kontouppgifter saknas. Gå tillbaka och fyll i.",


        // create password
        "authenticate-create-password-header": "Skapa lösenord",
        "authenticate-create-password-text": "Du måste skapa ett lösenord för att kunna börja använda ditt konto:",
        "authenticate-create-password-password-input": "\n .label = Lösenord",
        "authenticate-create-password-repeat-password-input": "\n .label = Upprepa lösenord",
        "authenticate-create-password-submit-button": "{ -authenticate-submit-button }\n .loadingLabel = Skapar ditt lösenord...",
        "authenticate-create-password-one-time-token-missing": "Engångstoken hittades inte. Gå tillbaka ett steg och försök igen.",
        "authenticate-create-password-password-is-required": "Du måste ange ett lösenord",
        "authenticate-create-password-password-too-short": "Ditt lösenord måste vara minst { $minPasswordLength } tecken",
        "authenticate-create-password-repeat-is-required": "Du måste upprepa ditt lösenord",
        "authenticate-create-password-password-mismatch": "Lösenorden stämmer inte överens",


        // reset password
        "authenticate-reset-password-header": "Återställ lösenord",
        "authenticate-reset-password-button": "Återställ\n .loadingLabel = Återställer...",
        "authenticate-reset-password-username-missing": "Användarnamn / e-post saknas. Gå tillbaka och fyll i.",


        // login route components
        "login-external-guest-dialog": "\n .title = Gästköp\n .message = Du håller på att göra ett gästköp. Butiken öppnas i ett nytt fönster.\n .buttonLabel = Fortsätt",


        // --- User cards ---
        "user-cards-loading-element": "\n .title = Läser in kort...",


        // home
        "-show-all": "Visa alla",
        "-home-upcoming-meetings": "Kommande möten",
        "-home-offers": "Bra erbjudanden",
        "-home-shops": "Butiker",
        "-home-my-favorites": "Mina favoriter",

        "home-label": "Hem",

        "home-news": "\n    .title = Nyheter\n    .rightOptionText = { -show-all }",
        "home-news-kitchen": "\n    .title = Nytt från kantinen\n    .rightOptionText = { -show-all }",
        "home-news-company-and-service-center": "\n    .title = Nytt från {$company}\n    .rightOptionText = { -show-all }",
        "home-news-facilitynet": "\n    .title = Nytt från { -gopay-app-name }\n    .rightOptionText = { -show-all }",

        "home-my-meetings": "\n    .title = Kommande möten\n    .rightOptionText = { -show-all }",
        "home-upcoming-meetings": "Kommande möten",
        "home-contact-the-canteen": "Kontakta kantinen",
        "home-no-planned-meetings": "Inga planerade möten",
        "home-plan-meeting": "Planera ett möte",

        "home-you-are-here": "Du är här:",
        "home-change-location": "Byt plats",

        "home-takeaway-offers": "\n     .title = { -home-offers } \n     .rightOptionText = { -show-all }",
        "home-all-takeaway-offers-title": "\n   .primaryTopBarTitle = { -home-offers }",

        "home-takeaway-shops": "\n      .title = { -home-shops } \n      .rightOptionText = { -show-all }",
        "home-all-takeaway-shops-title": "\n   .primaryTopBarTitle = { -home-shops }",

        "home-product-favorites": "\n      .title = { -home-my-favorites } \n      .rightOptionText = { -show-all }",
        "home-all-product-favorites-title": "\n   .primaryTopBarTitle = { -home-my-favorites }",
        "home-all-product-favorites-empty": "Du har inga favoriter.",
        "home-product-favorites-options": "\n .loadingDescription = Tar bort favorit...",
        "home-remove-favorite": "Ta bort favorit",

        "home-user-card-survey-rate-now": "Betygsätt nu",

        "home-user-card-check-in-reminder-check-in": "Tack för att du checkade in i dag",
        "home-user-card-check-in-reminder-no-check-in": "Kom ihåg att checka in i kantinen i dag",
        "home-user-card-check-in-reminder-learn-more": "Läs mer",

        "home-user-card-lunch-subscription-used-count": "{ $used } av { $total }",
        "home-user-card-lunch-subscription-used-this-week": "använt denna vecka",
        "home-user-card-lunch-subscription-used-this-month": "använt denna månad",

        "home-user-card-recent-purchase-view-receipt": "Visa kvitto",
        "home-user-card-recent-purchase-status": "Status: { $status }",
        "home-user-card-recent-purchase-status-item-received": "MOTTAGEN",
        "home-user-card-recent-purchase-status-item-paid": "BETALD",
        "home-user-card-recent-purchase-status-item-failed": "MISSLYCKADES",
        "home-user-card-recent-purchase-status-item-canceled": "ANNULLERAD",

        // user survey modal
        "user-survey-step2-question-good": "Vad gjorde vi särskilt bra?",
        "user-survey-step2-question-bad": "Vad kunde vi ha gjort bättre?",
        "user-survey-add-comment-button": "\n {$comment ->\n      *[0] Skriv en\n      [other] Redigera\n} kommentar",
        "user-survey-thank-you-message": "Tack för att du deltog",
        "user-survey-next-button": "Nästa",
        "user-survey-skip-button": "Hoppa över",
        "user-survey-submit-button": "Skicka\n .loadingLabel = Skickar...",
        "user-survey-close-button": "Stäng",
        "user-survey-comment-drawer": "\n   .title = Kommentar",
        "user-survey-comment-field": "\n   .label = Skriv din kommentar här",
        "user-survey-comment-submit-button": "Spara",
        "user-survey-comment-cancel-button": "Avbryt",

        // navigation
        "bottom-navigation-home": "\n   .label = Hem",
        "bottom-navigation-todays-purchases": "\n   .label = I dag",
        "bottom-navigation-order": "\n   .label = Beställ",
        "bottom-navigation-messages": "\n   .label = Meddelanden",
        "bottom-navigation-me": "\n   .label = Jag",

        "app-navigation-to-shop-select": "Gå till butiker",
        "app-navigation-to-login": "Gå till inloggning",
        "app-navigation-to-home": "Gå till startsidan",


        // Consent dialog
        "consent-dialog-personal-information-header": "Personuppgifter",
        "consent-dialog-pii-category-contact-info": "Kontaktinformation",
        "consent-dialog-pii-category-identifiers": "Identifierare",
        "consent-dialog-pii-category-purchases": "Köp",
        "consent-dialog-pii-category-user-content": "Användarinnehåll",
        "consent-dialog-pii-item-name": "Namn",
        "consent-dialog-pii-item-email-address": "E-post",
        "consent-dialog-pii-item-phone-number": "Telefonnummer",
        "consent-dialog-pii-item-user-id": "Användar-ID",
        "consent-dialog-pii-item-device-id": "Enhets-ID",
        "consent-dialog-pii-item-customer-support": "Kundtjänst",
        "consent-dialog-pii-item-purchase-history": "Köphistorik",
        "consent-dialog-privacy-policy-header": "Integritetspolicy",
        "consent-dialog-privacy-policy-link": "Visa företagets integritetspolicy",


        // My meetings
        "my-meetings-sittings": "{$amount} kuvert",
        "my-meetings-header-label": "\n   .primaryTopBarTitle = Mina möten",
        "my-meetings-tab-meeting-booking": "\n .label = Resursbokningar",
        "my-meetings-tab-meeting-service": "\n .label = Mötesförtäring",
        "my-meetings-now-badge": "\n      .label = Nu",
        "my-meetings-search-label": "\n   .label = Sök",
        "my-meetings-no-filtered-label": "Inga matchande möten",
        "my-meetings-no-meetings-label": "Inga planerade möten",


        // Meeting details
        "meeting-details-delete-dialog": "\n  .description = Avboka leveransen den {$date}?\n .loadingDescription = Tar bort leverans...\n  .confirmLabel = Ta bort",


        // Todays purchases
        "todays-orders-receipt-top-bar-title": "Dagens köp",
        "todays-orders-receipt-top-bar-title-length": "Dagens köp {$selectedOrder} av {$maxOrders}",
        "todays-orders-none-found": "\n .title = Inga köp\n .description = Du har inga köp i dag.",
        "todays-orders-no-such-order": "Beställningen finns inte",


        // My orders
        "my-orders-page": "\n  .primaryTopBarTitle = Mina beställningar",
        "my-orders-page-header": "\n .title = Mina beställningar",

        // My Orders tabs
        "my-orders-tab-purchases": "\n .label = Dagliga köp",
        "my-orders-tab-wallet-refills": "\n .label = Påfyllning",

        // My Orders expenses chart tabs
        "my-orders-expenses-chart-tab-last-week": "\n .label = Senaste 7 dagarna",
        "my-orders-expenses-chart-tab-last-month": "\n .label = Senaste 30 dagarna",
        "my-orders-expenses-chart-tab-last-year": "\n .label = Senaste 365 dagarna",
        "my-orders-expenses-chart-title": "Privatköp",

        // My Orders filter button
        "my-orders-filter-button-none": "Ingen",
        "my-orders-filter-button-all": "Alla",
        "my-orders-filter-button-some": "{$selected} / {$available} valda",
        "my-orders-filter-button-lunch": "{ -order-type-lunch(count: 100) }",
        "my-orders-filter-button-take-away": "{ -order-type-take-away(count: 100) }",
        "my-orders-filter-button-catering": "{ -order-type-catering(count: 100) }",
        "my-orders-filter-button-meeting": "{ -order-type-meeting(count: 100) }",
        "my-orders-filter-button-refill-account": "{ -order-type-refill-account(count: 100) }",
        "my-orders-filter-button-auto-refill-account": "{ -order-type-auto-refill-account(count: 100) }",
        "my-orders-filter-button-lunch-subscription": "{ -order-type-lunch-subscription(count: 100) }",
        "my-orders-filter-button-subscribe-payment-card": "{ -order-type-subscribe-payment-card(count: 100) }",
        "my-orders-filter-button-refund": "{ -order-type-refund(count: 100) }",

        // My Orders filter order types
        "my-orders-filter-order-type-lunch": "\n .label = { -order-type-lunch(count: 100) }",
        "my-orders-filter-order-type-take-away": "\n .label = { -order-type-take-away(count: 100) }",
        "my-orders-filter-order-type-catering": "\n .label = { -order-type-catering(count: 100) }",
        "my-orders-filter-order-type-meeting": "\n .label = { -order-type-meeting(count: 100) }",
        "my-orders-filter-order-type-refill-account": "\n .label = { -order-type-refill-account(count: 100) }",
        "my-orders-filter-order-type-auto-refill-account": "\n .label = { -order-type-auto-refill-account(count: 100) }",
        "my-orders-filter-order-type-lunch-subscription": "\n .label = { -order-type-lunch-subscription(count: 100) }",
        "my-orders-filter-order-type-subscribe-payment-card": "\n .label = { -order-type-subscribe-payment-card(count: 100) }",
        "my-orders-filter-order-type-refund": "\n .label = { -order-type-refund(count: 100) }",

        "my-orders-filter-modal": "\n .title = Ordertyper\n .cancelButtonLabel = Stäng",
        "my-orders-filter-order-type-label": "Ordertyper",
        "my-orders-results": "{$amount} resultat",
        "my-orders-load-more-button": "Läs in fler",
        "my-orders-price-total": "Totalt",
        "my-orders-print": "Skriv ut",
        "my-orders-edit": "Redigera",
        "my-orders-share": "Dela",
        "my-orders-delete": "Ta bort",
        "my-orders-receipt": "Kvitto",


        // Transactions
        "transaction-generic-processing": "Vänta...",
        "transaction-generic-success": "Transaktionen är genomförd",
        "transaction-missing-payment-window-url": "API:et skickade ingen länk till betalningsfönstret. Kontakta support",


        // Shop - payment methods
        "-payment-accept-terms": "Jag godkänner villkoren",

        "payment-method-initial-message": "Välj betalningsmetod",
        "payment-method-mobile-pay-alert-message": "Du måste använda MobilePay-appen i din telefon för att slutföra betalningen. Du måste godkänna betalningen i MobilePay inom 5 minuter och återvända hit.",
        "payment-method-free": "Gratis",
        "payment-method-credit-card": "Kreditkort",
        "payment-method-one-click": "Betala med { $card }",
        "payment-method-mobile-pay": "MobilePay",
        "payment-method-salary-reduction": "Löneavdrag",
        "payment-method-lunch-registration": "Lunchordning",
        "payment-method-guest-lunch-registration": "Lunchordning",
        "payment-method-facilitynet-account": "{ -gopay-wallet } (<balance>{ $balance }</balance>)",
        "payment-method-invoice": "Faktura",
        "payment-method-card-expired": "utgånget",


        // --- Shopping basket ---

        // Basket section
        "shopping-basket-product-to-pay-by-free": "(gratis)",
        "shopping-basket-product-to-pay-by-credit-card": "(med\u00A0kort)",
        "shopping-basket-product-to-pay-by-one-click-payment": "(med\u00A0kort)",
        "shopping-basket-product-to-pay-by-mobile-pay": "(med\u00A0MobilePay)",
        "shopping-basket-product-to-pay-by-payroll-deduction": "(via\u00A0löneavdrag)",
        "shopping-basket-product-to-pay-by-lunch-registration": "(via\u00A0lunchordning)",
        "shopping-basket-product-to-pay-by-guest-lunch-registration": "(via\u00A0lunchordning)",
        "shopping-basket-product-to-pay-by-gopay-account": "(med\u00A0{ -gopay-wallet })",
        "shopping-basket-product-to-pay-by-invoice": "(med\u00A0faktura)",

        // Section total
        "shopping-basket-total-section-header": "Att betala:",
        "shopping-basket-total-line": "\n .label = Att betala",
        "shopping-basket-total-private-price": "Din betalning: { $privateTotal }",
        "shopping-basket-total-company-price": "Företagsbetalning: { $companyTotal }",

        "shopping-basket-total-to-pay-by-free": "\n .label = Gratis",
        "shopping-basket-total-to-pay-by-credit-card": "\n .label = Med kort",
        "shopping-basket-total-to-pay-by-one-click-payment": "\n .label = Med kort",
        "shopping-basket-total-to-pay-by-mobile-pay": "\n .label = Med MobilePay",
        "shopping-basket-total-to-pay-by-payroll-deduction": "\n .label = Via löneavdrag",
        "shopping-basket-total-to-pay-by-lunch-registration": "\n .label = Via lunchordning",
        "shopping-basket-total-to-pay-by-guest-lunch-registration": "\n .label = Via lunchordning",
        "shopping-basket-total-to-pay-by-gopay-account": "\n .label = Med { -gopay-wallet }",
        "shopping-basket-total-to-pay-by-invoice": "\n .label = Med faktura",

        // Accounting section
        "shopping-basket-accounting-header": "\n .description = Kontering",

        // Conditions section
        "shopping-basket-conditions-terms-label": "Jag godkänner <conditionsLink>villkoren</conditionsLink>",
        "shopping-basket-conditions-terms-not-accepted": "Du måste godkänna villkoren",

        // Refill account
        "-refill-account-balance": "{ $pos -> \n [initial] Saldo \n *[other] saldo \n }",
        "refill-account-balance-limit": "Ditt { -refill-account-balance } får aldrig överstiga { $maxBalance }",

        "refill-title": "\n .primaryTopBarTitle = { -gopay-wallet }",
        "refill-tab": "\n .label = Fyll på",
        "refill-current-balance": "Nuvarande { -refill-account-balance }: { $currentBalanceFormatted }",
        "refill-step-description-1": "\n    .description = Ange belopp",
        "refill-step-description-1-custom": "Annat belopp:",
        "refill-no-preset-amounts": "Inga fördefinierade belopp tillgängliga",
        "refill-step-amount-custom-amount": "\n .label = Belopp ({ $currencySymbol })\n .helperText = Nytt { -refill-account-balance } får inte överstiga { $maxBalance }",
        "refill-menu-item-custom-amount": "Annat",
        "refill-terms-not-provided": "Försäljningsstället har inte angett några villkor. Kontakta dem för att lösa problemet.",
        "refill-account-perform-refill-button": "Genomför påfyllning ({ $toPay })\n .loadingLabel = Fyller på...",

        // Automatic account refill
        "automatic-refill-page-title": "\n .primaryTopBarTitle = Automatisk påfyllning",
        "automatic-refill-tab": "\n .label = Automatisk påfyllning",
        "automatic-refill-description": "Aktivera automatisk påfyllning när ditt { -refill-account-balance } går under ett visst belopp. Ditt { -refill-account-balance } får aldrig överstiga { $maxBalance }",
        "automatic-refill-step-description-1": "\n    .description = Fyll på med",
        "automatic-refill-step-when-balance-below": "\n .description = När saldot är under",
        "automatic-refill-step-when-balance-below-input-amount": "\n    .label = Belopp ({ $currencySymbol })",
        "automatic-refill-confirm-button": "Aktivera automatisk påfyllning\n .loadingLabel = Aktiverar...",
        "automatic-refill-confirm-button-activated": "Uppdatera\n .loadlingLabel = Uppdaterar...",
        "automatic-refill-account": "Uppdatera automatisk påfyllning",
        "automatic-refill-cancel": "Avsluta automatisk påfyllning",
        "automatic-refill-cancel-modal": "\n    .description = Avsluta automatisk påfyllning?\n .loadingDescription = Avslutar...\n  .confirmLabel = Avsluta",
        "automatic-refill-activated-chip": "\n  .label = Aktiv",
        "automatic-refill-limit-amount-too-low": "Saldogränsen för påfyllning får inte vara lägre än { $minLimitAmount }",
        "automatic-refill-would-exceed-max-balance": "Nytt { -refill-account-balance } får inte överstiga { $maxBalance }",

        // Top-up
        "automatic-refill-no-subscription-banner-text": "Aktivera automatisk påfyllning",
        "automatic-refill-no-subscription-help-link": "Hur fungerar det?",
        "automatic-refill-no-subscription-help-drawer": "Genom att aktivera automatisk påfyllning har du alltid pengar i din digitala wallet.\n\n Du väljer påfyllningsbelopp och en beloppsgräns, så fyller systemet automatiskt på din wallet när saldot går under gränsvärdet.\n\n När du aktiverar automatisk påfyllning sparas ditt betalkort säkert hos vår certifierade betalningsgateway.\n .title = Så fungerar automatisk påfyllning",
        "automatic-refill-active-subscription-banner-text": "Automatisk påfyllning är aktiv",
        "automatic-refill-active-subscription-banner-below": "När saldot är under {$sum}",
        "automatic-refill-active-subscription-banner-amount": "Fyll på med {$amount}",

        // Lunch subscription
        "lunch-subscription-page-title": "\n .primaryTopBarTitle = Mina prenumerationer",
        "my-lunch-subscriptions-no-active-subscriptions": "Du har inga aktiva prenumerationer.",
        "lunch-subscription-subscribe-button": "Teckna ny prenumeration",
        "lunch-subscription-subscribe-page-title": "\n .primaryTopBarTitle = Välj prenumeration",
        "lunch-subscription-subscribe-no-offers-message": "Det finns inga tillgängliga prenumerationer.",
        "lunch-subscription-choose-offer": "Välj",
        "lunch-subscription-unsubscribe-screen": "\n .primaryTopBarTitle = Avsluta prenumeration",
        "lunch-subscription-unsubscribe-title": "Är du säker på att du vill avsluta din prenumeration?",
        "lunch-subscription-unsubscribe-terms": "Läs villkoren",

        "lunch-subscription-menu-cancel-subscription-option": "Avsluta prenumeration",
        "lunch-subscription-subscription-conditions-modal": "\n .title = Prenumerations­villkor",

        "lunch-subscription-basket": "\n .primaryTopBarTitle = Lägg till prenumeration",
        "lunch-subscription-basket-step-subperiod-label": "Start",
        "lunch-subscription-basket-step-payment-label": "Betalning",
        "lunch-subscription-basket-period": "\n .description = Välj prenumerationsstart",
        "lunch-subscription-basket-period-too-late-notice": "(för sent)",
        "lunch-subscription-basket-period-next-button": "Nästa",
        "lunch-subscription-basket-step-two": "\n .description = Kost",
        "lunch-subscription-basket-step-three": "\n .description = Betalning",
        "lunch-subscription-basket-diet-vegetarian": "Vegetarisk",
        "lunch-subscription-terms": "{ -payment-accept-terms }",
        "lunch-subscription-payment-method-subscribe-payment-card": "Registrera nytt betalkort",
        "lunch-subscription-payment-method-one-click-payment": "Betala med { $card }",
        "lunch-subscription-payment-method-payroll-deduction": "Löneavdrag",

        "lunch-subscription-price-per-month": "{$amount} per månad",

        "lunch-subscription-order": "Prenumerera\n .loadingLabel = Prenumererar...",
        "lunch-subscription-cancel": "Avsluta prenumeration",
        "lunch-subscription-cancel-button": "Avsluta\n .loadingLabel = Avslutar...",
        "lunch-subscription-closed": "Stängt",
        "lunch-subscription-tab-month": "\n .label = Månad",
        "lunch-subscription-tab-week": "\n .label = Vecka",
        "lunch-subscription-tab-day": "\n .label = Dag",
        "lunch-subscription-monthly-overview-description-closed": "Köket är stängt",
        "lunch-subscription-monthly-overview-description-subscribed": "Anmälda dagar",
        "lunch-subscription-on-locations": "\n .primaryTopBarTitle = Prenumerationsplatser",
        "lunch-subscription-locations": "{$amount} platser",
        "lunch-subscription-conditions": "Prenumerationsvillkor",
        "lunch-subscription-start-date": "Startar {$date}",

        "lunch-subscription-weekly-overview": "Veckoöversikt",
        "lunch-subscription-weekly-overview-dish-of-the-day": "Dagens rätt",
        "lunch-subscription-weekly-overview-description": "Välj bort de dagar då du inte vill beställa lunch.",


        // New Order
        "order-cart-cleared": "Varukorgen har tömts.",
        "-payment-cards": "{ $pos -> \n [title] Betalkort \n [initial] Betalkort \n *[other] betalkort \n [one] betalkort \n }",


        // Paymentcards
        "paymentcards-title": "\n .primaryTopBarTitle = Betalkort",
        "paymentcards-card": "\n    .primary = {$number} ({$type})",
        "paymentcards-empty": "Du har inga registrerade betalkort. Om du sparar ett kort här blir det enklare att betala, eftersom du inte behöver ange dina kortuppgifter varje gång.",
        "paymentcards-too-many-cards": "\n  .text = Du kan ha { $maxPaymentCards} registrerade betalkort. Ta bort ett kort för att kunna lägga till ett nytt.",
        "paymentcards-button-add-payment-card": "Lägg till betalkort\n .loadingLabel = Lägger till kort...",
        "paymentcards-remove-card": "Ta bort betalkort",
        "paymentcards-options-dialog": "\n .loadingDescription = Tar bort kort...",

        // Conditions section
        "paymentcards-conditions-terms-error-dialog-label": "\n .text = Ditt företag har inte angett några villkor. Kontakta företaget för att lösa problemet.",
        "paymentcards-conditions-terms-error-label": "Det går inte att lägga till kort utan villkor",
        "paymentcards-conditions-terms-not-accepted": "Du måste godkänna villkoren",
        "paymentcards-conditions-iframe-done-button": "Stäng",


        // QuickPay
        "quickpay-generic-success": "Din betalning har genomförts",
        "quickpay-finalization-error-timeout": "Vi har ännu inte fått någon bekräftelse från din betalningsleverantör. Kontrollera din internetbank för att vara säker på att betalningen genomfördes.",
        "quickpay-finalization-error-other": "Betalningen kunde inte genomföras på grund av ett oväntat fel. Försök att genomföra en ny betalning senare.",
        "quickpay-source-mismatch-title-should-be-app": "Åtgärd från appen",
        "quickpay-source-mismatch-title-should-be-web": "Åtgärd från webbläsaren",
        "quickpay-source-mismatch-message-should-be-app": "Det gick inte att automatiskt växla tillbaka till din GoPay-app. Du måste själv gå tillbaka till appen.",
        "quickpay-source-mismatch-message-should-be-web": "Det här är din GoPay-app. Du använde GoPay i din webbläsare, men har appen installerad. Det orsakar konflikter vid kreditkorts- och MobilePay-transaktioner. Använd helst appen när den är installerad.",
        "quickpay-canceled": "\n .title = Köpet avbröts\n .message = Du avbröt betalningen av ditt köp",


        // Me page
        "me-user-greeting": "Hej {$firstName}",
        "me-my-wallets-section": "\n .title = Mina wallets",
        "me-my-wallets-status-auto-refill-active": "Automatisk påfyllning",
        "me-my-wallets-wallet-name": "Min wallet",
        "me-my-wallets-refill-select-card-balance": "Saldo:",
        "me-my-wallets-wallet-brand": "GoPay Wallet",
        "me-my-facility-net": "Mitt { -gopay-app-name }",
        "me-my-meetings": "Mina möten",
        "me-my-orders": "Mina beställningar",
        "me-customer-service-category": "Kundtjänst",
        "me-customer-service-intercom-consent-dialog": "\n .title = Tillåt chatt \n .message = Vi använder Intercom för vår chattsupport. Om du samtycker delar vi uppgifterna nedan med Intercom. \n .skipLabel = Inte nu \n .consentLabel = Tillåt",
        "me-customer-service-intercom-error-dialog": "\n .title = Chatten misslyckades\n .message = Något gick fel när chatten lästes in. Du kan skicka ett e-postmeddelande till support istället.\n .buttonLabel = Uppfattat",
        "me-customer-service-intercom-loading-dialog": "\n .description = Chatten läses in...",
        "me-help-center": "Hjälpcenter",
        "me-chat-support": "Chatt",
        "me-email-support": "E-postsupport",
        "me-shortcuts": "Genvägar",
        "me-payment-cards": "Betalkort",
        "me-lunch-subscription-menu-item": "Prenumeration",
        "me-settings": "Inställningar",
        "me-switch-account": "Byt användarkonto",
        "me-switch-account-top-bar-title": "\n .primaryTopBarTitle = Byt användarkonto",
        "me-switch-account-description": "Du har följande konton med e-postadressen <email>{ $email }</email>.",
        "me-switch-account-email-confirm": "Innan du kan byta mellan dina användarkonton måste du bekräfta att du äger e-postkontot <email>{ $email }</email>.",
        "me-switch-account-no-accounts": " Inga andra användarkonton hittades med den här e-postadressen.",
        "me-switch-account-invalid-authentication": "Ogiltigt svar från servern",
        "me-switch-account-send-confirmation-link": "Skicka bekräftelselänk",
        "me-switch-account-confirmation-send": "Ett e-postmeddelande har skickats. Kontrollera din inkorg.",
        "me-switch-account-failed-to-load": "Det gick inte att läsa in dina användarkonton.",
        "me-switch-account-contact-support": "Kontakta support",
        "me-switch-account-send-confirmation-submit-button": "{ -authenticate-submit-button }\n .loadingLabel = Väntar på bekräftelse...",
        "me-logout": "Logga ut",

        // User account page
        "user-account-page": "\n .primaryTopBarTitle = Användarkonto",
        "user-account-type-company-account": "\n .label = Kontotyp\n .value = Företagskonto",
        "user-account-type-private-account": "\n .label = Kontotyp\n .value = Privat konto",
        "user-account-company": "\n .label = Företag",
        "user-account-username": "\n .label = Användarnamn",
        "user-account-name": "\n .label = Namn",
        "user-account-email": "\n .label = E-post",
        "user-account-mobile": "\n  .label = Mobilnummer",
        "user-account-department": "\n .label = Avdelning",
        "user-account-about-link": "Om det här kontot",
        "user-account-about-drawer-company-account": "### Företagskonto\n\n Företag och andra organisationer kan erbjuda olika tjänster med betalning via GoPay, t.ex. köp av mat i kantinen. Med ditt företagskonto har du tillgång till dessa butiker.\n .title = Om ditt användarkonto",
        "user-account-about-drawer-private-account": "### Privat konto\n\n Utöver företagsbutiker finns det även offentliga butiker i GoPay där privat­personer kan handla. Med ditt privata konto har du tillgång till alla offentliga butiker.\n .title = Om ditt användarkonto",


        // Me settings module
        "settings-title": "\n .primaryTopBarTitle = Inställningar",
        "settings-change-language": "Byt språk",
        "settings-english": "Engelska",
        "settings-danish": "Danska",
        "settings-norwegian": "Norska",
        "settings-swedish": "Svenska",
        "settings-notifications": "Aviseringar",
        "settings-push-notifications": "Pushnotiser",
        "settings-receipts-via-email": "Kvitto via e-post",
        "settings-receipts-meeting-service": "Kvitton för mötesservice",
        "settings-receipts-canteen": "Privata kantinköp",
        "settings-sms-notifications": "SMS-aviseringar",
        "settings-customer-service": "Kundtjänst",
        "settings-social-media": "Sociala medier",
        "settings-policies": "Policyer",
        "settings-HELP_CENTER": "Hjälpcenter",
        "settings-HELP_CENTER_USER_GUIDE_MOBILE_APP": "Användarguide",
        "settings-SOCIAL_FACILITYNET_FACEBOOK": "Följ oss på Facebook",
        "settings-SOCIAL_FACILITYNET_LINKEDIN": "Följ oss på LinkedIn",
        "settings-PRIVACY_TERMS": "Integritetspolicy",
        "settings-COOKIES_TERMS": "Cookiepolicy",
        "settings-EULA": "EULA",


        // Fast track menu
        "fast-track-menu-show-shop": "\n .title = Fast Track\n .contentActionText = Visa butik",
        "fast-track-menu-no-menus": "Inga menyer tillgängliga. Kontakta kantinen.",


        // Anonymous enter kitchen
        "anonymous-enter-kitchen-header": "Ange butiksnummer",
        "anonymous-enter-kitchen-shop-number-input": "\n .label = Butiksnummer",
        "anonymous-enter-kitchen-submit-button": "{ -authenticate-submit-button }",
        "anonymous-enter-kitchen-loading-dialog": "\n .description = Läser in Fast Track-meny...",
        "anonymous-enter-kitchen-shop-number-requirements": "Ange ett butiksnummer med { $minDigits }-{ $maxDigits} siffror",


        // To fast track menu via route
        "to-fast-track-menu-via-route-invalid-kitchen-id": "Butiksnumret {$kitchenCode} finns inte.",
        "to-fast-track-menu-via-route-error": "Något gick fel",
        "to-fast-track-menu-via-route-contact": "Kontakta kökspersonalen om felet upprepas.",


        // Choose location
        "choose-location-picker-no-location": "Välj plats",
        "choose-location-header-company-user": "\n .title = Välj din plats",
        "choose-location-header-private-user": "\n .title = Välj försäljningsställe",
        "choose-location-shop-number-field": "\n .label = Ange butiksnummer",
        "choose-location-shop-number-submit-button": "Välj\n .loadingLabel = Läser in...",
        "choose-location-about-shop-number-link": "Vad är ett butiksnummer?",
        "choose-location-alternative-method-header": "⸺ eller ⸺",
        "choose-location-search-for-location-button": "Visa alla försäljningsställen",
        "choose-location-search-field": "\n .label = Sök på namn, adress, butiksnummer...",
        "choose-location-help-text": "Om din plats inte finns med ska du kontakta er administratör.",
        "choose-location-dialog": "\n .title = Byt plats",
        "choose-location-dialog-private-user": "\n .title = Byt försäljningsställe",
        "choose-location-shop-numbers": "Butiker: { $numbers }",
        "choose-location-select-location-button": "Välj\n .disabledLabel = Aktiv\n .loadingLabel = Sparar val...",
        "choose-location-active-badge": "Aktiv",
        "choose-location-confirm-button": "Bekräfta plats\n .loadingLabel = Sparar val...",
        "choose-location-no-results": "Inga platser matchar din sökning.",


        // Order takeaway
        "order-takeaway-page-title": "\n .primaryTopBarTitle = Takeaway",

        // Order catering
        "order-catering-page-title": "\n .primaryTopBarTitle = Lunch",

        // --- Products page --- Offer tag ---
        "offer-tag-good-price": "Bra pris",
        "offer-tag-great-offer": "Fantastiskt erbjudande",
        "offer-tag-buy-now": "Köp nu",
        "offer-tag-discount": "Rabatt",
        "offer-tag-week-offer": "Veckans erbjudande",
        "offer-tag-day-offer": "Dagens erbjudande",
        "offer-tag-save-x-percentage": "Spara { $amount }%",
        "offer-tag-save-x-price": "Spara { $amount } kr",
        "offer-tag-limited-on-sale": "Bara { $amount } kvar",

        // --- Products page --- Allergens ---
        "order-food-allergens-list": "Allergener: { $allergens }",

        // Order takeaway/catering
        // "webshop-canteen-not-found-dialog": "\n .title = Ikke fundet\n .message = Køkkenet blev ikke fundet. Kontakt support.\n .buttonLabel = Luk",
        "order-basket-title": "\n .primaryTopBarTitle = Varukorg",
        "webshop-shop-type-lunch": "Lunchordning",
        "webshop-shop-type-take-away": "Takeaway",
        "webshop-shop-type-catering": "Mötesförtäring",
        "order-food-add": "Lägg till",
        "order-food-amount-private": "Till mig",
        "order-food-amount-company": "Till gäst",
        "order-food-pickup": "Hämtas: {$time} {$place}",
        "order-food-items-left": "{$amount} kvar",
        "order-food-basket-button": "Gå till kassan ({ $items }) { $price }",
        "order-food-item-out-of-stock": "Varan är slutsåld",
        "order-food-item-past-deadline": "Deadline har passerat",
        "order-food-item-quick-add": "Lägg till",
        "order-food-basket-overview": "\n .description = Varor",
        "order-food-basket-private": "Privat",
        "order-food-basket-company": "Företag",
        "order-food-basket-guest": "Gäst:",
        "order-food-basket-guest-information": "\n .description = Gästinformation",
        "order-food-basket-payment": "\n .description = Betalningsmetod",
        "order-food-basket-payment-methods": "\n .title = Betalningsmetoder",
        "order-food-basket-delivery": "\n .description = Leverans",
        "order-food-basket-pickup": "Ska hämtas",
        "order-food-basket-paid": "Betald - {$selectedOrder} av {$maxOrders}",
        "order-food-basket-name": "\n .label = Ditt namn\n .emptyErrorText = Det här fältet måste fyllas i",
        "order-food-basket-email": "\n .label = Din e-post\n .emptyErrorText = Det här fältet måste fyllas i\n .invalidErrorText = E-postadressen är ogiltig",
        "order-food-basket-phone": "\n .label = Telefonnummer (valfritt)",
        "order-food-basket-comment": "\n .label = Skriv en kommentar",
        "order-food-basket-purchase-button": "Genomför betalning\n .loadingLabel = Genomför köp...",
        "order-food-basket-seller": "Säljare: {$seller}",
        "order-food-canteen-details-contact": "Kontakt",
        "order-food-canteen-details-address": "Adress",
        "order-food-canteen-details-closed": "Stängt",
        "order-food-receipt-scan-to-checkin": "Skanna för att checka in",
        "order-food-receipt-scan-to-checkin-offline": "QR-koden kunde inte läsas in.",
        "order-food-featured-deliverydate": "Välj leveranstid",
        "order-food-upcoming-menus": "Kommande menyer",
        "order-food-empty-state-text": "Köket erbjuder ingen mat i dag. Prova en av de andra dagarna.",
        "order-food-sold-out-snackbar": "Varan är slutsåld",

        "product-unavailable-product-not-found-modal": "\n .title = Varan hittades inte\n .message = Den önskade varan finns inte i sortimentet för det valda datumet. Kontakta kantinen.\n .buttonLabel = Jag förstår",
        "product-unavailable-product-unavailable-modal": "\n .buttonLabel = Uppfattat",


        // Product modal
        "canteenProductModal-preparation-label": "Tillagning",
        "canteenProductModal-declaration-label": "Varudeklaration",
        "canteenProductModal-allergens-label": "Allergener",


        // Proof of Purchase
        "proof-of-purchase-order-id": "Order-ID: { NUMBER($orderId, useGrouping: 0) }",
        "proof-of-purchase-open-receipt-button": "Visa kvitto",
        "proof-of-purchase-subscription-type": "Prenumerationstyp: { $limit } dagar per { $period }",
        "proof-of-purchase-usage-remaining": "Förbrukning: { $remaining } dagar kvar",
        "usage-period-week": "vecka",
        "usage-period-month": "månad",
        "proof-of-purchase-order-type-lunch": "{ -order-type-lunch }",
        "proof-of-purchase-order-type-takeaway": "{ -order-type-take-away }",
        "proof-of-purchase-order-type-take-away": "{ -order-type-take-away }",
        "proof-of-purchase-order-type-catering": "{ -order-type-catering }",
        "proof-of-purchase-order-type-meeting": "{ -order-type-meeting }",
        "proof-of-purchase-order-type-refill-account": "{ -order-type-refill-account }",
        "proof-of-purchase-order-type-auto-refill-account": "{ -order-type-auto-refill-account }",
        "proof-of-purchase-order-type-lunch-subscription": "{ -order-type-lunch-subscription }",
        "proof-of-purchase-order-type-subscribe-payment-card": "{ -order-type-subscribe-payment-card }",
        "proof-of-purchase-order-type-refund": "{ -order-type-refund }",
        "receipt-qr-dialog-back": "Tillbaka",
        "receipt-no-deliveries": "Beställningen innehåller inga leveranser",
        "receipt-company-orderlines": "Företagsköp",

        "receipt-product-paid-by-free": "(gratis)",
        "receipt-product-paid-by-credit-card": "(med\u00A0kort)",
        "receipt-product-paid-by-one-click-payment": "(med\u00A0kort)",
        "receipt-product-paid-by-mobile-pay": "(med\u00A0MobilePay)",
        "receipt-product-paid-by-payroll-deduction": "(via\u00A0löneavdrag)",
        "receipt-product-paid-by-lunch-registration": "(via\u00A0lunchordning)",
        "receipt-product-paid-by-guest-lunch-registration": "(via\u00A0lunchordning)",
        "receipt-product-paid-by-gopay-account": "(med\u00A0{ -gopay-wallet })",
        "receipt-product-paid-by-invoice": "(med\u00A0faktura)",


        // Receipt
        "receipt-title": "Kvitto",
        "receipt-forward-receipt-dialog": "\n .title = { $receipts -> \n *[one] Skicka kvitto \n [other] Skicka kvitton \n } \n .cancelButtonLabel = Avbryt\n .submitButtonLabel = Skicka\n .submitButtonLoadingLabel = Skickar...",
        "receipt-forward-receipt-email-field": "\n .label = E-postadress",
        "receipt-forward-receipt-email-field-is-required": "E-postadress måste fyllas i",
        "receipt-forward-receipt-email-field-must-be-valid": "Ogiltig e-postadress",

        // Purchase section
        "receipt-purchase-details-section": "\n .heading = Köpinformation",
        "receipt-purchase-payment-status-paid": "BETALD",
        "receipt-purchase-payment-status-unpaid": "EJ BETALD",
        "receipt-purchase-payment-status-invoiced": "FAKTURERAD",
        "receipt-purchase-payment-status-refunded": "ÅTERBETALD",
        "receipt-purchase-payment-status-cancelled": "ÅTERBETALD",
        "receipt-purchase-payment-status-failed": "MISSLYCKADES",
        "receipt-purchase-address-item": "\n .label = Köpställe:",
        "receipt-purchase-seller-vat-number": "Org.nr:\u00A0{ $vatNumber }",
        "receipt-purchase-customer-item": "\n .label = Kund:",
        "receipt-purchase-time-item": "\n .label = Köptidpunkt:",
        "receipt-purchase-payment-method-item": "\n .label = Betalningsmetod:",
        "receipt-purchase-order-id-item": "\n .label = Order-ID:",
        "receipt-purchase-shop-channel-item": "\n .label = Via:",
        "receipt-purchase-cost-center-item": "\n .label = Kostnadsställe:",

        // Actions section
        "receipt-actions-forward-receipt-button": "Skicka kvitto",
        "receipt-actions-cancel-order-button": "Avboka beställning",
        "receipt-actions-delete-order-button": "Ta bort beställning",
        "receipt-actions-edit-order-button": "Redigera",
        "receipt-actions-update-order-button": "Redigera beställning",
        "receipt-actions-confirm-cancel-dialog": "\n .description = Du håller på att avboka din beställning. Är du säker?\n .confirmLabel = Ja\n .cancelLabel = Nej\n .loadingDescription = Avbokar beställning...",
        "receipt-actions-cancel-unavailable-dialog": "\n .buttonLabel = Uppfattat",

        // Delivery section
        "receipt-delivery-details-section": "\n .heading = Leverans",
        "receipt-delivery-order-type-item": "\n .label = Ordertyp:",
        "receipt-order-type-lunch": "{ -order-type-lunch }",
        "receipt-order-type-takeaway": "{ -order-type-take-away }",
        "receipt-order-type-take-away": "{ -order-type-take-away }",
        "receipt-order-type-catering": "{ -order-type-catering }",
        "receipt-order-type-meeting": "{ -order-type-meeting }",
        "receipt-order-type-meeting-service": "{ -order-type-meeting-service }",
        "receipt-order-type-refill-account": "{ -order-type-refill-account }",
        "receipt-order-type-auto-refill-account": "{ -order-type-auto-refill-account }",
        "receipt-order-type-lunch-subscription": "{ -order-type-lunch-subscription }",
        "receipt-order-type-subscribe-payment-card": "{ -order-type-subscribe-payment-card }",
        "receipt-order-type-refund": "{ -order-type-refund }",
        "receipt-delivery-type-item": "\n .label = Leveranstyp:",
        "receipt-seats-item": "\n .label = Platser",
        "receipt-delivery-type-pick-up": "Avhämtning",
        "receipt-delivery-time-item": "\n .label = Leveranstid:",
        "receipt-delivery-comment-item": "\n .label = Kommentar:",

        // Order details section
        "receipt-payment-method-free": "Gratis",
        "receipt-payment-method-credit-card": "Kreditkort",
        "receipt-payment-method-one-click-payment": "Kreditkort",
        "receipt-payment-method-mobile-pay": "MobilePay",
        "receipt-payment-method-salary-reduction": "Löneavdrag",
        "receipt-payment-method-payroll-deduction": "Löneavdrag",
        "receipt-payment-method-lunch-registration": "Lunchordning",
        "receipt-payment-method-guest-lunch-registration": "Lunchordning",
        "receipt-payment-method-gopay-account": "{ -gopay-wallet }",
        "receipt-payment-method-invoice": "Faktura",
        "receipt-payment-method-subscribe-payment-card": "Registrerat betalkort",

        "receipt-order-details-buyerparty-private": "Privatköp",
        "receipt-order-details-buyerparty-company": "Företagsköp",
        "receipt-order-details-vat-total": "\n .label = Varav moms:",

        // Accounting details section
        "receipt-accounting-details-section": "\n .heading = Kontering",


        // Order details
        "orderDetails-deliveryType-delivery": "\n .title = Leverans",
        "orderDetails-deliveryType-pick-up": "\n .title = Avhämtning",
        "orderDetails-deliveryType-eating-in-canteen": "\n .title = Äter i kantinen",
        "orderDetails-attendees-subject-label": "\n    .title = Mötesdeltagare ({$count})",
        "orderDetails-note-label": "\n    .title = Anteckning",
        "orderDetails-catering-totalAmountIncludeVat-label": "Belopp inkl. moms",
        "orderDetails-header-label": "\n    .title = Mitt möte",
        "orderDetails-meeting-privacyStatus-label": "\n    .label = Privat",
        "orderDetails-resources-subject-label": "Resurser",
        "deleteMeeting-labels": "\n    .title = Är du säker på att du vill ställa in mötet?\n    .buttonLabel = Bekräfta",


        // Confirmation-dialog
        "confirmation-dialog-cancel": "Avbryt",


        // Notifications
        "notification-snackbar-action-view": "Visa",
        "notification-snackbar-default-message": "Nytt meddelande",
        "notifications-page-title": "\n .primaryTopBarTitle = Meddelanden",
        "notifications-no-notifications": "\n .title = Inga meddelanden just nu\n .description = Här kan du läsa meddelanden från kantinen och servicecentret.",
        "notifications-no-notifications-notification-time-type-news": "Nyhet",
        "notifications-no-notifications-notification-time-type-info": "Information",
        "notifications-no-notifications-notification-time-type-important": "Viktigt",
        "notifications-no-notifications-notification-time-type-offer": "Erbjudande",
        "notifications-no-notifications-notification-time-type-event": "Evenemang",
        "notifications-no-notifications-notification-time-type-reminder": "Påminnelse",
        "notifications-no-notifications-notification-time-type-menu-change": "Meny",
        "notifications-no-notifications-notification-time-type-feedback": "Feedback",
        "notifications-no-notifications-notification-time-type-health": "Hälsa",
        "notifications-sender-type-merchant": "Försäljningsställe",
        "notifications-sender-type-service-center": "Service",


        // Room Finder
        "roomfinder-tabs-searcher": "Sök",
        "roomfinder-tabs-overview": "Översikt",
        "roomfinder-overview-find-room": "\n    .label = Hitta rum",
        // "roomFinder-availableResourceGroup-1": "Ledige rum",
        // "roomFinder-availableResourceGroup-2": "Ledige AV udstyr",
        // "roomFinder-availableResourceGroup-4": "Ledige biler",


        // Week menu
        "home-week-menu-title": "\n .title = Veckans meny",
        "weekMenu-badge": "Veckans meny",
        "weekMenu-header-label": "\n .title = Veckans meny",
        "weekMenu-no-menu-label": "Tyvärr har inga menyer skapats den här veckan.",
        "weekMenu-no-menu-subtitle": "Här kan du se menyn för de kommande dagarna, om den är tillgänglig. Du kan inte handla här.",


        // Home Booking Meeting
        "home-book-meeting-title": "\n .title = Mötesplanering",


        // Meeting Planning Dialog
        "order-meeting-service": "Beställ mötesförtäring",
        "book-resources": "Boka resurser",


        // Booking Meeting Screen
        "meeting-service-screen-add-title": "Lägg till mötesförtäring",
        "meeting-service-screen-edit-title": "Redigera mötesförtäring",
        "meeting-planning-screen-title": "Lägg till leverans av mötesförtäring",
        "delivery-form-delivery-type-field-title": "Leveranstyp",
        "cancel-delivery": "\n .description = Vill du avbryta den nuvarande beställningen?\n .confirmLabel = Ja\n .cancelLabel = Nej",


        // Add Initial Delivery Form
        "delivery-form-meeting-field-label": "\n .label = Mötesnamn\n .title = Mötesnamn\n .placeholder = Ange mötesnamn",
        "delivery-form-date-from-field-label": "\n .label = Från",
        "delivery-form-date-to-field-label": "\n .label = Till",
        "delivery-form-meeting-field-date-label": "\n .label = Leveransdatum\n .placeholder = Välj leveransdatum",
        "delivery-form-delivery-type-field-label": "\n .label = Leveranstyp\n .placeholder = Välj leveranstyp",
        "delivery-form-delivery-location-field-label": "\n .label = Leveransplats\n .placeholder = Välj leveransplats",
        "delivery-form-delivery-other-location-field-label": "\n .label = Annan leveransplats",
        "delivery-form-delivery-other-location-field-button": "+ Annan leveransplats",
        "delivery-form-delivery-time-field-label": "\n .label = Leveranstid\n .placeholder = Välj leveranstid",
        "delivery-form-delivery-time-field-select-label": "Välj tid",
        "delivery-form-delivery-seats-field-label": "\n .label = Kuvert",
        "delivery-form-delivery-seats-field-placeholder": "Ange eller välj antal kuvert",
        "delivery-form-delivery-other-message-field-label": "\n .label = Beställningsmeddelande\n .title = Beställningsmeddelande\n .placeholder = Skriv ett meddelande till din beställning",
        "delivery-payment-form-delivery-bill-receiver-field-label": "Fakturamottagare",
        "delivery-payment-form-delivery-bill-receiver-field-placeholder": "Välj fakturamottagare",
        "delivery-payment-form-delivery-cost-center-field-label": "Kostnadsställe",
        "delivery-payment-form-delivery-cost-center-field-placeholder": "Ange kostnadsställe",

        "delivery-form-meeting-field-name-required": "Mötesnamn krävs",
        "delivery-form-meeting-field-name-max-length": "Maximal längd är { $maxLength} tecken",
        "delivery-form-meeting-field-other-location-max-length": "Maximal längd för Annan leveransplats är { $maxLength } tecken",
        "delivery-form-meeting-field-other-message-max-length": "Maximal längd för annat meddelande är { $maxLength } tecken",
        "delivery-form-meeting-field-from-date-required": "Startdatum krävs",
        "delivery-form-meeting-field-date-required": "Leveransdatum krävs",
        "delivery-form-meeting-field-from-date-should-be-before-to-date": "Startdatum måste vara före slutdatum",
        "delivery-form-meeting-field-to-date-required": "Slutdatum krävs",
        "delivery-form-meeting-field-delivery-type-required": "Leveranstyp krävs",
        "delivery-form-meeting-field-delivery-time-required": "Leveranstid krävs",
        "delivery-form-meeting-field-delivery-time-available": "Leveranstid den valda dagen är tillgänglig från { $from } till { $to }",
        "delivery-form-meeting-field-delivery-time-need-date": "Välj ett leveransdatum först.",
        "delivery-form-delivery-location-field-required": "Leveransplats krävs",
        "delivery-form-delivery-other-location-field-required": "Annan leveransplats krävs",
        "delivery-form-meeting-field-seats-min-length": "Minsta antal platser är { $minLength}",
        "delivery-form-meeting-field-seats-max-length": "Högsta antal platser är { $maxLength}",
        "delivery-payment-form-delivery-dynamic-field-required": "{ $field } krävs",

        "delivery-form-meeting-submit-button": "Fortsätt ({ $finalPrice})\n .loadingLabel = Läser in...",

        "delivery-form-delivery-extra-services-title": "Extratjänster",
        "delivery-form-delivery-extra-services-add": "Lägg till",
        "delivery-form-delivery-extra-services-remove": "Ta bort",
        "delivery-form-delivery-extra-services-per-person": "{ $pricePerSeat} per kuvert",
        "delivery-form-delivery-extra-services-min-max": "Min { $minimumPrice} | Max { $maximumPrice}",


        // Delivery Type Options
        "delivery-type-option-canteen": "Äter i kantinen",
        "delivery-type-option-pick-up": "Avhämtning",
        "delivery-type-option-delivery": "Leverans",


        // Other Location
        "other-location": "Annan leveransplats",


        // Basket
        "basket-removeAll-label": "Rensa alla",
        "basket-meeting-catering-title": "Mötesförtäring",


        // Meeting options
        "meetingOptions-extendMeeting-label": "Förläng möte",
        "meetingOptions-endMeeting-label": "Avsluta möte",
        "meetingOptions-viewDetails-label": "Visa detaljer",
        "meetingOptions-delete-label": "Ställ in möte",
        "meetingOptions-edit-label": "Redigera möte",
        "meetingOptions-assistance-label": "Assistans",
        "-meetingOptions-startMeeting-base-label": "Starta möte",
        "meetingOptions-startMeeting-expired-label": "{-meetingOptions-startMeeting-base-label} (utgånget)",
        "meetingOptions-startMeeting-left-label": "{-meetingOptions-startMeeting-base-label} ({$time} kvar)",
        "meetingOptions-startMeeting-in-label": "{-meetingOptions-startMeeting-base-label} (om {$time})",

        "meeting-planned-complete": "Beställningen är genomförd",
        "meeting-planned-added": "Mötesförtäring tillagd",
        "meeting-planned-updated": "Mötesförtäring uppdaterad",
        "meetingBooked-next-choice-go-to-resource-booking": "Lägg till resursbokning i beställningen",

        // Confirm Meeting Planning Modal
        "confirm-meeting-planning-modal-title": "Tidsfristen har passerat",
        "confirm-meeting-planning-modal-description": "Du kan fortfarande lägga beställningen, men den måste godkännas av personalen, vilket innebär att du inte kan förvänta dig att den levereras.",
        "confirm-meeting-planning-modal-button-denied": "Genomför inte beställningen",
        "confirm-meeting-planning-modal-button-accept": "Genomför beställningen",

        // meetings
        "meetingBooked-complete": "Bokning genomförd",
        "meetingBooked-when": "När:",
        "meetingBooked-next-choice": "Vad vill du göra nu?",
        "meetingBooked-next-choice-go-to-meeting-planning": "Lägg till mötesförtäring",
        "meetingBooked-next-choice-finish": "Jag är klar",
        "meetingStarted-labels": "\n    .topbarTitle = Möte påbörjat\n    .title = Mötet har påbörjats\n    .buttonLabel = Fortsätt",
        "meetingEnded-labels": "\n    .topbarTitle = Möte avslutat\n    .title = Mötet har avslutats\n    .buttonLabel = Fortsätt",
        "meetingDeleted-labels": "\n    .topbarTitle = Möte borttaget\n    .title = Mötet har tagits bort\n    .buttonLabel = Fortsätt",
        "meetingBooked-labels": "\n    .topbarTitle = Möte bokat\n    .buttonLabel = Nästa",
        "meetingUpdated-labels": "\n    .topbarTitle = Möte uppdaterat\n    .buttonLabel = Nästa",
        "meeting-open-in-calendar": "Lägg till mötet i kalendern",
        "hours": "timmar",


        // Buttons
        "edit-button": "Redigera",
        "delete-button": "Ta bort",


        // News
        "allNews-header-label": "\n    .title = Nyheter",
        "allNews-noNews-label": "Inga nyheter",
        "newsDetails-header-label": "\n    .title = Mina nyheter",
        "newsDetails-by-label": "Av {$creator} | {$company}",

        "completeBooking-advancedOptions-button-label": "Nästa",
        "completeBooking-advancedOptions-button-complete": "Slutför",
        "completeBooking-advancedOptions-booking-time": "Bokningstid:",
        "completeBooking-advancedOptions-header-label": "Fler inställningar",
        "completeBooking-advancedOptions-privateMeeting-label": "Privat möte",
        "completeBooking-advancedOptions-sittingsArrangement-label": "Möblering",
        "completeBooking-header-label": "\n .title = Slutför bokning",
        "completeBooking-meeting-endTime-label": "Till",
        "completeBooking-meeting-header-label": "Detaljer",
        "completeBooking-meeting-meetingName-label": "Mötesnamn",
        "completeBooking-meeting-startTime-label": "Från",
        "completeBooking-meeting-card-meeting-details-title": "Mötesdetaljer",
        "completeBooking-meeting-card-resources-title": "Resurser",
        "assistance-header-label": "\n    .title = Begär assistans",
        "assistance-location-label": "Plats:",
        "assistance-noLocations-label": "Inga platser",
        "assistance-button-next-label": "Nästa",
        "assistance-chooseMessage-label": "Välj meddelande:",
        "assistance-yourMessage-label": "Ditt meddelande:",
        "assistance-writeMessage-label": "Skriv ditt eget meddelande",
        "confirmBooking-header-label": "\n    .title = Bekräfta bokning",
        "confirmBooking-button-label": "Slutför beställning",
        "confirmBooking-empty-basket-label": "Varukorgen är tom",
        "endMeeting-button-label": "Bekräfta",
        "endMeeting-header-label": "Avsluta möte",
        "endMeeting-success-label": "Mötet är avslutat",
        "endMeeting-text-label": "Är du säker på att du vill avsluta mötet?",
        "endMeeting-labels": "\n    .title = {endMeeting-text-label}\n    .title = {endMeeting-button-label}",
        "startMeeting-button-label": "Bekräfta",
        "startMeeting-text-label": "Är du säker på att du vill starta mötet?",
        "startMeeting-labels": "\n    .title = {startMeeting-text-label}\n    .title = {startMeeting-button-label}",
        "extendMeeting-button-label": "Bekräfta",
        "extendMeeting-changeEndTimeTo-label": "Ändra mötets sluttid till:",
        "extendMeeting-header-label": "\n    .title = Förläng möte",
        "extendMeeting-success-label": "Mötet är förlängt",
        "meeting-button-end-label": "Avsluta",
        "meeting-button-extend-label": "Förläng",
        "meetingUpdated-button-label": "Stäng",
        "meetingUpdated-header-label": "Klart",
        "myMeetings-button-delete-label": "Ta bort",
        "myMeetings-button-update-label": "Uppdatera",
        "myMeetings-header-label": "Filter",
        "myMeetings-search-label": "Sök",
        "roomFinder-button-addResource-label": "Lägg till",
        "roomFinder-button-removeResource-label": "Ta bort",
        "roomFinder-button-next-label": "Nästa",
        "roomFinder-resourceInfo-av-subject-label": "AV-utrustning",
        "roomFinder-resourceInfo-availableTimeSlots-subject-label": "Lediga tider",
        "roomFinder-resourceInfo-moreTimeSlots-subject-label": "Fler tider...",
        "roomFinder-resourceInfo-noAvailableTimeSlots-subject-label": "Inga lediga tider",
        "roomFinder-resourceInfo-sittingsArrangements-subject-label": "Möblering",
        "roomFinder-specifyTime-header-label": "\n    .title = Ange tid",
        "roomFinder-search-filter-label": "Filter",
        "roomFinder-search-nofilter-label": "Inga filter",
        "roomFinder-search-unsupported-element": "Elementtypen stöds inte: {$type}",
        "roomFinder-search-hour-label": "timme",
        "roomFinder-search-hours-label": "timmar",
        "roomFinder-continue-to-checkout-label": "Fortsätt till kassan ({$amount})",
        "roomFinder-subject-label": "Lediga {resourceType}",
        "roomFinderFilter-fixedAv-label": "AV-utrustning",
        "roomFinderFilter-floor-label": "Våning",
        "roomFinderFilter-group-button-label": "Visa",
        "roomFinderFilter-group-fixedAv-label": "AV-utrustning",
        "roomFinderFilter-group-floor-label": "Våning",
        "roomFinderFilter-group-location-label": "Platser",
        "roomFinderFilter-group-resourceTypes-label": "Resurstyp",
        "roomFinderFilter-group-tag-label": "Taggar",
        "roomFinderFilter-header-label": "Filter",
        "roomFinderFilter-location-label": "Platser",
        "roomFinderFilter-meetingDuration-hour-label": "Timme",
        "roomFinderFilter-meetingDuration-hours-label": "Timmar",
        "roomFinderFilter-meetingDuration-subject-label": "Varaktighet",
        "roomFinderFilter-removeAll-label": "Rensa alla",
        "roomFinderFilter-remove-label": "Rensa",
        "roomFinderFilter-resourceTypes-av-label": "AV-utrustning",
        "roomFinderFilter-resourceTypes-car-label": "Bil",
        "roomFinderFilter-resourceTypes-label": "Resurstyp",
        "roomFinderFilter-resourceTypes-room-label": "Rum",
        "roomFinderFilter-resourceTypes-subject-label": "Resurstyper",
        "roomFinderFilter-room-label": "Rum",
        "roomFinderFilter-room-subject-label": "Rum",
        "roomFinderFilter-sittings-numberOfSittings-label": "Antal sittplatser",
        "roomFinderFilter-sittings-subject-label": "Sittplatser",
        "roomFinderFilter-tag-label": "Taggar",
        "roomFinderFilter-dateTimePicker-change-date-button": "\n .buttonLabel = Ändra datum",
        "upcomingMeetings-button-end-label": "Avsluta",
        "upcomingMeetings-button-extend-label": "Förläng",
        "upcomingMeetings-meetingStatus-label": "Nu",
        "upcomingMeetings-moreInfo-label": "Mer info",
        // Misc
        "action-sheet-cancel-button": "Avbryt",
        "alert-dialog-close-button": "Stäng",
        "options-novalue": "-- Välj --",
        "date-time-picker": "\n .okLabel = OK\n .cancelLabel = Avbryt",

        // Tooltip hints
        "unavailable-date-tooltip": "Det här datumet kan inte väljas",

        // Errors
        "error-required": "Fältet måste fyllas i",
        "error-max-length": "Maximal längd är {$maxLength}",

        // --- API components ---
        "api-query-table-fetch-error-message": "Fel vid inläsning av data",
        "api-query-autocomplete": "Läser in data...",
        "api-query-autocomplete-fetch-error-message": "Fel vid inläsning av data. <retryLink>Försök igen</retryLink>",
    },
} satisfies Translations;

type LocaleKey<Locale extends keyof typeof defaultTranslations> = keyof typeof defaultTranslations[Locale]
type SourceKey = LocaleKey<"da-DK"> | LocaleKey<"en-US">
type NoKeys<Keys extends never> = Keys

export type NorwegianMissingKeys = NoKeys<Exclude<SourceKey, LocaleKey<"nb-NO">>>
export type NorwegianExtraKeys = NoKeys<Exclude<LocaleKey<"nb-NO">, SourceKey>>
export type SwedishMissingKeys = NoKeys<Exclude<SourceKey, LocaleKey<"sv-SE">>>
export type SwedishExtraKeys = NoKeys<Exclude<LocaleKey<"sv-SE">, SourceKey>>
